// Seed data utility for populating CMS with initial content
// Run this once to create pages matching existing dummy content

import BusinessDomains from '../components/BusinessDomains';
import ProjectCategoryCard from '../components/ProjectCategoryCard';
import { createPage } from '../services/firestoreService';
import { getCorporateUrl } from '../config/urls';
import { ALL_JOBS_URL, SALES_INTERNSHIP_URL } from '../config/careerLinks';

// 共通のHiring Bannerデータ（全ページで共有）
export const HIRING_BANNER_DATA = {
  label: "We're hiring!",
  title: '現在3職種募集中！',
  description: 'マネージャー、エンジニア、セールスなど<br />多種多様な職種を募集しています。<br />募集要項およびエントリーはこちら。',
  buttonText: '募集要項・エントリー',
  buttonHref: ALL_JOBS_URL,
  imageSrc: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/homepage/hiring-banner-section/1.jpg',
  imageAlt: 'FLARETECH採用中 - 一緒に働く仲間を募集しています'
};

// ============================================
// ABOUT PAGES
// ============================================

// About Summary Page - matching AboutSummaryPage.js
export const aboutSummaryPageData = {
  title: '1ページで考えるFLARETECH',
  titleEn: 'Overview',
  subtitle: 'FLARETECHに関心をお寄せいただき、ありがとうございます。このページでは、FLARETECHとはどんな会社なのか、どんな価値観で働いているのか、数字で見るとどんな環境なのかを、1ページでお伝えします。',
  slug: '/about/summary',
  category: 'about',
  site: 'career',
  metaTitle: '1ページで考えるFLARETECH | FLARETECH採用サイト',
  metaDescription: 'FLARETECHの全体像を1ページで把握。会社概要、働く魅力、価値観、実績、環境、数字で見るFLARETECHをご紹介します。',
  metaKeywords: ['FLARETECH', '会社概要', '採用情報', 'エンジニア採用'],
  metadata: {
    updatedDate: '2026.02.09',
    readTime: '約5分'
  },
  showToc: true,
  tocLinks: [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: {
    type: 'simple',
    backgroundImage: '',
    buttons: []
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-1',
        title: 'FLARETECHとは？',
        alignment: 'left'
      }
    },
    {
      type: 'featureGrid',
      data: {
        columns: 3,
        items: [
          {
            title: 'エンジニアの市場価値を高めるテックカンパニー',
            description: 'システム開発、インフラ構築、DX推進、AI開発を通じて、お客さまの課題解決とエンジニアの成長を両立させます。'
          },
          {
            title: '400社以上の支援実績',
            description: '2020年11月の創業から5年で、400社以上のお客さまにシステム開発やインフラ構築、DX支援を提供。多様な業界・企業の経験を積める環境です。'
          },
          {
            title: '挑戦・誠実・共創の価値観',
            description: '情熱と誇りを持って挑戦する姿勢、高い倫理観を持った誠実な行動、仲間と助け合う共創の精神を大切にしています。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-2',
        title: 'FLARETECHで働く魅力',
        alignment: 'left'
      }
    },
    {
      type: 'numberedList',
      data: {
        showImage: false,
        items: [
          {
            title: '幅広い技術領域に関われる',
            description: 'システム開発からインフラ構築、DX推進、AI開発まで。多様なプロジェクトを通じて、エンジニアとしての市場価値を高められます。'
          },
          {
            title: '判断と説明まで引き受ける仕事',
            description: '実装だけでなく、なぜその技術を選んだのか、どう設計したのか。判断の理由を説明し、責任を持って進める経験ができます。'
          },
          {
            title: '成長を支援する環境',
            description: 'AI駆動開発を全社で推進。学習機会の提供、定期的なキャリア面談など、エンジニアの成長に向き合う体制があります。'
          },
          {
            title: '柔軟な働き方',
            description: 'リモートワークなど仕事と家庭の両立を支援し、持続的に働ける環境を整えています。'
          },
          {
            title: 'キャリアの選択肢',
            description: 'スペシャリスト、PM、上流設計と、複数のキャリアパスがあります。会社が決めるのではなく、本人が選ぶキャリア設計です。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-3',
        title: '私たちの働く価値観',
        alignment: 'left'
      }
    },
    {
      type: 'featureGrid',
      data: {
        columns: 3,
        variant: 'image-only',
        items: [
          {
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/1.png',
            title: '挑戦',
            description: '新しい技術領域に踏み出し、未知の課題に立ち向かう。エンジニアの成長は挑戦から生まれると信じています。'
          },
          {
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/2.png',
            title: '誠実',
            description: '誠実にコミュニケーションを取り、約束を守る。信頼関係がすべての仕事の基盤です。'
          },
          {
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/3.png',
            title: '共創',
            description: 'クライアント、チーム、社会と共に価値を創る。一人では成し遂げられないことに挑みます。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-4',
        title: 'FLARETECHの実績',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>3つの事業領域で成長を加速</h3><p>FLARETECHはシステムインテグレーション、人材開発、AI開発の3つの事業領域を展開しています。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>2020年11月の創業から5年で、400社以上のお客さまに価値を提供してきました。多様な業界・企業との関わりを通じて、エンジニア一人ひとりが幅広い経験を積める環境があります。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'featureGrid',
      data: {
        columns: 3,
        items: [
          {
            title: 'システムインテグレーション事業',
            description: '業務システムから基盤構築まで。要件定義から実装・改善・運用まで、責任を持ってやり切ります。'
          },
          {
            title: 'AI活用・導入支援',
            description: 'ChatGPT・Copilot活用から業務自動化まで。AIを導入で終わらせず、業務として定着させるまでを支援。'
          },
          {
            title: 'AI×HR・組織設計',
            description: '評価・採用・配置などの人事判断を、AIと設計で前に進める。属人化を減らし、スケーラブルな組織運用を実現。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-5',
        title: 'こだわりのワーク＆ライフ環境',
        alignment: 'left'
      }
    },
    {
      type: 'numberedList',
      data: {
        showImage: false,
        items: [
          {
            title: '定期的な1on1',
            description: 'マネージャーとの定期面談を通じて、キャリアの悩みや成長の方向性を一緒に考えます。'
          },
          {
            title: 'AI駆動開発の推進',
            description: 'ChatGPT、GitHub Copilotなどの生成AIツールを全社で活用。生産性向上と品質改善に取り組んでいます。'
          },
          {
            title: '学習支援制度',
            description: '書籍購入補助や資格取得支援など、継続的な学習を全面的にバックアップ。'
          },
          {
            title: '副業OK',
            description: '本業に支障がない範囲で副業が可能。多様なキャリア形成を応援します。'
          },
          {
            title: 'リモートワーク',
            description: '本人の経験次第でフルリモート勤務可能。柔軟な働き方を実現。全国どこからでも働くことができます。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-6',
        title: '数字から見るFLARETECH',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>数字で見るFLARETECHの規模と特徴です。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'statsGrid',
      data: {
        columns: 2,
        items: [
          {
            number: '400',
            unit: '社以上',
            label: 'お客さま実績',
            description: '2020年創業から5年で支援した企業数'
          },
          {
            number: '200',
            unit: '名以上',
            label: '社員数',
            description: '多様なバックグラウンドを持つエンジニア集団'
          },
          {
            number: '92',
            unit: '％',
            label: 'エンジニア比率',
            description: '社員数の内エンジニアが占める割合'
          },
          {
            number: '1→3→6',
            unit: 'ヶ月後',
            label: 'キャリア面談の実施',
            description: '段階的な成長サポート体制'
          },
          {
            number: '11',
            unit: '回／月',
            label: '学習・共有の機会',
            description: '社内勉強会や技術共有の頻度（その他にも、ほぼ毎日社内チャットで技術に関する共有がされていたり、社内ブログを通してTipsを共有し合う社風があります）'
          },
          {
            number: '56',
            unit: '回／年',
            label: '社内イベント数',
            description: 'チームビルディングと交流の場'
          },
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-7',
        title: 'よくある疑問に答えます',
        alignment: 'left'
      }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: 'FLARETECHはSES企業ですか？',
            answer: '<p>FLARETECHは客先常駐型の技術者派遣(SES)をメインとする企業です。ただし、単に人を送り込むだけでなく、お客さまの課題に向き合い、技術選定から実装、運用まで一貫して関わることを大切にしています。エンジニアが判断と説明責任を引き受けられる環境を目指しています。</p>'
          },
          {
            question: '未経験からでも大丈夫ですか？',
            answer: '<p>完全未経験からの採用は現在行っていません。ただし、実務経験が浅い方やキャリアチェンジを考えている方には、オンボーディング制度(1ヶ月→3ヶ月→6ヶ月の段階的サポート)や学習支援制度を通じて成長を支援します。</p>'
          },
          {
            question: 'キャリアパスは会社が用意してくれますか？',
            answer: '<p>会社が決めるのではなく、本人が選びます。スペシャリスト、PM、上流設計など複数の方向性があり、定期的な1on1を通じてキャリアの方向性を一緒に考えます。ただし、選んだ道の責任は本人が引き受けることになります。</p>'
          },
          {
            question: 'AIに仕事を奪われませんか？',
            answer: '<p>AIは仕事を奪うのではなく、働き方を変えるツールだと考えています。FLARETECHではChatGPTやGitHub Copilotなどの生成AIを全社で活用し、生産性向上と品質改善に取り組んでいます。AIを使いこなすスキルが、これからのエンジニアには必要です。</p>'
          }
        ]
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>この先のキャリア、考えていますか？</h3><p>いま目の前の仕事に追われて、気づいたら3年。スキルは増えたけれど、市場価値は？成長は？次の選択肢は？</p><p>FLARETECHは「今、ここで何ができるか」だけでなく、<strong>「3年後、どうなっていたいか」</strong>を一緒に考える会社です。</p><p>判断と説明まで引き受ける仕事、AI時代を見据えた学習環境、キャリアの選択肢。あなたの「次」を設計できる場所があります。</p>',
        alignment: 'center'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/about/business"><strong>FLARETECHの事業へ</strong></a><br>どんな事業を展開しているかご紹介します。',
        alignment: 'center'
      }
    }
  ]
};

// About Business Page
export const aboutBusinessPageData = {
  title: 'FLARETECHの事業',
  titleEn: 'Business',
  subtitle: 'このページでは、FLARETECHの事業戦略・事業編成・各事業に対する考え方を整理します。事業の考え方は、そのまま「役割」「期待値」「働き方」に直結します。どんな課題を、どこまで責任を持って引き受ける会社なのかを理解するために、ぜひご覧ください。',
  slug: '/about/business',
  category: 'about',
  site: 'career',
  metaTitle: 'FLARETECHの事業 | FLARETECH採用サイト',
  metaDescription: 'FLARETECHの事業戦略、3つの事業セグメント、新規事業・アライアンス・M&Aについて詳しくご紹介します。',
  metaKeywords: [],
  metadata: {
    updatedDate: '2026.xx.xx',
    readTime: '約8分'
  },
  showToc: true,
  tocLinks: [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: {
    type: 'simple',
    backgroundImage: '',
    buttons: []
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-1',
        title: 'FLARETECHのポートフォリオ経営',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>一般的に、開発会社は「現場支援（開発／SES）」に寄りやすく、プロダクト会社は「自社サービス」に寄りやすい傾向があります。FLARETECHは、そのどちらか一方に寄せません。</p><p>理由は単純です。現場で起きている課題は、たいてい 人・業務・技術・意思決定が分断されており、片側だけを支援しても、成果が途中で止まるからです。</p><p>FLARETECHのポートフォリオは「分散」ではなく、責任範囲を完結させるための設計です。</p><ul><li>現場に入り、価値が出るところまで実装する</li><li>AIを"導入"で終わらせず、業務に定着させる</li><li>HR領域の属人化（評価・採用）を、設計とAIで前に進める</li></ul>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-2',
        title: '私たちの事業分野',
        alignment: 'left'
      }
    },
    {
      type: 'businessDomains',
      data: {}
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/ai/ai-3.jpg',
        alt: 'FLARETECHのAI活用と事業展開'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>ここから先は、役割や責任を「言葉」ではなく、実際に起きている仕事の形で見ていきます。</p><p>紹介するのは、特別な成功事例や、珍しい案件ではありません。FLARETECHで日常的に発生している、よくある現場の一部です。</p><p>「できそうかどうか」ではなく、<strong>「この状況に立ち続けられるか」</strong>という視点で読んでください。</p>',
        alignment: 'center'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '現場実装・人材支援（開発／SES）',
        subtitle: 'このセグメントの本質：現場に入り、仕様が揺れる中でも「動く・使われる」状態までやり切る。',
        alignment: 'left',
        noBar: true
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>現場に入り、価値が出るところまで責任を持つセグメントです。ここで扱うのは、単なるアサインではありません。実装だけでなく、改善・運用・説明まで含めて向き合います。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>主な対象：</strong>業務システム／Webサービス／データ基盤</p><p><strong>引き受ける責任：</strong>設計意図の説明、改善の継続、現場の前提整理</p><p><strong>よく起きる課題：</strong>仕様の揺れ／優先順位の変化／意思決定の停滞</p>',
        alignment: 'left'
      }
    },
    {
      type: 'projectCategoryCard',
      data: {
        categories: [
          {
            title: '基幹業務システムの刷新・再設計',
            examples: ['人事／勤怠／会計／販売管理システムのリプレイス'],
            features: [
              '既存業務のしがらみが強い',
              '要件が途中で変わる',
              '現場説明・合意形成が必須'
            ]
          },
          {
            title: '業務SaaS・社内ツールの新規開発／改善',
            examples: ['営業支援ツール、業務ダッシュボード、社内ポータル'],
            features: [
              'ユーザーが社内にいる',
              'フィードバックが早い',
              '改善サイクルを回し続ける必要あり'
            ]
          },
          {
            title: 'データ基盤・業務フロー再構築',
            examples: ['CSV地獄の解消、データ統合、業務自動化の前段設計'],
            features: [
              '技術だけでなく業務理解が必要',
              '「正解がない」構造設計',
              '説明責任が重い'
            ]
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'AI活用・AI導入支援（研修 → 導入 → 実装）',
        subtitle: 'このセグメントの本質：AIを"試す"のではなく、"業務として回る状態"まで落とす。',
        alignment: 'left',
        noBar: true
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>AIは導入して終わりません。業務に落ちるまでの設計、運用、定着のほうが難しい。このセグメントは AIを意思決定の中に組み込むところまでを支援します。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>例：</strong>生成AI研修／導入支援パッケージ（例：Lens AI）</p><p><strong>例：</strong>AI活用の業務設計／ナレッジ整備／ワークフロー自動化</p><p><strong>引き受ける責任：</strong>使える状態まで"運用設計"をやり切る</p>',
        alignment: 'left'
      }
    },
    {
      type: 'projectCategoryCard',
      data: {
        categories: [
          {
            title: '生成AI研修・内製化支援（全社／部門別）',
            examples: [
              'ChatGPT／Copilot活用研修',
              '職種別プロンプト設計'
            ],
            features: [
              '技術説明より業務適用が主',
              '「使われない」リスクが高い',
              '定着設計が重要'
            ]
          },
          {
            title: '業務自動化（AIエージェント／RPA×AI）',
            examples: [
              '問い合わせ一次対応AI',
              '定型業務の自動処理'
            ],
            features: [
              '業務理解が浅いと失敗する',
              '現場調整が多い',
              '責任範囲の切り分けが難しい'
            ]
          },
          {
            title: 'AI導入PoCから本番運用への移行',
            examples: [
              'PoC止まり案件の再設計',
              'セキュリティ・運用設計込みの実装'
            ],
            features: [
              '技術より判断が難しい',
              '「やらない理由」を整理する場面が多い',
              '経営層との説明・合意が必須'
            ]
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'AI×HR／組織設計（評価・採用・配置）',
        subtitle: 'このセグメントの本質：人が迷う・揉める領域を、設計とAIで前に進める。',
        alignment: 'left',
        noBar: true
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHが注力している成長領域です。評価・採用・配置は、意思決定の負荷が高く、属人化しやすい。ここに AIと設計を入れることで、企業の意思決定を前に進めます。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>想定提供：</strong>評価業務の効率化AI／採用業務の効率化AI</p><p><strong>主な対象：</strong>従業員1,000人以上のエンタープライズ</p><p><strong>引き受ける責任：</strong>運用に耐える業務設計、説明可能な判断支援</p>',
        alignment: 'left'
      }
    },
    {
      type: 'projectCategoryCard',
      data: {
        categories: [
          {
            title: '評価業務の効率化・標準化AI',
            examples: [
              '目標設定・評価コメントの支援',
              '評価基準の可視化'
            ],
            features: [
              '感情とロジックが衝突する',
              '説明責任が極めて重い',
              '現場理解が不可欠'
            ]
          },
          {
            title: '採用業務DX（スカウト／書類選考／面接支援）',
            examples: [
              'スカウト文自動生成',
              '面接メモ・評価補助AI'
            ],
            features: [
              '公平性・再現性が問われる',
              '法務・コンプラ配慮が必要',
              '人事と現場の調整が多い'
            ]
          },
          {
            title: '人材配置・組織分析支援',
            examples: [
              'スキルマップ設計',
              '異動・配置判断の補助'
            ],
            features: [
              '数字だけでは決められない',
              '組織政治が絡む',
              '経営視点が求められる'
            ]
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/ai/ai-2.jpg',
        alt: 'チームで協力して事業を推進する'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>ここからは「どれが向いているか」ではなく、<br>「どの責任を引き受けられるか」を比べます</h3>',
        alignment: 'center'
      }
    },
    {
      type: 'table',
      data: {
        rows: [
          { cells: ['比較軸', 'A｜現場実装<br>（開発／SES）', 'B｜AI活用<br>・導入支援', 'C｜AI×HR<br>／組織設計'] },
          { cells: ['<strong>仕事の本質</strong>', '現場に入り、動くものを作り続ける', 'AIを業務に落とし、使われる状態をつくる', '人と組織の判断を仕組みにする'] },
          { cells: ['<strong>主な役割</strong>', '実装・設計・改善を前に進める', 'AIの使いどころを設計・説明する', '評価・採用・配置の判断を設計する'] },
          { cells: ['<strong>日常の判断頻度</strong>', '高い<br>（ほぼ毎日）', '中〜高<br>（設計・運用局面で集中）', '非常に高い<br>（判断が主業務）'] },
          { cells: ['<strong>曖昧さの多さ</strong>', '高い<br>（要件が頻繁に変わる）', '非常に高い<br>（正解が存在しない）', '極めて高い<br>（感情とロジックが衝突）'] },
          { cells: ['<strong>技術の比重</strong>', '高<br>（実装・設計が土台）', '中<br>（技術＋業務理解）', '低〜中<br>（技術は手段、設計が主）'] },
          { cells: ['<strong>業務理解の比重</strong>', '中', '高', '非常に高い'] },
          { cells: ['<strong>説明責任の重さ</strong>', '中', '高', '非常に高い'] },
          { cells: ['<strong>精神的な負荷</strong>', '中〜高', '高', '非常に高い'] },
          { cells: ['<strong>成果が見えるまで</strong>', '比較的早い', '中期', '長期'] },
          { cells: ['<strong>価値の出し方</strong>', '現場を止めずに進める', 'AIを「使える」から「使われる」に変える', '判断を属人化させず再現可能にする'] },
          { cells: ['<strong>向いている人</strong>', '技術で現場を前に進めたい人', '技術と業務を横断したい人', '判断・設計を仕事にしたい人'] },
          { cells: ['<strong>向いていない人<br>（示唆）</strong>', '仕様固定・安定前提で<br>働きたい人', '正解をすぐ<br>欲しい人', '人・評価の摩擦を<br>避けたい人'] },
          { cells: ['<strong>キャリアの伸び方</strong>', '技術深化／<br>現場信頼の蓄積', '横断スキル／<br>AI設計力', '経営・組織レイヤー<br>への接続'] }
        ]
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p style="text-align: center; margin-top: 2rem;"><strong>この表を見て、一番「重い」と感じたのはどこでしたか？</strong></p>',
        alignment: 'center'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>これらの案件は、どれか一つを選んで終わるものではありません。</p><p>実際の現場では、複数の要素が同時に起き、その都度、判断と説明が求められます。</p><p>もし今、「どれも少し重い」と感じたなら、それは自然な反応です。</p><p>大切なのは、どれを避けたいかではなく、<strong>どれを引き受けたいかを考えること</strong>です。</p>',
        alignment: 'center'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-3',
        title: '新規事業・アライアンス・M&A',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、新規事業を「拡張」ではなく「必然」として扱います。現場で起きた課題を解像度高く理解できるからこそ、プロダクト化の機会が生まれます。</p><p>また、FLARETECHは自社完結にこだわりません。責任範囲を完結させるために、連携・OEM・アライアンスを選びます。</p><p><strong>例：</strong>研修会社・販売チャネルとの提携（OEM）［差し替え：ジェイック等］ <strong>例：</strong>大手SIerとのアライアンス［差し替え］ <strong>例：</strong>顧問ネットワーク・紹介導線の活用［差し替え］</p><p>"作る力"だけでなく、"届ける構造"まで設計することに興味がありますか？</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-4',
        title: '現場へのこだわり（開発／AI／HR）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、現場の仕事を「下請け」と捉えません。現場は、課題と意思決定が最も濃く出る場所です。</p><ul><li>要望を作るのではなく、課題を定義し直す</li><li>作って終わりではなく、運用・改善まで責任を持つ</li><li>「説明できる設計」を前提にする（属人化を許容しない）</li></ul><p>現場で蓄積した知見は、AI導入支援やAI×HRのプロダクト化にも循環します。現場 → ナレッジ → 仕組み化 → 再利用の循環をつくることが、FLARETECHの強みです。</p><p>"納品"で終わる仕事ではなく、"価値が出るまで"を仕事にできますか？</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-5',
        title: '事業課題と機会',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは課題を隠しません。課題はそのまま「この会社で引き受ける責任」だからです。</p><h3>課題（例）</h3><ul><li>AIは導入より運用が難しい（定着がボトルネック）</li><li>HR領域は属人化しやすく、説明責任が重い</li><li>現場では「技術」より「判断と説明」が詰まりやすい</li></ul><h3>機会（例）</h3><ul><li>AIで作業は減るが、判断は増える</li><li>エンタープライズは業務量が大きく、改善インパクトが出やすい</li><li>現場知見がプロダクトに再利用できる</li></ul><p>FLARETECHは、この変化を追い風として語りません。引き受ける責任として扱い続けることを選びます。</p><p>「正解がない領域」で、判断と説明を続ける覚悟はありますか？</p>',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>代表メッセージ</h3><p>FLARETECHは、できることが多い会社ではありません。</p><p>その代わり、引き受ける責任を、最初から曖昧にしない会社です。</p><p>現場に入り、判断し、説明し、途中で投げずにやり切る。</p><p>私たちは、その一連を「仕事」だと定義しています。</p><p>技術が好きな人は多い。AIに興味がある人も増えています。</p><p>けれど、判断が必要な場面に立ち続けたい人は、まだ多くありません。</p><p>FLARETECHは、その少数派であることを前提に、事業をつくり、組織を組んでいます。</p><p>この事業の中で、あなたは、どの責任を引き受けたいですか。</p><p>それを考えたいと思えたなら、ぜひお話ししましょう</p>',
        alignment: 'center'
      }
    },

    {
      type: 'richText',
      data: {
        content: '<a href="/about/prerequisites"><strong>入社前に知ってほしい前提へ</strong></a><br>働く上で大切にしている前提や考え方をまとめています。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// about Prerequisites Page
export const aboutPrerequisitesPageData = {
  title: '入社前に知ってほしい前提',
  titleEn: 'Prerequisites',
  subtitle: 'ここに書いているのは、FLARETECHの良いところを並べた説明ではありません。実際に働く中で、何度も立ち返ることになる考え方や前提をまとめています。',
  slug: '/about/prerequisites',
  category: 'about',
  site: 'career',
  metaTitle: '入社前に知ってほしい前提 | FLARETECH採用サイト',
  metaDescription: '入社してから知るのではなく、入社前に知っておいてほしい。FLARETECHが「一時的な関係」ではなく、長く一緒に価値を積み上げていく関係を前提にしているからです。',
  metaKeywords: [],
  metadata: {
    updatedDate: '2026.xx.xx',
    readTime: '約8分'
  },
  showToc: true,
  tocLinks: [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'richText',
      data: {
        content: '<p>入社してから知るのではなく、入社前に知っておいてほしい。</p><p>それは、FLARETECHが「一時的な関係」ではなく、長く一緒に価値を積み上げていく関係を前提にしているからです。</p><p>ここに書かれている前提を読んで、自分がどんな働き方をしたいのか、どんな姿勢で仕事に向き合いたいのか。</p><p>その視点で、読み進めてみてください。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/prerequisites/prereq-1.png',
        alt: 'FLARETECHで働く前提'
      }
    },
    {
      type: 'numberedList',
      data: {
        showImage: false,
        items: [
          {
            title: 'FLARETECHでは、全員が主役です',
            description: '役割や年次に関係なく、一人ひとりが「意思決定と結果」に向き合います。主役である以上、責任も裁量も分かち合います。'
          },
          {
            title: '長く在籍してほしいと、本気で考えています',
            description: '短期的な成果や消耗ではなく、数年単位で価値を積み上げられる関係を前提にしています。だからこそ、配置・役割・期待値は対話で更新します。'
          },
          {
            title: 'キャリアは固定せず、更新し続けます',
            description: '入社時の肩書きや期待は、あくまでスタート地点です。事業の変化と個人の成長に合わせて、役割や挑戦領域を見直すことを前提にしています。'
          },
          {
            title: '厳しさはありますが、切り捨てはしません',
            description: '要求水準は高いです。ただし、個人に押し付けることはしません。チーム・仕組み・上司が責任を持って支えます。'
          },
          {
            title: '考えることを、仕事の中心に置いています',
            description: '「何をやったか」だけでなく、「なぜそう考えたか」「どう更新したか」を重視します。思考が止まらない限り、成長の余地はあります。'
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/prerequisites/prereq-2.jpg',
        alt: 'チームで協力して働く'
      }
    },
    {
      type: 'numberedList',
      data: {
        showImage: false,
        items: [
          {
            title: 'AIや新技術は、長く戦うための武器です',
            description: '一部の得意な人のものではありません。全員が使い、全員が試し、仕事の持続性を高めるために導入しています。'
          },
          {
            title: '忙しい局面はありますが、消耗前提ではありません',
            description: '負荷がかかる時期はあります。その前提で、回復と分散の設計を行います。長く走るためのペース配分を重視します。'
          },
          {
            title: '評価は「積み上げ」を見ます',
            description: '単発の成果より、再現性・学習・チームへの波及を評価します。続けるほど価値が増す設計です。'
          },
          {
            title: '組織は未完成ですが、だからこそ居場所があります',
            description: '完成された箱ではありません。改善余地があり、関われる余白があります。長くいるほど、影響範囲は広がります。'
          },
          {
            title: 'この前提を理解した上で、選んでほしい',
            description: 'FLARETECHは、「腰を据えて挑み続けたい人」に向いています。その覚悟を、会社も引き受けます。'
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/prerequisites/prereq-3.jpg',
        alt: 'FLARETECHの働き方'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>ここまで読んで、すべてにすぐ納得できなくても問題ありません。</p><p>大切なのは、「こういう前提で成り立っている会社なんだ」と理解した上で、それでも腰を据えて挑み続けたいと思えるかどうかです。</p><p>FLARETECHは、全員が主役として関わり、役割や期待を更新しながら、長い時間をかけて価値を積み上げていく組織でありたいと考えています。</p><p>この前提を共有できるなら、ここでの経験は、きっと一過性では終わりません。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/about/ai"><strong>AIと働くFLARETECHの現在地</strong></a><br>FLARETECHにおけるAI開発の考え方をまとめています。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// About AI Page
export const aboutAIPageData = {
  title: 'AIと働くFLARETECHの現在地',
  titleEn: 'AI & Development',
  subtitle: 'FLARETECHでは、生成AIを「便利なツール」ではなく、開発・育成・組織設計そのものを変える前提条件として扱っています。',
  slug: '/about/ai',
  category: 'about',
  site: 'career',
  metaTitle: 'AIと働くFLARETECHの現在地 | FLARETECH採用サイト',
  metaDescription: 'AIを前提にした開発・育成・組織づくり。FLARETECHにおけるAI開発の考え方、AI R&D室の役割と実践、AIを前提にしたエンジニア育成をご紹介します。',
  metaKeywords: [],
  metadata: {
    updatedDate: '2026.xx.xx',
    readTime: '約8分'
  },
  showToc: true,
  tocLinks: [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>このページでは、</p><ul><li>FLARETECHにおけるAI開発の考え方</li><li>AI R&D室の役割と実践</li><li>AIを前提にしたエンジニア育成・キャリア設計</li></ul><p>をまとめてお伝えします。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-1',
        title: 'なぜ、AIを"前提"にしているのか',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>生成AIの進化によって、「コードを書くこと」自体の価値は急速に下がっています。</p><p>一方で、価値が上がっているのは次の領域です。</p><h4>人が担うべき価値</h4><ul><li>要件を構造化する力</li><li>仕様の矛盾に気づく力</li><li>ビジネスと技術をつなぐ設計力</li><li>品質・再現性・セキュリティを守る判断</li></ul><p>FLARETECHではこの変化を一過性のトレンドではなく、不可逆な前提条件として捉えています。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/ai/ai-1.jpg',
        alt: 'AIと働くFLARETECH'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-2',
        title: 'FLARETECHのAI開発思想',
        subtitle: 'AIは「代替」ではなく「増幅装置」',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHにおけるAIの位置づけは明確です。</p><blockquote><p>AIは、人の仕事を奪うものではなく、人の判断力・設計力・再現力を拡張する存在</p></blockquote><ul><li>コーディングはAIに任せる</li><li>仕様・構造・品質の判断は人が担う</li><li>人は「考える仕事」に集中する</li></ul><p>この分業を前提に、開発プロセスを再設計しています。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-3',
        title: 'AI R&D室とは何をしている組織か',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHには、AI活用を"個人任せ"にしないための専門組織として<strong>AI R&D室</strong>があります。</p><h4>AI R&D室の役割</h4><ul><li>AI駆動開発の実案件への適用</li><li>成功・失敗パターンの蓄積と標準化</li><li>全社向けトレーニング・ハッカソンの設計</li><li>セキュリティ・品質基準の整備</li></ul><p>単なる研究組織ではなく、「現場で使える形に落とす」ことがミッションです。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/ai/ai-2.jpg',
        alt: 'AI R&D室での活動'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-4',
        title: '数字で見る、AI活用のロードマップ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、3年以内にエンジニアの<strong>80%</strong>がAI駆動開発を経験することを目標にしています。これは「義務」ではなく、市場価値を高めるための実装ロードマップです。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'featureGrid',
      data: {
        columns: 3,
        items: [
          {
            title: '1年目',
            description: '基礎習得'
          },
          {
            title: '2年目',
            description: '実案件適用・応用習得'
          },
          {
            title: '3年目',
            description: '標準化・リーダーシップ'
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/ai/ai-3.jpg',
        alt: 'エンジニアの成長ロードマップ'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-5',
        title: 'AIを前提にしたエンジニア育成',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、AIを使えるエンジニアではなく<strong>AIを前提に設計できるエンジニア</strong>を育てます。</p><h4>推奨されるキャリアパス</h4><p>PG → SE → 上流SE → PM <small>（コーディング×AI → 設計×AI → 要件定義×AI）</small></p><h4>必須スキルの変化</h4><ul><li>プロンプト設計・エージェント設計</li><li>要件定義と仮説検証</li><li>テスト設計・品質保証</li><li>セキュリティ・再現性の担保</li></ul><p>AIは「近道」ではなく、より高い視座に上がるための踏み台です。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-6',
        title: '現場での実践（リアルな声）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<blockquote><p>「10年物のレガシーアプリを1日で書き換えた」</p></blockquote><blockquote><p>「新人でもAIを使ってPM的な動きができている」</p></blockquote><blockquote><p>「設計と品質に集中できるようになった」</p></blockquote><p>こうした声が出てくるのは、AIを個人スキルではなく、組織スキルとして扱っているからです。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/ai/ai-4.jpg',
        alt: '現場でのAI活用実践'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-7',
        title: '忙しさと向き合うスタンス',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>AI導入によって、仕事が一時的に忙しくなるフェーズはあります。</p><p>ただしFLARETECHでは、</p><ul><li>消耗を前提にしない</li><li>自動化・再利用・標準化で回収する</li><li>長期的に楽になる設計を優先する</li></ul><p>というスタンスを取っています。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-8',
        title: 'この環境が向いている人',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>AIを「使う側」から「設計する側」に行きたい</li><li>技術トレンドを実案件で試したい</li><li>コーディングだけで終わりたくない</li><li>市場価値を数年単位で高めたい</li></ul>',
        alignment: 'left'
      }
    }, {
      type: 'sectionTitle',
      data: {
        id: 'section-9',
        title: '最後に',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>AI時代の開発は、「書ける人」ではなく「考えられる人」が選ばれる時代です。</p><p>FLARETECHは、その変化を恐れず、先に実装し、人とAIが共に強くなる組織をつくっています。</p><p>腰を据えて、次のスタンダードを一緒につくりませんか。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/about/story"><strong>AFLARETECHの物語</strong></a><br>出来事ではなく「何が転機になり、何を選び、何が積み上がったか」で辿ります。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// About Story Page
export const aboutStoryPageData = {
  title: 'FLARETECHの物語',
  titleEn: 'Our Story',
  subtitle: '更新日：— ｜ 読了時間の目安：約12〜15分',
  slug: '/about/story',
  category: 'about',
  site: 'career',
  metaTitle: 'FLARETECHの物語 | FLARETECH採用サイト',
  metaDescription: '創業から社名変更までの5年を、出来事ではなく「何が転機になり、何を選び、何が積み上がったか」で辿ります。',
  metaKeywords: ['FLARETECH', 'ストーリー', '創業', '社名変更', 'AI転換', '第二創業'],
  showToc: true,
  tocLinks: [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、最初から完成された会社ではありません。</p><p>「挑戦したい」という衝動と、「諦めない」という約束が先にあり、仕組みも文化も、あとから追いついてきました。</p><p>このページでは、創業から社名変更までの"5年"を、出来事ではなく <strong>「何が転機になり、何を選び、何が積み上がったか」</strong> で辿ります。</p>',
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/story/story-1.jpg',
        alt: 'FLARETECHの始まり'
      }
    },
    {
      type: 'timeline',
      data: {
        items: [
          {
            year: '2020',
            title: '火種：2人から始まった',
            description: 'はじまりは、立派なオフィスでも、整った計画でもありません。帰り道の食事の席で「やる」と決め、2人で走り出したのが起点でした。\n\n当時から一貫していたのは、会社を大きくすることよりも、「ビジネスが分かるエンジニア集団を作る」という意思です。\n\nそして象徴的なのが、ロゴづくりの迷走です。フォントの案が飛び、結局「河畑が作る」ことになり、試行錯誤の末に"決めた"。この雑さと執念が、当時の等身大でした。',
            image: '' // Placeholder removed for presentation
          },
          {
            year: '2021',
            title: '加速：採用へ資金を投下し、勝ち筋を掴む',
            description: '1年目は、現場に出ながら、採用と営業を回す時期でした。\n\n転機は「採用に張る」と決めたこと。現場から抜け、借入も行い、採用に資金を投下する。ここで組織は"増やせる状態"に入ります。結果、応募は増え、面接は土日に18件というレベルまで積み上がり、期末には社員が大きく増えました。\n\nこの頃に生まれたのは、単なる人数ではなく、「採用を勝てるオペレーションにする」という再現性でした。',
            image: '' // Placeholder removed for presentation
          },
          {
            year: '2022',
            title: '会社化：部署が生まれ、場が広がる',
            description: '人数が増えると、場が必要になります。オフィス移転、イベントの開催、採用活動の分業。会社が"会社の形"になっていく。\n\nそして重要なのが、機能の誕生です。キャリア管理・広報・採用といった「人の挑戦を支える機能」が、この頃に育ち始めます。',
            image: '' // Placeholder removed for presentation
          },
          {
            year: '2023',
            title: '基盤：MVVとDXで、意思決定をチームへ移す',
            description: 'この年に、MVVが言語化され、社内業務のDXも進みます。\n\nここで起きた変化は大きい。"頑張る会社"から、"積み上げが残る会社"へ。属人的な努力ではなく、仕組みで増やすフェーズに入った、ということです。',
            image: '' // Placeholder removed for presentation
          },
          {
            year: '2024',
            title: '連鎖：200名を超え、挑戦が文化になる',
            description: '数字で見れば、売上は1期1億→5期18億へ。社員は200名を突破し、部長が生まれ、オンボーディングやサークル活動など「つながりの設計」も動き始めます。\n\nこの段階で、会社は"人数を増やす会社"から、"人が育ち、挑戦が連鎖する組織"へ移行した、と資料自身が総括しています。',
            image: '' // Placeholder removed for presentation
          },
          {
            year: '2025',
            title: '第二創業：Principleと社名変更、そしてAI転換へ',
            description: '200人を超えた時、バリューだけでは行動が揃いにくくなる。だからPrinciple（行動原理）が追加され、「どう動くか」「何を生むか」を言語化した。\n\nそして社名変更。FLARE＝炎・光・広がり、TECH＝技術・知恵・人の力。"挑戦する人の火種"を社会へ広げる、という決意を名前に刻んだ。',
            image: '' // Placeholder removed for presentation
          },
          {
            year: '2026',
            title: '支援の担い手が現場へ',
            description: 'エンジニアリーダーが1on1とキャリア相談を担い、教育推進が全社のテーマになります。',
            image: ''
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/story/story-5.jpg',
        alt: 'FLARETECHの未来へ'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>5年前、火種は小さかった。</p><p>でも、火種は"集まる"ことで炎になる。炎は"重なる"ことで社会を照らす。</p><p>FLARETECHは、これからの5年で、挑戦を文化として根づかせ、次の世代の当たり前を作っていきます。\n\n次の章は、AIです。AI駆動開発を「会社の標準」にし、3年以内に80%が経験する。育成の方向性は、上流/PMへ。ここまでを、すでに社内資料として宣言しています。’</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/about/future"><strong>私たちの進む方向</strong></a><br>予測ではなく、設計で近づける。FLARETECHが見据える未来',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// About Future Page
export const aboutFuturePageData = {
  title: '私たちの進む方向',
  titleEn: 'Future Direction',
  subtitle: 'FLARETECHが、これから向かう場所 ｜ 更新日：2026.01.xx ｜ 読了時間の目安：約7分 ※この記事は代表のメッセージを、そのまま掲載しています。',
  slug: '/about/future',
  category: 'about',
  site: 'career',
  metaTitle: '私たちの進む方向 - FLARETECHが、これから向かう場所 | FLARETECH採用サイト',
  metaDescription: '予測ではなく、設計で近づける。FLARETECHが見据える未来と、AI駆動開発の標準化への挑戦。代表からのメッセージ。',
  metaKeywords: ['FLARETECH', '未来', 'AI駆動開発', '標準化', '代表メッセージ'],
  showToc: true,
  tocLinks: [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>採用サイトをつくるにあたって、「会社がどこへ向かうのか」を言葉にしてほしいと言われました。正直、これまで同じ問いには何度も答えてきたつもりです。でも、いざ文章にすると、安易なスローガンに逃げたくなる。だから今回は、断言ではなく、前提と意思だけを書きます。</p><p>私は、見えていないものを「こうなる」と決め打ちするのが苦手です。ただし、経営者として、何を見て、何に賭け、何を諦めないかは明確に言えます。それが、ここに書くことです。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '1. 予測より、設計で近づける',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>「10年後どうなっていますか？」と聞かれたら、まずこう答えます。<strong>正確にはわからない。</strong> でも、近づけるための設計ならできる。</p><p>ここ数年で、仕事の価値は「経験年数」よりも「再現性」に寄ってきました。そしてAIは、その傾向を極端に加速させています。だから未来を"当てる"より、未来に対して壊れにくい構造を作る方が強い。</p><p>FLARETECHが言いたいのは、こういうことです。</p><ul><li>予測で安心するのではなく、前提を揃えて前に進む</li><li>スローガンで盛り上げるのではなく、再現できる仕組みを残す</li><li>一時の熱狂ではなく、数年かけて強くなる設計をする</li></ul>',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/future/future-1.jpg',
        alt: '設計で未来に近づける'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '2. いま起きている変化は「開発の定義」そのものを変える',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>AIは「効率化ツール」ではありません。開発という行為の構造を変える道具です。</p><p>コードを書くこと自体のコストは、これからもっと下がります。その一方で、難しくなるのは別の部分です。</p><ul><li>何をつくるべきか（要件）</li><li>何を守るべきか（制約）</li><li>なぜそれが正しいのか（検証）</li><li>誰が責任を持つのか（説明）</li></ul><p>つまり、AI時代に残るのは「作業」ではなく、判断と設計です。エンジニアの価値も、実装量ではなく、要件・品質・再現性・セキュリティに寄っていく。</p><p>ここで曖昧にすると、SESは特に弱くなります。現場が分散しているからこそ、やり方がバラける。だからこそ、会社としてやるべきことは一つです。<strong>"AI前提の開発"を、個人芸にしない。標準にする。</strong></p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '3. FLARETECHが賭けるのは、AI駆動開発の"標準化"',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの意思は明確です。<strong>AI駆動開発を、会社の強みにする。</strong></p><p>目標はシンプルです。<strong>3年以内に、エンジニアの80%がAI駆動開発を実戦で経験する。</strong>（研修だけで終わらせない。案件で使う。）</p><p>そのために、AI R&D室は「研究室」ではなく、現場の勝ち筋を作る組織として動いています。</p><ul><li>研修とPoCで終わらせず、案件に落とす</li><li>成功パターンをテンプレ化し、横展開する</li><li>品質・セキュリティ・標準化まで含めて設計する</li></ul><p>そして、これをエンジニアだけの話にしません。営業も、管理も、採用も、AIを「個人の便利技」ではなく、組織の武器として持つ。Lens AIのような取り組みも、同じ思想の延長線上にあります。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '4. 事業は広げる。軸はぶらさない',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、ただの開発会社で終わる気はありません。でも、何でも屋にもならない。</p><p>軸は一つです。<strong>"人が長く価値を積み上げられる構造"を、技術でつくる。</strong>だから、事業は広がっても、中心の問いは変わりません。</p><ul><li><strong>SES：</strong>現場で価値を出し続けるための土台</li><li><strong>AI/HR：</strong>評価・育成・組織運用の非効率を構造から変える</li><li><strong>研修・導入支援：</strong>企業がAIを使いこなすための実装支援</li></ul><p>「事業拡大」は目的ではなく、問いを解く手段です。やることが増えるほど、判断が鈍る会社もある。FLARETECHは逆で、やることが増えるほど、OS（基準）を強くする方向に進みます。</p>',
        alignment: 'left',
        imageUrl: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/future-vision.jpg'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '5. 他社が避ける選択を、仕組みでやり切る',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>成長期の会社は、勢いで走れます。でも、勢いは再現できない。だから崩れます。</p><p>FLARETECHは、成長の初期から「再現性」を優先してきました。</p><ul><li>教育・評価・運用を仕組みに落とす</li><li>属人を称賛しすぎず、型を残す</li><li>現場分散でも品質が揃う標準を作る</li></ul><p>派手ではありません。ただ、長期で見ると、これが一番強い。</p><p>AI駆動開発の標準化も同じです。AIを"使える人"を増やすだけなら簡単。難しいのは、"壊れない形"で普及させることです。そこに本気で取り組みます。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '6. FLARETECHで育てたい力',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>技術が速くなるほど、人に残るのは「姿勢」と「判断」です。FLARETECHが大切にしたいのは、次の3つです。</p><h4>1）挑戦を続ける力</h4><p>未知に踏み込み、学習し、更新する。止まらないこと。</p><h4>2）誠実を貫く力</h4><p>品質・セキュリティ・説明責任から逃げないこと。</p><h4>3）共創して前に進む力</h4><p>一人で勝つのではなく、仕組みとチームで勝つこと。</p><p>AIは、この3つをサボるための道具ではありません。この3つを、より高い水準でやり切るための道具です。</p>',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/future/future-2.jpg',
        alt: 'FLARETECHの未来'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '7. まだ答えは置いていない',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、完成された場所ではありません。ただし、未完成を言い訳にもしません。</p><p>私たちがやりたいのは、「出来上がった船に乗る」ことではなく、次の航路を決め、推進力を増やす側に回ることです。</p><p>変化が起きたときに、問い直せる。選び直せる。作り直せる。その筋力を、組織として持ちたい。</p>',
        alignment: 'center'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/about/experience-shift"><strong>企業の体験価値シフトの歴史</strong></a><br>企業の成長要因が「技術」や「規模」から「体験価値」へとシフトした歴史を辿り、FLARETECHが目指す方向性を理解する。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// About Design Shift Page
export const aboutExperienceShiftPageData = {
  title: '企業の体験価値シフトの歴史',
  titleEn: 'Experience Shift',
  subtitle: '企業の成長要因が「技術」や「規模」から「体験価値」へとシフトした歴史を辿り、FLARETECHが目指す方向性を理解する。',
  slug: '/about/experience-shift',
  category: 'about',
  site: 'career',
  metaTitle: '企業の体験価値シフトの歴史 | FLARETECH採用サイト',
  metaDescription: '企業の成長要因が「技術」や「規模」から「体験価値」へとシフトした歴史を辿り、FLARETECHが目指す方向性を理解する。',
  metaKeywords: ['体験価値', 'デザイン', 'DX', '生成AI', 'UX', 'FLARETECH'],
  showToc: true,
  tocLinks: [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>企業の成長要因は、長い間「技術」や「規模」だと言われてきました。しかし今は、もう少し解像度の高い言い方が必要です。</p><p>結論から言うと、企業はこの数十年で <strong>"何を作るか"</strong> 以上に、<strong>"どう感じられ、どう使われ、どう続くか"</strong> で評価されるようになりました。</p><p>このページでは、戦後から現在までを振り返りながら、企業が「体験価値」をどのように扱うようになったのかを整理します。それは、FLARETECHが何に向き合い、どんな人材価値をつくろうとしているのかを理解するための前提でもあります。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '1. 「見た目」から始まった価値の設計',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>企業が最初に「デザイン」を必要としたのは、美しさを足すため、というより <strong>"品質を伝えるため"</strong> でした。</p><ul><li>良いモノを作る</li><li>それを分かりやすく見せる</li><li>信頼できる企業だと感じてもらう</li></ul><p>この3点は、今のプロダクトでも同じです。ただし、現在は対象が「モノ」だけではなく、サービス、ソフトウェア、体験、運用へと広がりました。</p><p>つまり、デザインの役割は「装飾」ではなく、<strong>価値の設計（Value Design）</strong>へ変わっていった。この変化が、企業の競争のルールを変えました。</p><p>「デザインは"装飾"から、いつ"価値の設計"に変わったのか？」</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '2. 戦後〜1990年代：モノの時代と"伝える技術"',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>戦後の日本で企業が強くなった理由は、高品質な製品を大量に作り、世界に届けたことにあります。</p><p>ここでの"設計"は主に2種類でした。</p><h4>工業の設計：性能と品質を成立させる</h4><ul><li>壊れにくい</li><li>使いやすい</li><li>量産できる</li><li>コストに収まる</li></ul><p>価値は「製品の中」に閉じていて、勝負はハードウェアや製造の強さでした。</p><h4>広告の設計：信頼と欲求を成立させる</h4><p>一方で、企業は「伝える」ために設計を必要としました。パッケージ、広告、CI。これは、商品の差が縮まるほど重要になる技術でした。</p><p>この時代に企業が学んだことは単純です。<strong>良いものを作るだけでは勝てない。良いものだと"理解される状態"まで設計する必要がある。</strong></p><p>「"良いものを作る"だけでは勝てない、と企業が学んだ瞬間はどこか？」</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '3. 2000年代：インターネットが"接点"を増やした',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>2000年代に起きた本質的な変化は、企業と顧客の接点が「売り場」から「画面」へ移ったことです。</p><ul><li>比較が一瞬で終わる</li><li>検索で候補が並ぶ</li><li>クリック一つで離脱できる</li></ul><p>この時点で、企業は初めて<strong>「体験が価値を決める」</strong>世界に入っていきました。</p><p>ただしまだ主戦場はWebサイトやECなど、"接点の整備"が中心です。経営の中心まで体験が入りきったわけではありません。</p><p>「接点が"売り場"から"画面"に移ったとき、競争のルールは何が変わった？」</p>',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/future/future-1.jpg',
        alt: 'デジタル変革と顧客体験の進化'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '4. 2007年以降：スマホが"体験"を主戦場にした',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>スマホが登場したことで、状況は変わります。</p><p>「接点」ではなく「体験そのもの」が商品になった。アプリは、使い続けてもらえなければ価値が出ない。つまり競争力が UI/UXと運用に寄っていきました。</p><p>ここで重要なのは、企業が初めて<strong>"継続的に改善する前提"</strong>で価値を設計し始めたことです。</p><ul><li>一度作って終わり、ではない</li><li>使われ方を見て直す</li><li>直した結果をまた計測する</li></ul><p>この循環が、プロダクト開発を"制作"から"経営"に変えていきました。</p><p>「なぜスマホは、"プロダクト＝体験"という前提を当たり前にしたのか？」</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '5. 2010年代：SaaSが"継続"を競争力に変えた',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>SaaSが普及して、企業の勝ち方が変わります。売り切りではなく、継続課金。導入ではなく、定着。機能ではなく、成果。</p><p>ここでの設計対象は、UIだけではありません。</p><ul><li>オンボーディング</li><li>運用フロー</li><li>カスタマーサクセス</li><li>価格</li><li>契約と解約理由</li></ul><p>つまり「プロダクト体験」と「業務体験」が一体化し、企業は <strong>成果が出るまでの全体設計</strong> を求められるようになった。</p><p>この流れが、ITの仕事を「作る」から<strong>"成果につながる状態を作る"</strong>へ引き上げました。</p><p>「売り切りから継続へ。評価軸が"機能"から"成果"に変わった理由は？」</p>',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/ai/ai-4.jpg',
        alt: 'DXとデジタル戦略の推進'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '6. 2018年以降：DXが「顧客起点」を経営に戻した',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>DXが広がった本質は、ツール導入ではありません。企業が改めて <strong>顧客起点に戻ったこと</strong>です。</p><ul><li>顧客が求める価値は何か</li><li>その価値を提供する一連の体験は何か</li><li>どこが詰まり、どこで離脱しているか</li><li>それをどう改善するか</li></ul><p>この問いは、単なるデザインの領域ではなく、経営・事業・現場の設計に直結します。</p><p>そしてここまで来ると、企業は次の壁に当たります。<strong>複雑すぎて、人の力だけでは回らない。</strong></p><p>「DXの本質はツールではない。では企業は何を"取り戻した"のか？」</p>',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '7. 2020年代：生成AIが"定義と保証"を価値に押し上げた',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>生成AIは、制作のコストを下げました。しかし同時に、別の問題を大きくします。</p><ul><li>作るスピードが上がるほど</li><li>変更量が増えるほど</li><li>"正しさ"の担保が難しくなる</li></ul><p>この時代に価値が上がるのは、単純な実装力ではなく、次の領域です。</p><ul><li>仕様を定義する（曖昧さを消す）</li><li>品質を保証する（壊れない仕組み）</li><li>説明できる形で残す（運用できる知）</li></ul><p>つまり、AI時代の競争力は<strong>「速さ」×「保証」</strong>で決まる。</p><p>この2つを両立できる組織は、強い。そしてこれは、個人の才能ではなく標準化と育成で作れる領域です。</p><p>「速く作れる時代に、逆に"正しさ"が難しくなるのはなぜか？」</p>',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/ai/ai-1.jpg',
        alt: 'AI時代の価値創造とイノベーション'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '8. FLARETECHがこの流れで担う役割',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>ここまでの歴史を、FLARETECHに引き寄せると結論は明確です。</p><p>私たちは、企業の「体験価値」を支える最前線にいます。そして今、その最前線はAIで再定義されつつあります。</p><p>FLARETECHが目指すのは、<strong>AI前提の開発と育成を"会社の標準"にすること</strong>です。</p><ul><li>速く作るだけでは終わらない</li><li>正しく作り、長く運用できる状態まで持っていく</li><li>それを再現可能な型に落とす</li><li>現場で積み上がるキャリアにする</li></ul><p>この歴史の延長線上で、私たちは「エンジニアの市場価値を高める」という言葉を、スローガンではなく <strong>運用として実現していきます。</strong></p><p>「この変化のど真ん中で、FLARETECHは"何を標準化"しようとしているのか？」</p>',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>企業の体験価値は、つくる人間の選択によって更新されてきました。</h3><p>この延長線上に、あなた自身のキャリアを重ねられるか。</p><p>それを確かめに来てください。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/about/leadership"><strong>FLARETECHの経営陣</strong></a><br>FLARETECHの経営チームを紹介。代表河畑、CTO高木、取締役中釜が考える、採用における大切なこと、そして社員から見た経営陣とは。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// About Leadership Page
export const aboutLeadershipPageData = {
  title: 'FLARETECHの経営陣',
  titleEn: 'Leadership Team',
  subtitle: '',
  slug: '/about/leadership',
  category: 'about',
  site: 'career',
  metaTitle: 'FLARETECHの経営陣 | FLARETECH採用サイト',
  metaDescription: 'FLARETECHの経営陣は、「強い個」を誇示するための存在ではありません。それぞれが異なる専門性と原体験を持ち、構造として会社を前進させる役割を担っています。',
  metaKeywords: [],
  metaUpdatedAt: '2026年1月',
  metaReadingTime: '約8分',
  showToc: true,
  tocLinks: [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの経営陣は、「強い個」を誇示するための存在ではありません。<br>それぞれが異なる専門性と原体験を持ち、構造として会社を前進させる役割を担っています。</p><p>このページでは、経歴や肩書きだけでなく、<br><strong>「どんな意思決定をする人なのか」「何を信じて会社を動かしているのか」</strong>に焦点を当てて紹介します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '代表取締役 CEO',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '河畑 福太郎',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>Fukutaro Kawabata</strong></p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/leadership/kawabata-2026.jpg',
        alt: '代表取締役CEO 河畑福太郎'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '静かに、深く、長く積み重ねる者の物語',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>1989年12月、大阪府岸和田市生まれ。<br>だんじり祭りの熱狂の中で育ちながら、本人は常に「一歩引いた視点」で世界を見ていた。</p><p>外側は騒がしく、内側は極端に静か。<br>河畑の思考の原点は、この外界と内面のギャップにある。</p><p>幼少期はスケボー、ポケモンカード、バトル鉛筆に熱中し、ときに衝動的な行動もあった。一方で、内側では常に「なぜそうなるのか」「どうすれば再現できるのか」という分析が止まらなかった。</p><p>偉人の伝記を読み漁り、<br>「人生を分けたのはどの選択か」<br>「成功の裏にある構造は何か」<br>を分解し続けた少年時代は、後の経営スタイルそのものにつながっている。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '組織を見る目が育った学生時代',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>中学は1学年11人という極端な環境。<br>「多数派」が存在しないため、河畑の行動原理は自然と<br><strong>"他人に合わせる"ではなく"自分の論理に従う"</strong>へと向かった。</p><p>高校・大学では体育会サッカー部に所属。<br>試合よりも彼の関心を引いたのは、<br>「なぜこの組織は機能しているのか」<br>「役割分担と情報伝達はどう設計されているのか」<br>という組織の力学だった。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社会人前半：泥臭さがつくった"地力"',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>新卒後、太陽光、保険、不動産などの営業に従事。<br>1日400〜500件のテレアポ、戸建てへの飛び込み営業。</p><p>断られ続ける現場で河畑が掴んだのは、<br>「営業は才能ではない。再現可能な構造だ」という確信だった。</p><p>この経験は、FLARETECHの営業設計・評価設計の原型となっている。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社会人後半：ITと構造化の融合',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>ITコンサルタントとして、要件定義、PMO、システム統合を経験。<br>さらにM&A統合プロジェクトで、会社そのものを「システム」として扱う経験を積む。</p><p>泥臭い現場で得た実行力<br>↓<br>ITで磨いた構造化・設計力</p><p>この二つが揃ったとき、<br>「会社をつくる準備が整った」と確信した。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'FLARETECHという"作品"',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>2020年11月、FLARETECH株式会社を創業。<br>3年で150名超のエンジニア組織へと成長。</p><p>河畑の意思決定軸は一貫している。</p><p><strong>長期合理性</strong>：5年後・10年後に意味があるか<br><strong>再現性</strong>：個人の才能に依存していないか<br><strong>誠実さ</strong>：信頼と透明性を守れているか</p><p>「静かに考え、静かに積み上げ、静かに未来をつくる」<br>それが、FLARETECHの経営スタイルの根底にある。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '取締役 / CTO',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '高木 昇',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>Kai Takagi</strong></p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/leadership/takagi.jpg',
        alt: '取締役CTO 高木昇'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '技術で、世界のルールを変える人',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>2020年11月11日、河畑と共にFLARETECHを共同創業。<br>Java：12年<br>iOS：5年<br>PM：3年</p><p>エンジニア、PM、コンサル、営業を横断してきた稀有な存在。</p><p>人事システムを5年間開発・導入し、<br><strong>「技術 × 業務 × 組織」</strong>を一体で捉える視点を持つ。</p><p>USJ、スターバックス、タニタなどのプロジェクト経験から、<br>「体験は、意識調査と設計でつくられる」ことを熟知している。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '何かを変えられる技術者であれ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>高木がエンジニアに伝え続けている言葉がある。</p><p>バグを直せる<br>↓<br>上司の意見を変えられる<br>↓<br>プロジェクトの進捗を好転できる<br>↓<br>会社を成長させられる<br>↓<br>ビジネスのルールを変えられる<br>↓<br>国家のあり方すら変えられる</p><p><strong>技術とは、影響範囲の広い意思決定手段である。</strong></p><p>FLARETECHが「エンジニアの市場価値」にこだわる理由を、<br>最も体現している存在。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '取締役',
        alignment: 'left'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '中釜 直樹',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>Naoki Nakagama</strong></p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/leadership/nakagama.jpg',
        alt: '取締役 中釜直樹'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '現場を知り、数字で語る営業責任者',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>2014年、大手通信系企業に入社。法人営業に従事。<br>厳しい環境の中で、成果を出し続ける営業を経験。</p><p>2022年、成長性と文化に惹かれFLARETECHへ参画。<br>現在は営業部のマネージャーとして、現場と経営をつなぐ役割を担う。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '「仕事が趣味」という覚悟',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>趣味は「仕事」。<br>この一言に、中釜のスタンスがすべて表れている。</p><p>数字だけを見るのではなく、</p><p>なぜ受注できたのか<br>↓<br>なぜ失注したのか<br>↓<br>再現できる構造になっているか</p><p>を徹底的に掘り下げる。</p><p>FLARETECHの営業が「属人化しない」理由は、<br>中釜のこの姿勢に支えられている。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '経営陣が大切にしていること',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの経営陣に共通しているのは、<br>声の大きさではなく、判断の一貫性です。</p><p>短期の成果より、長期の信頼<br>派手な成功より、再現可能な成長<br>個人の英雄より、強い構造</p><p>この価値観に共鳴できるかどうか。<br>それが、FLARETECHに向いているかどうかの分かれ目です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/about/leadership/leadership-4.jpg',
        alt: '経営陣とチーム'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社員から見たFLARETECH経営陣',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>― 判断の仕方が、会社の空気をつくっている ―</strong></p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの経営陣を一言で表すなら、<br><strong>「考え方が透けて見える経営」</strong>です。</p><p>強い言葉で鼓舞するタイプでも、<br>トップダウンで引っ張るタイプでもありません。<br>社員が日々感じているのは、<br>意思決定の"型"が一貫しているということです。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '1. 判断基準が感情ではなく「前提」にある',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>経営陣の意思決定は、<br>好き嫌いや勢いでは動きません。</p><p>その判断は長期的に合理的か<br>再現性のある構造になっているか<br>誠実さや信頼を損なわないか</p><p>この3点が、ほぼすべての判断の前提になっています。</p><p>社員から見ると、<br>「今は厳しい」と感じる判断でも、<br>後から振り返ると<br>"なぜそうしたのか"が必ず説明できることが多いです。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '2. 現場を知らずに決めることがない',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの経営陣は、<br>現場との距離が極端に遠くなることがありません。</p><p>実際にどこで詰まっているのか<br>誰に負荷が集中しているのか<br>数字に出ていない違和感は何か</p><p>こうした点を、<br>想像以上に細かく見ています。</p><p>トラブルが起きたときも、<br>「誰の責任か」より<br><strong>「どの構造が問題だったか」</strong>に議論が進むため、<br>現場が萎縮しにくい空気があります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '3. スピードは求めるが、雑さは許さない',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>意思決定は速いです。<br>ただし、準備が整ってから。</p><p>前提整理<br>↓<br>論点の切り分け<br>↓<br>判断軸の明確化</p><p>ここが揃うと、<br>一気に決まり、一気に動きます。</p><p>社員としては、<br>「ちゃんと考えた上で走っている」<br>という納得感があります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '4. 役職よりも、意見の中身が見られる',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、<br>役職によって意見の重みが変わることはほとんどありません。</p><p>新卒の意見が方針に反映される<br>若手の違和感が意思決定を止める<br>昨日決めたことが今日変わる</p><p>こうした場面は珍しくありません。</p><p>それを<br>「ブレ」ではなく<br><strong>「学習速度が速い」</strong>と捉える文化があります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '5. 言葉より、日々の選択で語る経営',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>経営陣は、多くを語りません。<br>代わりに、行動で示します。</p><p>短期利益より信頼を取る<br>人を簡単に切らない<br>見えにくい貢献も評価から外さない</p><p>社員は、<br>スローガンよりも<br>日々の判断の積み重ねから<br>会社の価値観を理解しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社員として感じる距離感',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの経営陣は、<br>「守ってくれる存在」ではありません。</p><p>どちらかと言えば、<br><strong>「ちゃんと考えることを期待してくる存在」</strong>です。</p><p>自分の頭で考えることを求められる<br>正解は用意されていない<br>ただし、誠実に向き合えば必ず見てもらえる</p><p>楽な環境ではありませんが、<br>フェアで、逃げ道の少ない環境だと感じています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '向いている人・向いていない人（社員視点）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>向いている人</strong></p><p>正解のない問いを考えるのが苦ではない<br>長期視点で物事を捉えたい<br>構造や前提を考えるのが好き<br>誠実さを軽視したくない</p><p><strong>向いていない人</strong></p><p>指示待ちで動きたい<br>すぐに答えが欲しい<br>判断の背景を考えるのが苦手</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'まとめ（社員視点）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの経営陣は、<br>声の大きさで引っ張るタイプではありません。</p><p>静かに、深く、<br>判断の質で組織を前に進める経営です。</p><p>この判断スタイルに共鳴できるかどうか。<br>それが、FLARETECHで働く上での<br>最も重要な相性だと、社員は感じています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'この経営陣と働くと、日常はどうなるか',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでの仕事は、<br>「言われたことを正しくこなす」日常ではありません。</p><p>毎日の業務の中で、<br>考え、選び、責任を引き受けることが前提になります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '朝｜タスクより先に「問い」が飛んでくる',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>出社して最初に向き合うのは、<br>ToDoリストではなく、問いです。</p><p>それは、誰のどんな課題を解こうとしているのか<br>今やるべき理由は、短期か、長期か<br>代替案は本当にないのか</p><p>「進めてください」よりも、<br>「どう考えている？」と聞かれる場面が多くあります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '会議｜結論よりも、前提が問われる',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>会議は静かです。<br>声の大きさや立場で空気が決まることはありません。</p><p>代わりに問われるのは、<br>その判断の前提が何か。</p><p>その数字はどこから来ているのか<br>その判断は誰にとって合理的か<br>5年後にも意味があるか</p><p>結論が鋭くても、前提が弱ければ止まります。<br>逆に、立場が若くても、構造が通っていれば通ります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '判断｜スピードより「長期合理性」',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>経営陣の判断は、派手ではありません。</p><p>感情や勢いで決まることは少なく、<br>次の3点が常に見られています。</p><p>長期的に意味があるか<br>再現できる構造か<br>誠実さを損なっていないか</p><p>そのため、<br>「今すぐ楽になる判断」は通りにくいですが、<br>後から振り返って納得できる判断が残ります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'トラブル時｜責められるのは人ではなく構造',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>うまくいかなかったとき、<br>最初に出てくる問いはこれです。</p><p>「どの設計が、そうさせた？」</p><p>個人の失敗を詰めるより、<br>仕組み・役割・判断フローが見直されます。</p><p>責任は問われますが、<br>切り捨てはされません。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '任され方｜裁量と責任はセットで来る',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、<br>任せられるスピードが早いです。</p><p>ただし、<br>裁量だけが渡されることはありません。</p><p>なぜそれを任せるのか<br>どこまでを自分で決めてよいか<br>どこから相談すべきか</p><p>この線引きは、対話で決まります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '評価｜派手な成果より、積み上げ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>評価は「一発の数字」では決まりません。</p><p>見られているのは、</p><p>学習しているか<br>再現できる形にしているか<br>周囲に良い影響を残しているか</p><p>静かに積み上げている人ほど、<br>ちゃんと見られています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '正直に言うと',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>楽ではありません。</p><p>考えなくていい環境を求める人、<br>指示がないと不安な人には、合わないと思います。</p><p>一方で、</p><p>考えることを仕事にしたい<br>長く通用する力を身につけたい<br>仕組みをつくる側に回りたい</p><p>そう考える人にとっては、<br>腰を据えて向き合える日常です。</p><p>FLARETECHの経営陣は、<br>正解を与える存在ではありません。</p><p>一緒に考え、決め、積み上げる存在です。</p><p>この日常を「しんどそう」と感じるか、<br>「面白そう」と感じるか。</p><p>その感覚こそが、<br>あなたにとっての判断材料だと思っています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '次の5年を、どこで積み上げるかを考える',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>※これは応募ではありません。価値観と前提をすり合わせるための対話です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/about/more"><strong>FLARETECHをもっと知るリンク集</strong></a><br>FLARETECHについてもっと知るための情報源を整理。公式発信、募集、文化、技術、実績など、今の自分に必要な情報だけを迷わず拾えるリンク集です。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// About More Page
export const aboutMorePageData = {
  title: 'FLARETECHをもっと知るリンク集',
  titleEn: 'More Information',
  subtitle: '',
  slug: '/about/more',
  category: 'about',
  site: 'career',
  metaTitle: 'FLARETECHをもっと知るリンク集 | FLARETECH採用サイト',
  metaDescription: 'FLARETECHについてもっと知るための情報源を整理。公式発信、募集、文化、技術、実績など、今の自分に必要な情報だけを迷わず拾えるリンク集です。',
  metaKeywords: [],
  metaUpdatedAt: '2026年1月2日',
  metaReadingTime: '約8分',
  showToc: true,
  tocLinks: [
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、外に向けた発信を「採用のための演出」ではなく、意思決定の材料を増やすための記録として捉えています。その分、ネット上の情報は多くなります。だからこそこのページでは、FLARETECHに興味を持っていただいた方が、"今の自分に必要な情報だけ"を迷わず拾えるように整理しました。気になるところから、短く拾い読みしてください。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'まずは温度感を掴む（公式／代表の発信）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '日々の取り組みやイベント、採用情報などFLARETECHの現在地を最速で追える公式note記事一覧です。エンジニアインタビューや社風・文化、技術記事など多様なコンテンツを発信しています。'
      }
    },
    {
      type: 'noteArticles',
      data: {
        articles: [
          {
            title: 'FLARETECH公式note',
            link: 'https://note.com/flaretech_it',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/230731905/4f1c517dd6f71f5ca316025923893ea6.png?width=800'
          },
          {
            title: '社員の本音インタビュー',
            link: 'https://note.com/flaretech_it/n/na6d46167f351',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/242293994/rectangle_large_type_2_30c1cbe41c9f43c3c788cb35e2fd7f4b.png?width=800'
          },
          {
            title: '創業5周年・社名変更',
            link: 'https://note.com/flaretech_it/n/na8028cc8ea1d',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/231851806/rectangle_large_type_2_47e3d83aecb6cde367ffb4ce009722ab.png?width=800'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '仕事の実態を掴む（募集・案件・働き方）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: `<p><strong>募集一覧（職種と期待値の入口）</strong></p><p>「何を担うのか」「どこまで裁量があるのか」を、最短で把握できます。まずは職種を眺め、気になるポジションがあれば深掘りしてください。</p><p><a href="${ALL_JOBS_URL}" target="_blank" rel="noopener noreferrer">募集一覧を見る</a></p><p><strong>カジュアル面談（応募ではなく、前提のすり合わせ）</strong></p><p>応募の前に、前提・期待値・成長の方向性をすり合わせる時間です。迷いがある人ほど先に使ってください。</p><p><a href="/jobs" target="_blank" rel="noopener noreferrer">カジュアル面談に申し込む</a></p>`,
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '文化と人を掴む（イベント・総会・制度）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '社員総会や社内イベント、文化・学びの場に関するnote記事一覧です。'
      }
    },
    {
      type: 'noteArticles',
      data: {
        articles: [
          {
            title: '社風・文化マガジン',
            link: 'https://note.com/flaretech_it/m/m8289899f595d',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/102826024/9c66bdc60ed153d7b7a9edab668193a6.png?width=800'
          }
        ]
      }
    },
    {
      type: 'workstyleArticles',
      data: {
        articles: [
          {
            title: '評価制度を見る',
            url: '/workstyle/evaluation'
          },
          {
            title: '育成制度を見る',
            url: '/workstyle/development'
          }
        ],
        description: '制度・評価・育成など、長く成長するための仕組みをまとめたページへのリンクです。'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '技術と学びを掴む（Tech / AI / 学習資産）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '現場で得た学びや技術観を発信するnote記事一覧です。'
      }
    },
    {
      type: 'noteArticles',
      data: {
        articles: [
          {
            title: 'アドベントカレンダー2025',
            link: 'https://note.com/flaretech_it/m/mb5bdc00f77ff',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/233129059/c18ca49a4825ecb619d9cfbcaa1a5409.png?width=800'
          },
          {
            title: 'エンジニアインタビュー',
            link: 'https://note.com/flaretech_it/m/m9338d3853802',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/98626792/0f55ab8c7930dcbfa38693ae0a4e779d.png?width=800'
          }
        ]
      }
    },
    {
      type: 'workstyleArticles',
      data: {
        articles: [
          {
            title: 'AI構築・AI開発',
            url: getCorporateUrl('/jp/service-details/7/')
          },
          {
            title: 'AI研修',
            url: getCorporateUrl('/jp/service-details/6/')
          },
          {
            title: '育成・学習制度を見る',
            url: '/workstyle/development'
          }
        ],
        description: 'AI前提の開発・育成や学習コンテンツ、育成制度などのリンク集です。'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '実績を掴む（プレス・受賞・提携）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: `<p>FLARETECHの実績は、外部から見える形でも積み上がっています。会社の成長の"事実"を確認したい方はこちらへ。</p><ul><li><a href="${getCorporateUrl('/jp/about/')}" target="_blank" rel="noopener noreferrer">会社概要・取引実績</a></li><li><a href="${getCorporateUrl('/jp/team/')}" target="_blank" rel="noopener noreferrer">メンバー紹介</a></li></ul>`,
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'さらに深く知りたい人へ（アーカイブ／連載）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: `<p><strong>連載・長文（思想の全体像に触れる）</strong></p><p>短い投稿では伝えきれない、会社の前提・価値観・変化の履歴。「この会社を選ぶ理由」を作りたい人向けの読み物です。</p><p><a href="https://note.com/flaretech_it" target="_blank" rel="noopener noreferrer">FLARETECH公式note全記事</a></p><p><strong>"もっとマニアックに"（原文・一次情報）</strong></p><p>編集された説明ではなく、一次情報に直接触れたい方へ。</p><p><a href="${getCorporateUrl('/jp/')}" target="_blank" rel="noopener noreferrer">FLARETECH公式サイト</a> / <a href="https://www.wantedly.com/companies/company_2519627/stories" target="_blank" rel="noopener noreferrer">Wantedly</a></p>`,
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '最後に',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>このリンク集は、応募を煽るためのページではありません。判断を急がせず、判断を正確にするためのページです。気になるものを見て、違うと思えばそれも大切な結論です。合いそうだと思ったなら、次は会話で確かめてください。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    }
  ],
  status: 'published'
};

// Function to seed all pages
export const seedAllPages = async (overwrite = false) => {
  console.log('Starting seed process...');

  try {
    // Seed all About pages
    console.log('Creating About Summary page...');
    const summaryId = await createPage(aboutSummaryPageData, overwrite);
    console.log('✅ Created About Summary page with ID:', summaryId);

    console.log('Creating About Business page...');
    const businessId = await createPage(aboutBusinessPageData, overwrite);
    console.log('✅ Created About Business page with ID:', businessId);

    console.log('Creating About Prerequisites page...');
    const prerequisitesId = await createPage(aboutPrerequisitesPageData, overwrite);
    console.log('✅ Created About Prerequisites page with ID:', prerequisitesId);

    console.log('Creating About AI page...');
    const aiId = await createPage(aboutAIPageData, overwrite);
    console.log('✅ Created About AI page with ID:', aiId);

    console.log('Creating About Story page...');
    const storyId = await createPage(aboutStoryPageData, overwrite);
    console.log('✅ Created About Story page with ID:', storyId);

    console.log('Creating About Future page...');
    const futureId = await createPage(aboutFuturePageData, overwrite);
    console.log('✅ Created About Future page with ID:', futureId);

    console.log('Creating About Experience Shift page...');
    const experienceShiftId = await createPage(aboutExperienceShiftPageData, overwrite);
    console.log('✅ Created About Experience Shift page with ID:', experienceShiftId);

    console.log('Creating About Leadership page...');
    const leadershipId = await createPage(aboutLeadershipPageData, overwrite);
    console.log('✅ Created About Leadership page with ID:', leadershipId);

    console.log('Creating About More page...');
    const moreId = await createPage(aboutMorePageData, overwrite);
    console.log('✅ Created About More page with ID:', moreId);

    // Seed all Culture pages
    console.log('Creating Culture Vision page...');
    const visionId = await createPage(cultureVisionPageData, overwrite);
    console.log('✅ Created Culture Vision page with ID:', visionId);

    console.log('Creating Culture Values page...');
    const valuesId = await createPage(cultureValuesPageData, overwrite);
    console.log('✅ Created Culture Values page with ID:', valuesId);

    console.log('Creating Culture FLARETECHのプリンシプル page...');
    const principlesId = await createPage(culturePrinciplesPageData, overwrite);
    console.log('✅ Created Culture FLARETECHのプリンシプル page with ID:', principlesId);

    console.log('Creating Culture Keywords page...');
    const keywordsId = await createPage(cultureKeywordsPageData, overwrite);
    console.log('✅ Created Culture Keywords page with ID:', keywordsId);

    console.log('Creating Culture Knowledge page...');
    const knowledgeId = await createPage(cultureKnowledgePageData, overwrite);
    console.log('✅ Created Culture Knowledge page with ID:', knowledgeId);

    // Seed all Job pages
    console.log('Creating Job Development Engineer page...');
    const jobDevelopmentEngineerId = await createPage(jobDevelopmentEngineerPageData, overwrite);
    console.log('✅ Created Job Development Engineer page with ID:', jobDevelopmentEngineerId);

    console.log('Creating Job Infrastructure Engineer page...');
    const jobInfrastructureEngineerId = await createPage(jobInfrastructureEngineerPageData, overwrite);
    console.log('✅ Created Job Infrastructure Engineer page with ID:', jobInfrastructureEngineerId);

    console.log('Creating Job DX Engineer page...');
    const jobDXEngineerId = await createPage(jobDXEngineerPageData, overwrite);
    console.log('✅ Created Job DX Engineer page with ID:', jobDXEngineerId);

    console.log('Creating Job Sales page...');
    const jobSalesId = await createPage(jobSalesPageData, overwrite);
    console.log('✅ Created Job Sales page with ID:', jobSalesId);

    // Seed all Workstyle pages
    console.log('Creating Workstyle Survey page...');
    const workstyleSurveyId = await createPage(workstyleSurveyPageData, overwrite);
    console.log('✅ Created Workstyle Survey page with ID:', workstyleSurveyId);

    console.log('Creating Workstyle PX page...');
    const workstylePxId = await createPage(workstylePxPageData, overwrite);
    console.log('✅ Created Workstyle PX page with ID:', workstylePxId);

    console.log('Creating Workstyle Onboarding page...');
    const workstyleOnboardingId = await createPage(workstyleOnboardingPageData, overwrite);
    console.log('✅ Created Workstyle Onboarding page with ID:', workstyleOnboardingId);

    console.log('Creating Workstyle Development page...');
    const workstyleDevelopmentId = await createPage(workstyleDevelopmentPageData, overwrite);
    console.log('✅ Created Workstyle Development page with ID:', workstyleDevelopmentId);

    console.log('Creating Workstyle Evaluation page...');
    const workstyleEvaluationId = await createPage(workstyleEvaluationPageData, overwrite);
    console.log('✅ Created Workstyle Evaluation page with ID:', workstyleEvaluationId);

    console.log('Creating Workstyle Career Path page...');
    const workstyleCareerPathId = await createPage(workstyleCareerPathPageData, overwrite);
    console.log('✅ Created Workstyle Career Path page with ID:', workstyleCareerPathId);

    console.log('Creating Workstyle Benefits page...');
    const workstyleBenefitsId = await createPage(workstyleBenefitsPageData, overwrite);
    console.log('✅ Created Workstyle Benefits page with ID:', workstyleBenefitsId);

    console.log('Creating Workstyle Systems page...');
    const workstyleSystemsId = await createPage(workstyleSystemsPageData, overwrite);
    console.log('✅ Created Workstyle Systems page with ID:', workstyleSystemsId);

    // Seed all New Graduate pages
    console.log('Creating New Graduate About Summary page...');
    const newGraduateAboutSummaryId = await createPage(newGraduateAboutSummaryPageData, overwrite);
    console.log('✅ Created New Graduate About Summary page with ID:', newGraduateAboutSummaryId);

    console.log('Creating New Graduate Process Hiring page...');
    const newGraduateProcessHiringId = await createPage(newGraduateProcessHiringPageData, overwrite);
    console.log('✅ Created New Graduate Process Hiring page with ID:', newGraduateProcessHiringId);

    console.log('Creating New Graduate Internship page...');
    const newGraduateInternshipId = await createPage(newGraduateInternshipPageData, overwrite);
    console.log('✅ Created New Graduate Internship page with ID:', newGraduateInternshipId);

    // Seed all Process pages
    console.log('Creating Process page...');
    const processProcessId = await createPage(processProcessPageData, overwrite);
    console.log('✅ Created Process page with ID:', processProcessId);

    console.log('Creating Process Reentry page...');
    const processReentryId = await createPage(processReentryPageData, overwrite);
    console.log('✅ Created Process Reentry page with ID:', processReentryId);

    console.log('Creating Process Alumni page...');
    const processAlumniId = await createPage(processAlumniPageData, overwrite);
    console.log('✅ Created Process Alumni page with ID:', processAlumniId);

    console.log('Creating Process FAQ page...');
    const processFaqId = await createPage(processFaqPageData, overwrite);
    console.log('✅ Created Process FAQ page with ID:', processFaqId);

    console.log('✅ Seed process completed successfully!');
    return {
      success: true,
      message: `33ページすべてを作成しました (9 About + 5 Culture + 4 Job + 8 Workstyle + 3 New Graduate + 4 Process)`
    };
  } catch (error) {
    console.error('❌ Seed process failed:', error);
    console.error('Error details:', error.message, error.stack);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
};

// ============================================
// CULTURE PAGES
// ============================================

// Culture Vision & Mission Page
export const cultureVisionPageData = {
  title: 'Purpose / Mission / Vision',
  titleEn: 'Vision & Mission',
  subtitle: 'パーパス・ミッション・ビジョン',
  slug: '/culture/vision-mission',
  category: 'culture',
  site: 'career',
  metaTitle: 'Purpose / Mission / Vision | FLARETECH採用サイト',
  metaDescription: 'FLARETECHのPurpose（存在意義）、Mission（使命）、Vision（未来像）について詳しくご紹介します。',
  metaKeywords: ['パーパス', 'ミッション', 'ビジョン', '企業理念', 'フレアテック'],
  showToc: true,
  tocLinks: [
    { title: 'Purpose / Mission / Vision', path: '/culture/vision-mission' },
    { title: 'FLARETECHのバリュー（行動指針）', path: '/culture/values' },
    { title: 'FLARETECHのプリンシプル（行動原理）', path: '/culture/principles' },
    { title: 'FLARETECHの社内用語', path: '/culture/keywords' },
    { title: 'FLARETECHのナレッジ', path: '/culture/knowledge' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'Purpose / Mission / Vision とは',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>Purpose（パーパス）、Mission（ミッション）、Vision（ビジョン）は、企業の存在意義と目指す未来を示す重要な概念です。</p><p><strong>Purpose（パーパス）</strong>は「なぜ存在するのか（Why）」を示す、企業の存在意義です。時代や環境が変わっても変わらない、企業の根幹となる理念を表します。</p><p><strong>Mission（ミッション）</strong>は「何を成し遂げるのか（What）」を示す、企業の使命です。Purposeを実現するために、具体的に何に取り組むかを明確にします。</p><p><strong>Vision（ビジョン）</strong>は「どこを目指すのか（Where）」を示す、企業の未来像です。中長期的に達成したい姿や、社会における理想的なポジションを描きます。</p><p>この3つは階層的な関係にあり、Purposeを基盤に、Missionが実行され、Visionが実現されます。FLARETECHでは、これらを明確に定義することで、組織全体が同じ方向を向き、一貫性のある意思決定ができるようにしています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'Purpose（パーパス）− 私たちの存在意義',
        alignment: 'left'
      }
    },
    {
      type: 'statsGrid',
      data: {
        columns: 1,
        stats: [
          {
            label: 'PURPOSE',
            value: 'すべての挑戦に、可能性がひらく社会を'
          }
        ]
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのPurpose（存在意義）は、「すべての挑戦に、可能性がひらく社会を」です。</p><p>この言葉には、「挑戦したい人が、自由に挑戦できる社会をつくりたい」という強い想いが込められています。年齢や性別、学歴や経験に関係なく、誰もが自分の可能性を信じて新しいことに挑戦できる。そんな社会の実現を、私たちは目指しています。</p><p>私たちは、SES事業を通じて、エンジニアが自分の強みを活かし、スキルを磨き、キャリアを積み重ねられる環境を提供しています。それは単なる「人材派遣」ではなく、一人ひとりの可能性を最大化し、挑戦を後押しする仕組みです。</p><p>FLARETECHが存在する理由。それは、「すべての人に、挑戦する権利がある」という信念のもと、その挑戦を支え、可能性をひらく社会をつくることです。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/vision/vision-1.jpg',
        alt: '挑戦を支える社会'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'Mission（ミッション）− 私たちの使命',
        alignment: 'left'
      }
    },
    {
      type: 'statsGrid',
      data: {
        columns: 1,
        stats: [
          {
            label: 'MISSION',
            value: '誰もが輝く社会を創る'
          }
        ]
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのMission（使命）は、「誰もが輝く社会を創る」です。</p><p>これは、Purposeである「すべての挑戦に、可能性がひらく社会を」を実現するために、私たちが果たすべき具体的な使命を示しています。</p><p>「誰もが輝く」とは、一人ひとりが自分の強みを活かし、成長し、価値を発揮できる状態を指します。そのために、私たちは以下のことに取り組んでいます。</p><ul><li><strong>個の尊重:</strong> 一人ひとりの個性や価値観を尊重し、それぞれに合ったキャリアパスを提供します。</li><li><strong>成長支援:</strong> スキルアップやキャリアアップのための研修制度や、メンター制度を整備しています。</li><li><strong>挑戦の機会:</strong> 新しい技術や分野への挑戦を積極的に支援し、失敗を恐れず挑戦できる文化を醸成しています。</li></ul><p>私たちの使命は、単に「エンジニアを派遣する」ことではありません。一人ひとりが自分の可能性を信じ、挑戦し、成長できる環境を整え、誰もが輝く社会を実現することです。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/vision/vision-2.jpg',
        alt: '誰もが輝く環境'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'Vision（ビジョン）− 私たちが目指す未来',
        alignment: 'left'
      }
    },
    {
      type: 'statsGrid',
      data: {
        columns: 1,
        stats: [
          {
            label: 'VISION',
            value: '価値の創造、永続的成長、個の尊重'
          }
        ]
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのVision（未来像）は、「価値の創造、永続的成長、個の尊重」という3つの柱で構成されています。</p><p>これらは、私たちが中長期的に達成したい姿であり、社会における理想的なポジションを示しています。</p><h3>価値の創造</h3><p>私たちは、単に技術を提供するだけでなく、クライアントのビジネス課題を深く理解し、本質的な価値を創造します。エンジニア一人ひとりが、「自分の仕事がどのような価値を生み出しているか」を理解し、誇りを持って働ける環境を目指します。</p><h3>永続的成長</h3><p>私たちは、短期的な利益ではなく、長期的な成長を重視します。そのために、社員のスキルアップやキャリア開発に投資し、組織全体が持続的に成長できる仕組みを構築します。また、社会の変化に柔軟に対応し、常に進化し続ける組織を目指します。</p><h3>個の尊重</h3><p>私たちは、一人ひとりの個性や価値観を尊重し、多様性を力に変えます。年齢や性別、学歴や経験に関係なく、誰もが自分の強みを活かし、挑戦できる環境を提供します。そして、社員一人ひとりが「自分らしく働ける」と感じられる組織を目指します。</p><p>この3つの柱を実現することで、FLARETECHは「誰もが輝く社会を創る」というMissionを果たし、「すべての挑戦に、可能性がひらく社会を」というPurposeを実現します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/vision/vision-3.jpg',
        alt: '永続的成長と個の尊重'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'FLARETECHにおけるこれらの言葉の使い方',
        alignment: 'left'
      }
    },
    {
      type: 'statsGrid',
      data: {
        columns: 1,
        stats: [
          {
            label: 'MISSION',
            value: '誰もが輝く社会を創る'
          }
        ]
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、Purpose・Mission・Visionを、日々の業務の中で意識的に活用しています。</p><ul><li><strong>意思決定の基準:</strong> 重要な意思決定を行う際、「この決定は、私たちのPurpose・Mission・Visionに沿っているか？」を自問します。</li><li><strong>目標設定:</strong> 個人やチームの目標を設定する際、Purpose・Mission・Visionとの整合性を確認します。</li><li><strong>採用活動:</strong> 新しいメンバーを迎える際、Purpose・Mission・Visionへの共感を重視します。</li><li><strong>評価制度:</strong> 社員の評価において、Purpose・Mission・Visionに沿った行動を評価します。</li></ul><p>これらの言葉は、単なるスローガンではなく、FLARETECHの文化や行動の基盤となっています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '候補者のみなさまへ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、「すべての挑戦に、可能性がひらく社会を」というPurposeのもと、「誰もが輝く社会を創る」というMissionを掲げています。</p><p>もしあなたが、「挑戦したい」「成長したい」「自分の可能性を試したい」と思っているなら、FLARETECHはあなたにとって最適な環境かもしれません。</p><p>私たちは、一人ひとりの個性や価値観を尊重し、それぞれに合ったキャリアパスを提供します。年齢や経験は関係ありません。大切なのは、「挑戦したい」という気持ちと、「成長したい」という意欲です。</p><p>FLARETECHで、あなたの可能性をひらいてみませんか？</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/vision/vision-4.jpg',
        alt: '一緒に可能性をひらく'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社員から見たPurpose / Mission / Vision',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのPurpose・Mission・Visionは、会社が定義して掲げているものですが、社員一人ひとりの解釈が加わり、日々の仕事の中で実践することによって、完成すると考えています。</p><p>ここでは、実際にFLARETECHで働く社員が、Purpose・Mission・Visionをどのように捉え、どのように実践しているかをご紹介します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '「すべての挑戦に、可能性がひらく社会を」というPurposeは、私自身の経験と深く結びついています。私は異業種からITエンジニアに転職しましたが、FLARETECHでは年齢や経験に関係なく、挑戦する機会を与えてもらえました。このPurposeがあるからこそ、私も後輩たちに「挑戦してみよう」と背中を押せるんです。',
        author: '中釜 直樹',
        role: 'エンジニア'
      }
    },
    {
      type: 'quote',
      data: {
        text: '「誰もが輝く社会を創る」というMissionは、単なる理想ではなく、日々の業務の中で実感しています。FLARETECHでは、一人ひとりの強みを活かし、成長をサポートする文化があります。私自身、苦手だった分野に挑戦する機会をもらい、それが今では強みになっています。このMissionがあるからこそ、私も周りのメンバーの成長を支援したいと思えます。',
        author: '高木 界',
        role: 'エンジニア'
      }
    },
    {
      type: 'quote',
      data: {
        text: '「価値の創造、永続的成長、個の尊重」というVisionは、FLARETECHの文化そのものだと思います。特に「個の尊重」は、私がFLARETECHを選んだ理由の一つです。ここでは、一人ひとりの価値観やキャリアプランが尊重され、それに合ったサポートが受けられます。だからこそ、長く働き続けたいと思える会社なんです。',
        author: '佐藤 真由',
        role: 'エンジニア'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/culture/values"><strong>FLARETECHのバリュー（行動指針）</strong></a><br>FLARETECHのバリュー「挑戦・誠実・共創」について詳しくご紹介します。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// Culture Values Page
export const cultureValuesPageData = {
  title: 'Our Values',
  titleEn: 'Our Values',
  subtitle: 'FLARETECHのバリュー（行動指針）',
  slug: '/culture/values',
  category: 'culture',
  site: 'career',
  metaTitle: 'Our Values - FLARETECHのバリュー | FLARETECH採用サイト',
  metaDescription: 'FLARETECHのバリュー「挑戦・誠実・共創」について詳しくご紹介します。Purpose / Mission / Visionを日々の行動で体現するための行動指針です。',
  metaKeywords: ['バリュー', '行動指針', '挑戦', '誠実', '共創', 'フレアテック'],
  showToc: true,
  tocLinks: [
    { title: 'Purpose / Mission / Vision', path: '/culture/vision-mission' },
    { title: 'FLARETECHのバリュー（行動指針）', path: '/culture/values' },
    { title: 'FLARETECHのプリンシプル（行動原理）', path: '/culture/principles' },
    { title: 'FLARETECHの社内用語', path: '/culture/keywords' },
    { title: 'FLARETECHのナレッジ', path: '/culture/knowledge' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのバリューは、「挑戦・誠実・共創」。<br>これはスローガンではなく、<strong>Purpose / Mission / Vision を、日々どう行動で体現するか（How）</strong>を示す行動指針です。</p><p>環境が変わっても、技術が変わっても、組織が拡大しても。<br>私たちはこの3つを"共通言語"として、意思決定と行動の品質を揃えます。<br>「自分はこのカルチャーに合うか？」を確かめるためにも、ぜひ読んでください。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'バリューが果たす役割',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、スピードと変化の中で成長しています。<br>この環境では、ルールや手順だけでは揃いません。必要なのは、現場で迷ったときに立ち戻れる <strong>判断基準</strong> です。</p><p>バリューは、次のためにあります。</p><ul><li><strong>判断を速くする：</strong>迷いを減らし、前に進む</li><li><strong>品質を揃える：</strong>成果のばらつきを小さくする</li><li><strong>信頼を積む：</strong>顧客・仲間・自分への約束を守る</li><li><strong>"らしさ"を再現する：</strong>誰がやってもFLARETECHになる</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '1｜挑戦：現状に満足せず、一歩前へ進む',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの成長は、メンバー一人ひとりの挑戦でできています。<br>ここで言う挑戦は、派手な一発勝負ではありません。<br><strong>「今より一歩前へ進む」</strong>という、積み重ねの姿勢です。</p><h3>挑戦とは（定義）</h3><ul><li>現状に慣れない</li><li>失敗を避けるのではなく、学びに変える</li><li>"伸びる方向"に、自分の時間を投資する</li></ul><h3>Do（こう行動する）</h3><ul><li>新しい技術・領域を学び、次のステップへ進む</li><li>クライアントの期待値を"超える設計"を考える</li><li>手を挙げる／提案する／試してみる（小さく速く）</li></ul><h3>Don\'t（これは避ける）</h3><ul><li>「いつかやる」で先送りする</li><li>変化を恐れて"慣れた勝ち方"に閉じる</li><li>失敗を隠して、学びを残さない</li></ul><h3>挑戦が生むもの</h3><p>挑戦は、会社の競争力を高めるだけでなく、あなた自身の市場価値を上げます。<br>FLARETECHは、挑戦が"報われる形"を、制度と育成で支え続けます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/values/values-1.jpg',
        alt: '挑戦する姿勢'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '2｜誠実：信頼を築くための基盤',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの強みは、技術力だけではありません。<br>私たちは、長期で選ばれる会社は <strong>信頼で決まる</strong> と考えています。<br>誠実さは優しさではなく、相手の信頼に応えるための技術です。</p><h3>誠実とは（定義）</h3><ul><li>事実を曲げない</li><li>期待値を調整する（できないことを言わない）</li><li>相手の時間と成果に責任を持つ</li></ul><h3>Do（こう行動する）</h3><ul><li><strong>クライアントに誠実：</strong>課題を正確に捉え、最適解を提案する</li><li><strong>チームに誠実：</strong>リスペクトを前提に、オープンに議論する</li><li><strong>自分に誠実：</strong>成長課題から逃げず、学び続ける</li><li>悪い情報ほど早く共有し、打ち手まで出す</li></ul><h3>Don\'t（これは避ける）</h3><ul><li>その場を丸くするための曖昧な返事</li><li>問題が起きてからの報告</li><li>"忙しい"を免罪符にして品質を落とす</li></ul><h3>誠実が生むもの</h3><p>短期の得より、長期の信頼。<br>この積み重ねが、顧客との関係性を強くし、仕事の幅を広げ、結果として会社も個人も伸ばします。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/values/values-2.jpg',
        alt: '誠実に向き合う'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '3｜共創：チームとクライアントと価値を生み出す',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、個の力を否定しません。<br>ただし、個の力だけでは、事業も組織もスケールしない。<br>私たちは <strong>「個の力 × チームの力」</strong> を最大化して、より大きな成果を取りにいきます。</p><h3>共創とは（定義）</h3><ul><li>違いを摩擦で終わらせず、価値に変える</li><li>依頼ではなく、共同編集で進める</li><li>受発注ではなく、パートナーとして並走する</li></ul><h3>Do（こう行動する）</h3><ul><li><strong>エンジニア同士の共創：</strong>学び合い、知見を共有する</li><li><strong>クライアントとの共創：</strong>「共に事業を伸ばす前提」で提案する</li><li><strong>組織の共創：</strong>「どうすればもっと良くなるか」を言語化し、改善する</li><li>情報を開き、意思決定の速度を上げる</li></ul><h3>Don\'t（これは避ける）</h3><ul><li>「それは自分の仕事じゃない」で止める</li><li>部門最適で勝って、全体最適で負ける</li><li>情報を抱え、属人化する</li></ul><h3>共創が生むもの</h3><p>共創は、仲の良さではありません。<br>成果が出るチーム状態をつくるための技術です。<br>共創できる組織は、学習が速く、変化に強く、長期で勝てます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/values/values-3.jpg',
        alt: '共創で価値を生む'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'このバリューは、日常でこう使われています',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのバリュー「挑戦・誠実・共創」は、<br>ポスターの中やスライドの中だけにある言葉ではありません。</p><p>私たちは、この3つを<br>日々の判断・会話・行動の基準として使っています。</p><h3>日常での使われ方（一例）</h3><p><strong>1on1・評価面談</strong></p><ul><li>「それは"挑戦"だったか？」</li><li>「誠実に向き合えていたか？」</li><li>「共創できていたか？」</li></ul><p><strong>プロジェクトの振り返り</strong></p><ul><li>成果だけでなく、どう進めたかを言語化する</li><li>個人プレーで終わっていないかを確認する</li></ul><p><strong>Slackや日常会話</strong></p><ul><li>「今のは誠実だったと思う」</li><li>「もう一歩、挑戦できそう」</li><li>「これ、一緒に考えよう（共創しよう）」</li></ul><p>バリューは、誰かを縛るためのルールではありません。<br>迷ったときに立ち戻るための、共通言語です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'このバリューが合う人・合わない人',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのバリューは、全員にフィットするものではありません。<br>だからこそ、先に言葉にしています。</p><h3>合う人</h3><ul><li>失敗を恐れず、一歩前に進もうとする人</li><li>正解よりも、誠実なプロセスを大切にしたい人</li><li>一人で勝つより、チームで価値を出したい人</li></ul><h3>合わない人</h3><ul><li>変化よりも安定を最優先したい人</li><li>指示されたことだけを正確にこなしたい人</li><li>他者との摩擦や議論を避けたい人</li></ul><p>どちらが良い・悪いではありません。<br>ただ、FLARETECHは前者を選び続ける会社です。</p><p>バリューは、<br><strong>「守るもの」ではなく「使うもの」</strong>です。</p><p>挑戦し、誠実に向き合い、共に創る。<br>この3つを、自分の言葉と行動で使いこなしたいと思えたなら、<br>FLARETECHでの日々は、きっとあなたを前に進めます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社員から見たFLARETECHのバリュー',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのバリューは<br>「挑戦・誠実・共創」。</p><p>これは理想論ではなく、<br>日々の仕事の中で <strong>迷ったときに立ち返る"判断軸"</strong> として機能しています。</p><p>ここでは、立場の異なる3名の社員が、<br>実体験として感じているバリューを語ります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: 'FLARETECHの「挑戦」は、成功したかどうかだけで評価されるものではありません。<br><br>営業として、まだ前例のない提案や、確度が高いとは言えない打ち手に踏み出す場面も多くあります。<br>そんなときでも「なぜそれをやろうとしたのか」「どこに価値を感じたのか」をきちんと聞いてもらえる。<br><br>失敗しても、挑戦した判断そのものが否定されない。<br>だからこそ、守りに入らず、前に出続けられます。<br><br>挑戦が"自己責任"で終わらない会社だと感じています。',
        author: '中釜 直樹',
        role: '営業部 マネージャー | 挑戦｜「今より一歩前に出る」ことを、ちゃんと見てくれる'
      }
    },
    {
      type: 'quote',
      data: {
        text: 'FLARETECHでいう誠実さは、単に正直であることではありません。<br><br>分からないことを分からないと言う。<br>無理な要望には、理由を添えて断る。<br>できることと、できないことを曖昧にしない。<br><br>技術的な判断でも、短期的に楽な道より、長期的に信頼が積み上がる選択を取るのが当たり前です。<br><br>クライアントに対しても、社内に対しても、同じ基準で向き合う。<br>この誠実さがあるから、エンジニアとしても、会社としても、無理のない成長ができていると感じます。',
        author: '高木 界',
        role: '取締役 / 技術責任者 | 誠実｜短期よりも「信頼が残る選択」をする文化'
      }
    },
    {
      type: 'quote',
      data: {
        text: 'FLARETECHでは、成果は「個人」ではなく「チーム」でつくるもの、という前提があります。<br><br>誰か一人が突出して頑張るのではなく、役割を越えて意見を出す、情報をオープンに共有する、困っている人に自然と手が伸びる。<br>この状態を"理想"ではなく、組織として当たり前の状態にしたいと思ってきました。<br><br>共創とは、仲良くすることではありません。<br>違いを出し合い、ぶつかり、より良い選択を一緒につくること。<br><br>一人で戦わせない。<br>それがFLARETECHの共創です。',
        author: '河畑 福太郎',
        role: '代表取締役 | 共創｜「一人で抱え込まない」が前提のチーム'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>バリューは「評価される言葉」ではなく「使われる言葉」</h3><p>FLARETECHのバリューは、</p><ul><li>迷ったとき、どう判断するか</li><li>行動を選ぶとき、何を優先するか</li><li>仲間と向き合うとき、どんな姿勢を取るか</li></ul><p>を決めるための <strong>共通言語</strong> です。</p><p>もしあなたが、</p><ul><li>挑戦することを肯定されたい</li><li>誠実に仕事と向き合いたい</li><li>仲間と一緒に価値をつくりたい</li></ul><p>そう思うなら、<br>このバリューは、きっと日常の行動と重なります。</p><p>この価値観が、仕事の中でどう表れるのか。<br>FLARETECHでの"日常"をのぞいてみてください。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/values/values-4.jpg',
        alt: 'バリューを体現する'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/culture/principles"><strong>FLARETECHのプリンシプル（行動原理）</strong></a><br>FLARETECHで働く一人ひとりが、日々の判断・行動に迷ったときに立ち返る"行動の基準"。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// Culture FLARETECHのプリンシプル（行動原理） Page
export const culturePrinciplesPageData = {
  title: 'FLARETECHのプリンシプル（行動原理）',
  titleEn: 'Principles',
  subtitle: 'FLARETECHが大切にする 7つの行動原理',
  slug: '/culture/principles',
  category: 'culture',
  site: 'career',
  metaTitle: 'FLARETECHのプリンシプル（行動原理） - 7つの行動原理 | FLARETECH採用サイト',
  metaDescription: 'FLARETECHで働く一人ひとりが、日々の判断・行動に迷ったときに立ち返る"行動の基準"。7つの行動原理について詳しくご紹介します。',
  metaKeywords: ['行動原理', 'FLARETECHのプリンシプル', '行動基準', 'フレアテック', '企業文化'],
  showToc: true,
  tocLinks: [
    { title: 'Purpose / Mission / Vision', path: '/culture/vision-mission' },
    { title: 'FLARETECHのバリュー（行動指針）', path: '/culture/values' },
    { title: 'FLARETECHのプリンシプル（行動原理）', path: '/culture/principles' },
    { title: 'FLARETECHの社内用語', path: '/culture/keywords' },
    { title: 'FLARETECHのナレッジ', path: '/culture/knowledge' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'はじまりに｜価値観だけでは、文化は続かない',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>創業期、夜遅くまで議論し続けていた夜がある。<br>案件も、お金も、経験もなかった。<br>それでも、「誰もが輝く社会を創りたい」という想いだけは、誰よりも強かった。</p><p>あのときの熱と必死さが、FLARETECHの始まりでした。</p><p>創業当初から、私たちが大切にしてきた価値観があります。<br>それが <strong>「挑戦・誠実・共創」</strong> です。</p><p>この3つのバリューは、間違いなくFLARETECHの文化の土台をつくってきました。<br>しかし、組織が200名を超えた今、はっきりと感じていることがあります。</p><p><strong>価値観だけでは、行動が揃わない。<br>行動が揃わなければ、文化は続かない。</strong></p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'なぜ「行動原理」が必要なのか',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>どれだけ美しい理念を掲げても、<br>どれだけ正しいバリューを語っても、<br>日々の行動が人によってバラバラであれば、文化は必ず薄まります。</p><p>組織は、放っておくと楽な方向に流れます。<br>挑戦より安定。<br>誠実より妥協。<br>共創より個プレー。</p><p>だからこそ私たちは、<br>「どう考え、どう動くか」を、行動レベルで言語化する必要がありました。</p><p>3つのバリューが文化の心臓だとすれば、<br>7つの行動原理は、文化を動かす筋肉と神経です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'FLARETECHのプリンシプルとは',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのプリンシプルとは、<br><strong>FLARETECHで働く一人ひとりが、日々の判断・行動に迷ったときに立ち返る"行動の基準"</strong>です。</p><p>これは制度のための言葉ではありません。<br>評価のためだけのチェックリストでもありません。</p><p>私自身が、経営の意思決定で、<br>現場で、仲間との対話の中で、<br><strong>実際に背負ってきた「軸」</strong>を、あらためて言語化したものです。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/principles/principles-1.jpg',
        alt: 'プリンシプルを実践する'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'FLARETECHが大切にする<br>「7つの行動原理」',
        alignment: 'left',
        variant: 'primary'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '1. 挑戦を続け、進化を重ねろ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>挑戦は派手ではない。<br>ほとんどが、地味で、泥臭くて、誰にも気づかれない積み重ねだ。</p><p>それでも続ける人だけが強くなる。<br>続けられる人だけが、未来を見に行ける。</p><p>挑戦は勇気、進化は習慣。<br>この両方を積み上げた人が、未来を変える。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '挑戦って"新しいことをやる"より、"昨日の自分を超えるか"だと思っています。FLARETECHでは、完璧じゃなくても一歩踏み出した人が評価される。だから、挑戦が特別じゃなく"日常"になります。',
        author: '高木 界',
        role: '取締役／エンジニア'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '2. 誠実を貫き、信頼を勝ち取れ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>誠実とは、簡単なときに選ぶものではない。<br>難しい決断ほど、「正しい側」に立てるかどうかだ。</p><p>隠さない。<br>誤魔化さない。<br>約束を守る。<br>守れないときは、逃げずに伝える。</p><p>小さな誠実の積み重ねが、<br>やがて大きな信頼になる。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '都合の悪いことも、ちゃんと伝える文化があります。短期的には損に見えても、結果的に"任せ続けてもらえる"。営業として一番の武器は、誠実さだと実感しています。',
        author: '中釜 直樹',
        role: '営業マネージャー'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '3. 共に挑み、響きを生み出せ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>強いチームには「響き」がある。<br>誰かの挑戦が、別の誰かの挑戦に火をつける。</p><p>それは、協力でも、単なる共有でもない。<br>共鳴というエネルギーだ。</p><p>ひとりの行動が、<br>チーム全体の挑戦を加速させる。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '誰かが本気で挑戦すると、周りも自然と動き出す。"手伝う"じゃなく、"一緒に背負う"感覚があるんです。この空気感が、FLARETECHの強さだと思います。',
        author: '冨山 大貴',
        role: '採用部 部長'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '4. なぜを問い続け、掴んだ本質で動け',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>考えすぎて動けないのも違う。<br>考えずに動くのも危険だ。</p><p>大切なのは、<br>「なぜ？」 → 本質 → 行動<br>この順番を崩さないこと。</p><p>問いの深さ × 行動の速さ。<br>この掛け算が、挑戦の質を決める。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '『それ、なぜやるんだっけ？』が普通に飛び交います。最初はドキッとしましたが、責める問いじゃない。本質に立ち返るための問いだと分かりました。',
        author: '渡邉さくら',
        role: '広報'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/principles/principles-2.jpg',
        alt: '本質を問い続ける'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '5. 責任を果たし、成果で応えろ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>責任とは、押しつけられるものではない。<br>「自分が引き受ける」と決めた瞬間に、覚悟が生まれる。</p><p>覚悟があるから折れない。<br>折れないから成果が出る。</p><p>成果とは、数字だけではない。<br><strong>「任せてよかった」</strong>と言われることだ。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '裁量が大きい分、"自分が引き受ける"覚悟を求められます。でも、その分成果が出たときの納得感が大きい。数字以上に、"信頼された"実感が残ります。',
        author: '福士 時崇',
        role: '営業チームリーダー'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '6. まず動け、すぐ学べ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>10回の思考より、1回の行動。</p><p>行動 → 学習 → 改善 → 再挑戦。<br>この循環の速さが、成長の速さになる。</p><p>迷ったら、まず動けばいい。<br>動いた人にしか、次の景色は見えない。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '正解を待つより、まずやってみる。失敗しても"じゃあ次どうする？"で終わる。このスピード感が、成長を加速させてくれます。',
        author: '森戸 有輝',
        role: '営業'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '7. 互いを尊び、感謝を忘れるな',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>挑戦の裏側には、必ず誰かの支えがある。<br>気遣い、助言、フォロー、見えない努力。</p><p>尊敬は信頼を生み、感謝は挑戦を続ける力を生む。</p><p>強い組織は、強さと同じくらい、あたたかさを持っている。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '『ありがとう』をちゃんと口にする人が多いです。役割に関係なく、支え合っている実感がある。だから安心して挑戦できるんだと思います。',
        author: '小池 織姫',
        role: 'セールスコーディネーター'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '行動こそが、文化をつくる',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>行動原理は、読んだ瞬間に価値が生まれるわけではありません。<br>行動したときにだけ、意味を持ちます。</p><p>だから、今日ひとつだけでいい。</p><p>誰か一人に、「ありがとう」を伝えてみてください。</p><p>文化は、こうした小さな行動から動き出します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/principles/principles-3.jpg',
        alt: '挑戦し続ける'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '最後に',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>挑戦・誠実・共創という3つのバリューが、FLARETECHの文化の骨格をつくりました。</p><p>そして今、7つの行動原理が、その文化を行動レベルで揺るぎないものにします。</p><p>規模が変わっても、事業が増えても、挑戦する組織であり続けるために。</p><p>私は、この7つの行動原理を背負い、もう一段、強い経営をします。</p><p><strong>あなたは、明日どの行動原理を選びますか？</strong></p><p>あなたの一歩が、FLARETECHの未来をつくる。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/culture/keywords"><strong>FLARETECHの社内用語</strong></a><br>FLARETECHには、仕事の判断に迷ったときに立ち返る「言葉」があります。現場で使われ、意思決定を前に進めてきた共通言語をご紹介します。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// Culture Keywords Page
export const cultureKeywordsPageData = {
  title: 'Culture Words',
  titleEn: 'Culture Words',
  subtitle: 'FLARETECHの社内用語',
  slug: '/culture/keywords',
  category: 'culture',
  site: 'career',
  metaTitle: 'Culture Words - FLARETECHの社内用語 | FLARETECH採用サイト',
  metaDescription: 'FLARETECHには、仕事の判断に迷ったときに立ち返る「言葉」があります。現場で使われ、意思決定を前に進めてきた共通言語をご紹介します。',
  metaKeywords: ['社内用語', '共通言語', '企業文化', 'フレアテック', 'North Star'],
  showToc: true,
  tocLinks: [
    { title: 'Purpose / Mission / Vision', path: '/culture/vision-mission' },
    { title: 'FLARETECHのバリュー（行動指針）', path: '/culture/values' },
    { title: 'FLARETECHのプリンシプル（行動原理）', path: '/culture/principles' },
    { title: 'FLARETECHの社内用語', path: '/culture/keywords' },
    { title: 'FLARETECHのナレッジ', path: '/culture/knowledge' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'quote',
      data: {
        text: '裁量が大きい分、"自分が引き受ける"覚悟を求められます。でも、その分成果が出たときの納得感が大きい。数字以上に、"信頼された"実感が残ります。',
        author: '福士 時崇',
        role: '営業チームリーダー'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHには、仕事の判断に迷ったときに立ち返る「言葉」があります。</p><p>それはスローガンではなく、<br><strong>現場で使われ、意思決定を前に進めてきた"共通言語"</strong>です。</p><p>このページでは、FLARETECHのメンバーが<br>どう考え、どう行動し、何を成果と呼ぶのか<br>を形づくっている言葉を紹介します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '1.North Star - 私たちは、何のために存在しているか',
        alignment: 'left',
        variant: 'primary'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '誰もが輝く社会を創る',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>個人の努力が、正しく報われる構造をつくる。</p><p>FLARETECHの最上位概念であり、すべての判断の起点です。<br>才能や肩書きではなく、環境と機会によって人は輝けるという信念が、この言葉に込められています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '努力が足りない、で終わらせない。輝ける構造を用意するのが会社の責任だと思っています。',
        author: '河畑 福太郎',
        role: '代表'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '環境次第で人は変わる',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>人を責める前に、環境を疑う。</p><p>成果が出ない理由を「本人の能力」に押し付けません。<br>配置、期待値、支援、情報量──<br>人が変わる前に、設計を変えるのがFLARETECHの考え方です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '人を変えようとする前に、まず"この環境で勝てる設計か"を見直します。',
        author: '髙木 界',
        role: '取締役'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '本質 / 目的は？',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>"正しい努力"に切り替えるための問い。</p><p>作業が増えたとき、議論が迷走したときに必ず立ち返ります。<br>Whyを問うことは、否定ではなく前進のための整理です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '"目的は？"って聞かれると、一度立ち止まって頭が整理されます。無駄な動きが減る。',
        author: '中釜 直樹',
        role: '営業部'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/keywords/keywords-1.jpg',
        alt: 'North Starに向かう'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '2.Growth & Market Value - 私たちは、何を増やす会社か',
        alignment: 'left',
        variant: 'primary'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '（エンジニアの）市場価値',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>給料ではなく、選択肢が増えること。</p><p>FLARETECHでは、<br>スキル・経験・信頼の蓄積を<br><strong>「自分の市場価値への投資」</strong>と捉えます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '今の案件が"次の自分にどう効くか"を考えるようになりました。',
        author: '現場エンジニア',
        role: ''
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '量',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>成長は、打席数で決まる。</p><p>完璧を待たず、まずやる。<br>量をこなすことでしか見えない景色があると考えています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '最初から正解を出そうとしなくていい、と言われて気持ちが楽になりました。',
        author: '若手エンジニア',
        role: ''
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'KPI',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>"良くなった"を、説明できる形にする。</p><p>KPIは管理のためではなく、価値を可視化するための共通言語です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '感覚の会話が減って、"何が良くなったか"をちゃんと説明できるようになりました。',
        author: '営業メンバー',
        role: ''
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '3.How We Act - 私たちは、どう動くか',
        alignment: 'left',
        variant: 'primary'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '挑戦・誠実・共創',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>迷ったら、この3つに戻る。</p><p>評価・行動・意思決定の共通軸です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '判断に迷ったとき、この3つに当てはめると答えが出ます。',
        author: '採用担当',
        role: ''
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '当事者意識（自分ごと） / コトに向かう',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>ヒトではなく、課題に向かう。</p><p>感情論や立場論に逃げず、<br><strong>「何を解決すべきか」</strong>に集中します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '誰のせいかじゃなく、"何が止まってるか"を見る文化だと思います。',
        author: 'プロジェクトマネージャー',
        role: ''
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '凡事徹底',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>強さは、地味な反復でしか作れない。</p><p>特別なことより、当たり前をやり切る。<br>信頼は、派手さではなく継続から生まれます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '地味なことをちゃんとやってる人が、一番信頼されてます。',
        author: 'エンジニアリーダー',
        role: ''
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '信頼',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>信頼は、次のチャンスを連れてくる。</p><p>短期の正解より、長期の信頼。<br>数字の裏に、信頼が残るかを必ず問います。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '一度築いた信頼が、次の案件につながる瞬間を何度も見ました。',
        author: '営業部',
        role: ''
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/keywords/keywords-2.png',
        alt: '行動で示す'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '4.Result - 私たちは、何を成果と呼ぶか',
        alignment: 'left',
        variant: 'primary'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '成果',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>作業ではなく、相手が前に進んだ証拠。</p><p>「やった」ではなく<br>「何がどう良くなったか」を成果と呼びます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: 'お客さんの行動が変わったか、で振り返るようになりました。',
        author: '顧客対応エンジニア',
        role: ''
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '一歩（小さな一歩）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>完璧より、前進。</p><p>未来は、今日の一歩でできている。<br>止まるより、進むことを選びます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '完璧じゃなくていい、と言われると一歩踏み出せます。',
        author: '若手メンバー',
        role: ''
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: "5.Vibes（補助概念） - 最後の判断軸",
        alignment: 'left',
        variant: 'primary'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'バイブス',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>論理と成果を前提にした、FLARETECHらしさの感覚。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '最後はバイブスを見る。でもそれは、雑に決めるためじゃない。',
        author: '髙木 界',
        role: '取締役'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>この言葉が、しっくり来るなら。</h3><p>FLARETECHの文化は、<br>言葉に共感できるかどうかで、ほぼ決まります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<a href="/culture/knowledge"><strong>FLARETECHのナレッジ </strong></a><br>現場が多様なSESだからこそ、仕事の質と成長に「再現性」をもたらすナレッジの運用は不可欠です。',
        alignment: 'center'
      }
    }
  ],
  status: 'published'
};

// Culture Knowledge Page
export const cultureKnowledgePageData = {
  title: 'Our Knowledge',
  titleEn: 'Our Knowledge',
  subtitle: 'FLARETECHのナレッジ',
  slug: '/culture/knowledge',
  category: 'culture',
  site: 'career',
  metaTitle: 'Our Knowledge - FLARETECHのナレッジ | FLARETECH採用サイト',
  metaDescription: 'FLARETECHのナレッジに対する考え方をご説明します。現場が多様なSESだからこそ、仕事の質と成長に「再現性」をもたらすナレッジの運用は不可欠です。',
  metaKeywords: ['ナレッジ', '知識共有', '成長支援', '再現性', 'フレアテック'],
  showToc: true,
  tocLinks: [
    { title: 'Purpose / Mission / Vision', path: '/culture/vision-mission' },
    { title: 'FLARETECHのバリュー（行動指針）', path: '/culture/values' },
    { title: 'FLARETECHのプリンシプル（行動原理）', path: '/culture/principles' },
    { title: 'FLARETECHの社内用語', path: '/culture/keywords' },
    { title: 'FLARETECHのナレッジ', path: '/culture/knowledge' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>このページでは、FLARETECHのナレッジに対する考え方をご説明します。<br>現場が多様なSESだからこそ、仕事の質と成長に「再現性」をもたらすナレッジの運用は不可欠です。<br>まだまだ改善の途中ですが、私たちのナレッジマネジメントの現在地を、このページでご確認ください。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'FLARETECHにおけるナレッジとは？',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでいうナレッジは、<strong>「誰かの経験を、次の誰かの再現に変えるもの」</strong>です。</p><p>SESでは、現場が変わるだけで扱う技術・役割・期待値が大きく変わります。<br>放っておけば、成長は「案件運」に寄っていきます。</p><p>だから私たちは、</p><ul><li>困ったときに戻れる場所</li><li>相談の入口が切れない導線</li><li>成長の偶然を減らす仕組み</li></ul><p>を、ナレッジとして設計します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '1. 全社共通のナレッジ',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/0029.jpg',
        alt: '全社共通のナレッジ - チームコラボレーション',
        caption: '',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>全社共通のナレッジは、<strong>「現場が違っても迷わないための土台」</strong>です。</p><h3>例）</h3><ul><li><strong>入社〜立ち上がり</strong>（オンボーディングの基本）</li><li><strong>各種手続き・ルール</strong>（勤怠・経費・セキュリティ等）</li><li><strong>学習支援</strong>（書籍購入補助、資格支援、学び方の指針）</li><li><strong>キャリア支援</strong>（面談の使い方、案件選びの考え方）</li></ul><p>ポイントは、制度を並べることではなく、<br><strong>「いつ」「何に困ったら」「どこを見ればいいか」</strong>が分かることです。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '2. 部門別のナレッジ',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/0025.jpg',
        alt: '部門別のナレッジ - 各部門の取り組み',
        caption: '',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>部門別ナレッジは、<strong>「勝ちパターンの言語化」</strong>です。<br>成果の定義が違う以上、必要なナレッジの粒度も変わります。</p><ul><li><strong>営業：</strong>商談の型／提案の型／単価交渉／案件理解</li><li><strong>採用：</strong>候補者体験／面接設計／スクリーニング観点</li><li><strong>エンジニアリーダー：</strong>1on1の設計／成長ロードマップ／案件アサインの視点</li><li><strong>エンジニア：</strong>学習ロードマップ／資格ロードマップ／技術共有</li></ul><p>部門別ナレッジは「上から与える」だけでなく、<br>現場の学びを吸い上げて更新される形を目指します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '3. 勉強会・共有会',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/0073.jpg',
        alt: '勉強会・共有会 - 学習とコミュニティ',
        caption: '',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>勉強会は知識共有だけではなく、<strong>「横のつながりを作る装置」</strong>です。<br>現場が違っても、学びが循環する状態をつくります。</p><ul><li>テーマ別の勉強会（例：AWS／フロント／データ等）</li><li>事例共有（現場で得た学び・失敗・工夫）</li><li>任意参加のコミュニティ（交流・相談の場）</li></ul><p>「参加しなきゃ」ではなく、<br>参加することで"視点と仲間"が増える設計にしています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '4. Slack（フロー型ナレッジ）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>Slackは、体系よりもスピードを担うフロー型ナレッジです。</p><ul><li>小さな疑問を即相談できる</li><li>失敗談やアンチパターンが共有される</li><li>「この状況、どう見る？」が集まる</li></ul><p>迷いが出たとき、まず戻れるのがSlack。<br>整理すべき内容は、必要に応じてストックに反映していきます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社員から見たナレッジ（声）',
        alignment: 'left'
      }
    },
    {
      type: 'quote',
      data: {
        text: '提案はセンスじゃなく、型。<br>共有すれば、誰でも再現できるようになります。',
        author: '中釜 直樹',
        role: '営業部',
        image: '/images/team/nakagama-back.jpg'
      }
    },
    {
      type: 'quote',
      data: {
        text: 'ナレッジは完成品じゃない。<br>使われて、改善されて、初めて価値になります。',
        author: '高木 界',
        role: '取締役',
        image: '/images/team/takagi-back.jpg'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'FLARETECHは本当にナレッジ豊富なのか？',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>正直に言うと、完璧に豊富ではありません。<br>だからこそ、私たちは2つを同時に進めます。</p><h3>1）ナレッジ化は、まだ道半ば</h3><p>技術も案件も変わる以上、追いつかない領域は必ず出ます。<br>ここは継続投資します。</p><h3>2）ナレッジは、正解集ではない</h3><p>便利なテンプレだけを増やすと、<br>「正解がないと動けない」人を増やしてしまう。</p><p>FLARETECHが増やしたいのは、<br><strong>"ヒント・視点・再現できる学び方"</strong>です。</p><ul><li>情報がなければ仮説で動く。</li><li>迷ったら相談する。</li><li>学びは共有して次の人の近道にする。</li></ul><p>この循環が、FLARETECHのナレッジです。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    }
  ],
  status: 'published'
};

// ============================================
//Job Page
// ============================================

// Job Development Engineer Page
export const jobDevelopmentEngineerPageData = {
  title: '開発エンジニア',
  titleEn: 'Development Engineer',
  subtitle: 'Engineering at FLARETECH',
  slug: '/jobs/development-engineer',
  category: 'job',
  site: 'career',
  metaTitle: '開発エンジニア | FLARETECH採用サイト',
  metaDescription: 'FLARETECHの開発エンジニア職について詳しくご紹介します。',
  metaKeywords: [],
  jobPosting: {
    identifier: 'FT-development-engineer',
    employmentType: 'FULL_TIME',
    hiringClosed: false,
    isRemote: true,
    directApply: false
  },
  showToc: true,
  tocLinks: [
    { title: '開発エンジニア', path: '/jobs/development-engineer' },
    { title: 'インフラエンジニア', path: '/jobs/infrastructure-engineer' },
    { title: 'DXエンジニア', path: '/jobs/dx-engineer' },
    { title: '営業', path: '/jobs/sales' }
  ],
  metadata: {
    updatedDate: '2026.xx.xx',
    readTime: '約10分'
  },
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHについて', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、「誰もが輝く社会を創る」 をミッションに掲げ、IT領域におけるエンジニアの技術力を通して、さまざまな業界・企業の経営課題を解決してきました。</p><p>私たちが向き合っているのは、「スキルがあるのに評価されない」「環境次第で、成長が止まってしまう」という エンジニア市場そのものの課題です。</p><p>2020年11月の創業以降、システムエンジニアリングサービス（SES）を主力事業として、400社以上のお客様に対し、システム開発・インフラ構築・DX支援を提供してきました。</p><p>近年は既存事業の成長に加え、ITエンジニア特化型転職支援サービス 「FLARETECHキャリア」 を立ち上げ、エンジニアの市場価値を多角的に高める事業を推進しています。</p><p>今回、事業拡大と組織強化に伴い、開発エンジニアとして事業成長を担う仲間を募集しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/development/flaretech-1.png',
        alt: 'Software Development'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHの開発エンジニアとは', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのエンジニアは、単にコードを書く存在ではありません。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'featureGrid',
      data: {
        columns: 3,
        items: [
          { title: '01｜価値を実装する人', description: '要件どおりに作るのではなく、「なぜそれを作るのか」「どうすれば価値が最大化するか」を考え、技術で実装します。' },
          { title: '02｜市場価値を積み上げる人', description: '案件は消費されるものではなく、次のキャリアにつながる"資産"として捉えます。' },
          { title: '03｜チームで成果を出す人', description: '一人で抱え込まず、デザイナー・PM・営業と連携しながら価値を創ります。' }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '仕事の魅力', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>本質的な価値づくりに関われる</h3><p>FLARETECHでは、「作業」ではなく 成果 が評価されます。</p><ul><li>ユーザー体験がどう変わったか</li><li>ビジネスにどう貢献したか</li><li>チームとして何を前進させたか</li></ul><p>技術は手段であり、目的ではありません。</p><h3>多様な業界・技術領域に挑戦できる</h3><p>金融、官公庁、医療、製造、AI・ロボティクスなど、業界もフェーズも異なるプロジェクトに関われます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/development/appeal-1.jpg',
        alt: 'エンジニアの仕事の魅力'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '具体的な職務内容', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>4,000社以上の提携先プロジェクトの中から、スキル・志向・キャリア設計を踏まえて配属を決定します。</p><ul><li>Android／iOSアプリケーション開発</li><li>Webアプリケーション開発</li><li>UI/UX設計・実装</li><li>テスト／リリース対応</li><li>要件定義・設計（プロジェクトにより）</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'プロジェクト事例（一部）', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>■ 金融系 新規サービスプラットフォーム開発</h3><p>大手証券会社の次世代投資サービス<br>技術選定〜設計・実装を担当<br>環境：React / Next.js / TypeScript / GitHub / AWS</p><h3>■ 環境系 データ収集システム新規開発</h3><p>省庁向け水質・地質データ管理システム<br>フロント・バックエンドをフルスタックで担当<br>環境：React / Next.js / TypeScript / Java(Spring Boot) / PostgreSQL / AWS</p><h3>■ 医療向け AIロボット操作アプリ開発</h3><p>AIリハビリ装置のモバイルUI開発<br>モバイルアプリ設計〜実装<br>環境：Flutter / VS Code</p><h3>■ 顧客向けシステム バックエンド開発</h3><p>Goを用いたバックエンド開発<br>要件定義〜実装まで参画<br>環境：Go / React / Redux / AWS / Docker</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '配属の考え方｜「いま」ではなく「次」で決める', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、スキルが合うから配属することをゴールにしていません。</p><p>配属判断の軸は、常にこの問いです。</p><p><strong>「この案件は、この人の市場価値を次にどう広げるか？」</strong></p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>配属までの流れ</h3><p><strong>スキル・経験の棚卸し</strong><br>言語・工程・役割だけでなく、得意・課題も整理します。</p><p><strong>キャリア志向の言語化</strong><br>フロント特化、フルスタック、上流挑戦などを共有。</p><p><strong>案件の期待値を明確化</strong><br>何を任され、何が評価されるかを事前にすり合わせます。</p><p><strong>配属後のフォローと更新</strong><br>配属は固定ではありません。成長に応じて、次の役割へ進みます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/development/assignment-1.jpg',
        alt: '配属の考え方'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '成長が「運」にならない理由', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、成長を個人任せにしません。</p><ul><li>定期的なキャリア面談</li><li>勉強会・ナレッジ共有</li><li>相談しやすい社内コミュニケーション</li><li>明確な評価制度</li></ul><p><strong>「頑張った人が、次に進める構造」</strong>を設計しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/development/growth-1.jpg',
        alt: 'エンジニアの成長支援'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '必須スキル', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>主体的に課題解決に向き合える姿勢</li><li>周囲を巻き込むコミットメント力</li><li>信頼関係を築けるコミュニケーション力</li><li>システム開発経験 半年以上</li></ul><p><strong>【使用言語例】</strong><br>React / Vue / JavaScript / Java / PHP / Python / Swift / Kotlin など</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '歓迎スキル', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>独学を含む半年以上の開発経験</li><li>経験浅め・第二新卒歓迎</li><li>転職回数・学歴不問</li><li>英語力がある方</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '求める人物像', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>FLARETECHのミッション・バリューに共感できる方</li><li>誠実で、素直に学べる方</li><li>会社とともに成長したい方</li><li>好奇心を持って課題に挑戦できる方</li><li>自ら機会をつくり、前進できる方</li></ul><p>※FLARETECHのバリュー<br>「挑戦」「誠実」「共創」</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '社員の声', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>佐藤航｜フロントエンドエンジニア</strong><br>「"今できること"より、"次に何を積むか"で案件を選んでもらえました。」</p><p><strong>山本 勇気｜バックエンドエンジニア</strong><br>「この経験が市場でどう評価されるかを、毎回一緒に考えてくれます。」</p><p><strong>鈴木 彩｜モバイルエンジニア</strong><br>「技術だけじゃなく、考え方も相談できるのが安心です。」</p><p><strong>田中 肇｜フルスタックエンジニア</strong><br>「少し背伸びする案件に挑戦できたことが、転機になりました。」</p><p><strong>中村 健太｜エンジニア</strong><br>「無理なことは無理と言ってくれる。その誠実さが信頼できます。」</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '最後に', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHが求めているのは、完璧なエンジニアではありません。</p><ul><li>伸びたいと思っている</li><li>環境を活かして成長したい</li><li>市場価値を上げたい</li></ul><p>そう考えている人と、長く一緒に働きたいと考えています。</p><p>この環境が、自分に合うかどうか。それは、話してみるのが一番早い。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社員インタビュー',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>開発エンジニアとして働く社員のインタビュー記事をご紹介します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'noteArticles',
      data: {
        articles: [
          {
            title: '『面白いことがしたい』から始まった30代エンジニア転職—飲食業界での課題解決体験が人生を変えた—｜エンジニアインタビュー【vol.32】',
            link: 'https://note.com/flaretech_it/n/n207e25a97f11',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/314829890/rectangle_large_type_2_089e2cde2e573870b81010bc8d0d7ccf.png?width=800'
          },
          {
            title: '好奇心と挑戦心で切り拓くキャリア──医療現場からIT業界への転身｜エンジニアインタビュー【vol.31】',
            link: 'https://note.com/flaretech_it/n/naebe6a5000f4',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/314831757/rectangle_large_type_2_9ad89864d4c3d5aa9afc89d24faaa053.png?width=800'
          },
          {
            title: 'エンジニアインタビュー【vol.28】kintoneからReactへ ～キャリアアップとスキル向上を求め、家族と共に北海道から東京へ～',
            link: 'https://note.com/flaretech_it/n/nd329887bf7c2',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/314839569/rectangle_large_type_2_c9f640dd6a5236c1004fb62f807aac53.png?width=800'
          },
          {
            title: 'エンジニアインタビュー【vol.28】バリスタからWeb系エンジニアへのキャリアチェンジの背景に迫る',
            link: 'https://note.com/flaretech_it/n/n65de6493eb2b',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/314847852/rectangle_large_type_2_aff5ad76aba024c179e521c8d0fa4b76.png?width=800'
          },
          {
            title: 'エンジニアインタビュー【vol.27】組み込みエンジニアからWeb系エンジニアへ～わずか2年で上流から下流まで全てを担当するエンジニアの成長の背景とは～',
            link: 'https://note.com/flaretech_it/n/n841fa34b2a42',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/314849095/rectangle_large_type_2_b7c42cefefb8333e2c252fb0398e0408.png?width=800'
          }
        ]
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs'],
        note: 'キャリア・技術・不安、何でも話してください。'
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

// Job Infrastructure Engineer Page
export const jobInfrastructureEngineerPageData = {
  title: 'インフラエンジニア',
  titleEn: 'Infrastructure Engineer',
  subtitle: 'Infrastructure Engineer at FLARETECH',
  slug: '/jobs/infrastructure-engineer',
  category: 'job',
  site: 'career',
  metaTitle: 'インフラエンジニア | FLARETECH採用サイト',
  metaDescription: 'FLARETECHのインフラエンジニア職について詳しくご紹介します。',
  metaKeywords: [],
  jobPosting: {
    identifier: 'FT-infrastructure-engineer',
    employmentType: 'FULL_TIME',
    hiringClosed: false,
    isRemote: true,
    directApply: false
  },
  showToc: true,
  tocLinks: [
    { title: '開発エンジニア', path: '/jobs/development-engineer' },
    { title: 'インフラエンジニア', path: '/jobs/infrastructure-engineer' },
    { title: 'DXエンジニア', path: '/jobs/dx-engineer' },
    { title: '営業', path: '/jobs/sales' }
  ],
  metadata: {
    updatedDate: '2026.xx.xx',
    readTime: '約10分'
  },
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHについて', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、「誰もが輝く社会を創る」 をミッションに掲げ、IT領域におけるエンジニアの技術力を通じて、企業や社会の課題解決に取り組んでいます。</p><p>私たちが向き合っているのは、「支える仕事が、正しく評価されない」「運用経験が、キャリアの選択肢につながらない」といった インフラエンジニアを取り巻く構造的な課題です。</p><p>2020年11月の創業から4年。システムエンジニアリングサービス（SES）を主力事業として、400社以上のお客さまに対し、システム開発・インフラ構築・クラウド移行・DX支援を提供してきました。</p><p>近年は既存事業の成長に加え、ITエンジニア特化型転職支援サービス「FLARETECHキャリア」 を立ち上げ、エンジニア市場全体の価値向上にも取り組んでいます。</p><p>今回、クラウド移行・自動化ニーズの拡大に伴い、インフラ部門の中核を担うエンジニアを募集します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/infrastructure/flaretech-1.png',
        alt: 'Cloud Infrastructure'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHのインフラエンジニアとは', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのインフラエンジニアは、「止めない人」では終わりません。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'featureGrid',
      data: {
        columns: 3,
        items: [
          { title: '01｜システムの土台を設計する人', description: '安定稼働は前提。なぜこの構成なのか、どう進化させるのかまで考えます。' },
          { title: '02｜自動化・最適化を進める人', description: '属人化した運用をなくし、再現性のある仕組みをつくります。' },
          { title: '03｜市場価値を積み上げる人', description: 'オンプレからクラウド、運用から設計・アーキテクトまで。経験を キャリア資産 に変えていきます。' }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '仕事の魅力', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>「守る」から「進化させる」インフラへ</h3><p>FLARETECHでは、インフラを コストセンター とは考えていません。</p><ul><li>可用性をどう高めるか</li><li>運用負荷をどう下げるか</li><li>ビジネススピードにどう貢献するか</li></ul><p>インフラエンジニアも、事業を前に進める当事者です。</p><h3>クラウド・自動化案件が豊富</h3><p>AWSを中心に、クラウド移行・モダナイゼーション・IaCなど成長市場ど真ん中の案件に関われます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '具体的な職務内容', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>4,000社以上の提携先から、スキル・志向・キャリア設計を踏まえて配属を決定します。</p><ul><li>インフラ設計・構築</li><li>運用・保守</li><li>オンプレミス環境対応</li><li>クラウド（AWS / Azure / GCP）</li><li>サーバ／ネットワーク構築・管理</li><li>自動化・改善提案　など</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'プロジェクト事例（一部）', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>■ AWS基盤 メタバース空間 開発支援</h3><p>AIによる音声・表情解析を活用したユーザーコミュニケーション促進システム<br>環境：Python3 / AWS / API Gateway / Lambda / S3 / DynamoDB / OpenSearch</p><h3>■ 大手求人サイト インフラ構築（AWS）</h3><p>BtoCサービスのインフラ構築全般を担当<br>コンテナ構築 / データ分析基盤 / ジョブ管理 / アーキテクチャ設計<br>環境：AWS</p><h3>■ ネットワーク設計・構築・運用</h3><p>大規模ネットワークの設計から維持管理まで担当<br>環境：Cisco ISR / ASR / Catalyst / Nexus9000 / Juniper SRX</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/infrastructure/projects-1.png',
        alt: 'Network Infrastructure'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '配属の考え方｜キャリア起点で決める', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、「いま空いている案件」では配属を決めません。</p><p>問いは常にこれです。</p><p><strong>「この経験は、次の市場価値をどう広げるか？」</strong></p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>配属フロー</h3><ul><li>スキル・経験の棚卸し</li><li>キャリア志向の確認（設計／クラウド／SREなど）</li><li>案件の期待値・役割の明確化</li><li>配属後の定期フォローと調整</li></ul><p>配属はゴールではなく、キャリア形成の通過点です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '成長が属人化しない理由', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>キャリア面談</li><li>勉強会・ナレッジ共有</li><li>社内コミュニティ</li><li>明確な評価制度</li></ul><p>「学べる人だけが伸びる」ではなく、伸びる構造を会社がつくることを重視しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/infrastructure/growth-1.png',
        alt: 'IoT Infrastructure'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '必須スキル', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>主体的に課題解決に向き合える姿勢</li><li>メンバーを巻き込むコミットメント力</li><li>信頼関係を築けるコミュニケーション力</li></ul><p><strong>以下いずれかの実務経験</strong></p><ul><li>保守運用</li><li>設計構築</li><li>オンプレミス</li><li>クラウド</li><li>サーバ</li><li>ネットワーク</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '歓迎スキル', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>独学を含む半年以上のインフラ経験</li><li>経験浅め・第二新卒歓迎</li><li>転職回数・学歴・ブランク不問</li><li>英語力がある方</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '求める人物像', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>FLARETECHのミッション・バリューに共感できる方</li><li>誠実で、素直に学べる方</li><li>会社とともに成長したい方</li><li>好奇心を持って課題に挑戦できる方</li><li>自ら機会を創り、前進できる方</li></ul><p>※FLARETECHのバリュー<br>「挑戦」「誠実」「共創」</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '社員の声（インフラのリアル）', alignment: 'left', background: 'white' }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>加藤 紘一｜インフラエンジニア</strong><br>「運用経験が、次の設計案件につながりました。」</p><p><strong>佐々木 翔｜クラウドエンジニア</strong><br>「"止めない"だけで終わらない評価が嬉しいです。」</p><p><strong>山口勉｜ネットワークエンジニア</strong><br>「構成の理由まで説明できるようになりました。」</p><p><strong>中村 亮｜インフラエンジニア</strong><br>「キャリアの相談を、技術の話と一緒にできます。」</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '最後に', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHが探しているのは、完成されたインフラエンジニアではありません。</p><ul><li>今より市場価値を上げたい</li><li>運用から次のステージに進みたい</li><li>環境を変えて成長したい</li></ul><p>そう考えている方と、長く一緒にキャリアをつくりたいと考えています。</p><p>この環境が、自分に合うかどうか。</p><p>まずは、話してみてください。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社員インタビュー',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>インフラエンジニアとして働く社員のインタビュー記事をご紹介します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'noteArticles',
      data: {
        articles: [
          {
            title: 'エンジニアインタビュー【vol.30】「夢はあなたから逃げない」： ReactからAWSへ、未踏のキャリアチェンジを実現したエンジニアの軌跡',
            link: 'https://note.com/flaretech_it/n/nc501095aa772',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/314834319/rectangle_large_type_2_1a9e68eb252eb20b282ec4521e25d4b1.png?width=800'
          },
          {
            title: 'エンジニアインタビュー【vol.26】困難を乗り越え、フルスタックエンジニアとして活躍する2児のパパ',
            link: 'https://note.com/flaretech_it/n/n9eb6aa99fcc9',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/158125079/rectangle_large_type_2_da9c3d9d4db5b322a9daec3c6595f423.png?width=1280'
          }
        ]
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs'],
        note: 'キャリア・技術・不安、何でも話してください。'
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

// Job Engineer Page
export const jobDXEngineerPageData = {
  title: 'DXエンジニア',
  titleEn: 'DX Engineer',
  subtitle: 'DX Engineer at FLARETECH',
  slug: '/jobs/dx-engineer',
  category: 'job',
  site: 'career',
  metaTitle: 'DXエンジニア | FLARETECH採用サイト',
  metaDescription: 'FLARETECHのDXエンジニア職について詳しくご紹介します。',
  metaKeywords: [],
  jobPosting: {
    identifier: 'FT-dx-engineer',
    employmentType: 'FULL_TIME',
    hiringClosed: false,
    isRemote: true,
    directApply: false
  },
  showToc: true,
  tocLinks: [
    { title: '開発エンジニア', path: '/jobs/development-engineer' },
    { title: 'インフラエンジニア', path: '/jobs/infrastructure-engineer' },
    { title: 'DXエンジニア', path: '/jobs/dx-engineer' },
    { title: '営業', path: '/jobs/sales' }
  ],
  metadata: {
    updatedDate: '2026.02.04',
    readTime: '約7分'
  },
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHについて', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは「誰もが輝く社会を創る」をミッションに掲げ、エンジニアの技術力を通じて、さまざまな業界・企業の経営課題の解決に取り組んでいます。</p><p>ミッションに込めた代表の想いはこちら：（リンク）</p><p>2020年11月の創業以来、SESを主力に 400社以上のお客さまへ、システム開発・インフラ構築・DX支援を提供してきました。近年は既存事業の成長に加え、新規事業「FLARETECHキャリア（ITエンジニア特化型転職支援サービス）」など、エンジニア市場の課題を多角的に解決するサービスも推進しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/dx/flaretech-1.png',
        alt: 'Digital Transformation'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'なぜ、DX職を募集するのか', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>DXの需要が高まる一方で、現場では「ツールを入れたが定着しない」「要件が曖昧で炎上する」「推進役がいない」といった"実装以外"の壁が起き続けています。</p><p>FLARETECHのDX職は、ツール導入をゴールにしません。業務の現実を捉え、関係者を巻き込み、仕組みとして定着させる——ここまで含めて支援します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/dx/why-1.png',
        alt: 'DX推進の理由'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '具体的な職務内容', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>上場企業との直取引を含む 4,000社以上の提携先にて、幅広いプロジェクトがあります。</p><ul><li>ローコード／ノーコード開発（Outsystems など）</li><li>RPA導入・自動化（UiPath 等）</li><li>Salesforce／kintone の導入・カスタマイズ</li><li>PMO（進捗・課題・品質・ベンダー管理）</li><li>IT／システム／DXコンサルティング（業務整理、要件定義、定着支援）</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'プロジェクト事例（一部）', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>大手製造業向け 基幹システム更改（SAP）</h3><p>導入支援／データ移行支援／PMO</p><h3>UiPathシナリオ作成</h3><p>顧客折衝／シナリオ作成／ロボット開発（UiPath, VBA）</p><h3>大手不動産会社向け Salesforce導入</h3><p>要件定義〜実装・リリースまで</p><h3>物流系システム再構築（Outsystems）</h3><p>受発注システムの再構築をローコードベースで推進</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'この仕事で伸びる力', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>DXは"技術＋業務＋推進"の掛け算で価値が決まります。FLARETECHで伸びるのは、次の3つです。</p><ul><li><strong>業務の構造化力</strong>：現場の言葉を要件に落とす</li><li><strong>合意形成／推進力</strong>：関係者を前に進める</li><li><strong>実装の再現性</strong>：属人化せずに回る仕組みにする</li></ul><p>「作れる」だけでなく、変えられる人になる。それがDX職のキャリア資産です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/dx/skills-1.jpg',
        alt: 'DXエンジニアのスキル'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'アサインの考え方', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>私たちは、スキルやキャリア志向を伺った上で、プロジェクト配属を決定します。目的は、単なる稼働ではなく "次の選択肢が増える経験" を積むことです。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '必須スキル', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>主体的に動き、合理的に課題解決できる力</li><li>メンバーを巻き込み、実行までやり切るコミットメント</li><li>信頼関係を築けるコミュニケーション力</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'いずれかの経験', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>システム開発（言語不問／ローコード・SFA経験も不問）</li><li>RPA導入プロジェクト</li><li>PMO</li><li>IT／システム／DXコンサルティング</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '歓迎スキル', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>独学でも半年以上の開発経験</li><li>第二新卒・経験浅め歓迎（転職回数・学歴・ブランク不問）</li><li>英語力</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '求める人物像', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>ミッション／バリュー（挑戦・誠実・共創）に共感できる方</li><li>誠実で、素直に学べる方</li><li>会社と共に成長したい方</li><li>好奇心を持って課題に挑戦できる方</li><li>自ら機会を創り、前に進められる方</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '社員の声', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>土井 真司｜DX推進（PMO）</h3><p>「"正解がない状態"を整理して前に進めるのが、DXの一番の価値だと実感しています。」</p><h3>田中 聡｜バックオフィス（DX推進）</h3><p>「導入より"定着"が難しい。そこまで並走するのがFLARETECHのDXだと思います。」</p><h3>中釜 直樹｜営業（DX案件担当）</h3><p>「ツール導入の話より、業務の本質を一緒に掘る案件の方が結果が出ます。」</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/dx/final-1.jpg',
        alt: 'DXチームの声'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '最後に', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは、「今できること」よりも「これから何を積み上げたいか」 を大切にしています。</p><p>開発か、インフラか。クラウドか、オンプレか。実装か、設計か。</p><p>答えは一つではありません。あなたの現在地と、次の一歩に合わせて、一緒に選びたいと考えています。</p><p>まずは、話してみませんか。選考ではありません。キャリア・技術・市場価値について、整理する時間です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社員インタビュー',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>DXエンジニアとして働く社員のインタビュー記事をご紹介します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'noteArticles',
      data: {
        articles: [
          {
            title: 'エンジニアインタビュー【vol.25】社内SEからDXエンジニアへ｜バイリンガル環境でDXスキルを磨く木本さんの挑戦',
            link: 'https://note.com/flaretech_it/n/n112fd1a213f9',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/314841856/rectangle_large_type_2_41c07e3a78947b32d9cebb4718bf50ab.png?width=800'
          },
          {
            title: 'DXエンジニアの日常：柔軟性と創造性でビジネス課題に立ち向かう ～評価される考え方～',
            link: 'https://note.com/flaretech_it/n/n1b45e6754971',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/123833609/rectangle_large_type_2_3de783092d155e16e874c0baeae24a3c.png?width=1280'
          }
        ]
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

// Sales Page
export const jobSalesPageData = {
  title: '営業',
  titleEn: 'Sales',
  subtitle: 'Sales at FLARETECH',
  slug: '/jobs/sales',
  category: 'job',
  site: 'career',
  metaTitle: '営業 | FLARETECH採用サイト',
  metaDescription: 'FLARETECHの営業職について詳しくご紹介します。',
  metaKeywords: [],
  jobPosting: {
    identifier: 'FT-sales',
    employmentType: 'FULL_TIME',
    hiringClosed: false,
    isRemote: true,
    directApply: false
  },
  showToc: true,
  tocLinks: [
    { title: '開発エンジニア', path: '/jobs/development-engineer' },
    { title: 'インフラエンジニア', path: '/jobs/infrastructure-engineer' },
    { title: 'DXエンジニア', path: '/jobs/dx-engineer' },
    { title: '営業', path: '/jobs/sales' }
  ],
  metadata: {
    updatedDate: '2026.02.04',
    readTime: '約7分'
  },
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHについて', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは「誰もが輝く社会を創る」 をミッションに掲げ、ITエンジニアの技術力を通じて、企業の経営課題を解決するテックカンパニーです。</p><p>2020年11月の創業以来、SES事業を主軸に400社以上の企業へシステム開発・インフラ構築・DX支援を提供してきました。</p><p>近年は既存事業の成長に加え、ITエンジニア特化型転職支援サービス 「FLARETECHキャリア」など、新規事業も立ち上げ、エンジニア市場そのものの構造課題に向き合っています。</p><p>今回募集するのは、この成長フェーズを <strong>営業の力でさらに加速させるセールス職</strong> です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/sales/flaretech-1.png',
        alt: 'Sales Team'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '採用背景 ― IT × 営業で、次の成長曲線へ', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>DX需要の拡大により、SES市場は今後も長期的な人材不足が続きます。FLARETECHではすでに案件数・取引社数ともに右肩上がりですが、</p><ul><li>エンジニアが活躍できる案件をもっと増やす</li><li>市場価値が上がるマッチングの精度を高める</li></ul><p>そのために、営業体制の強化が不可欠です。</p><p>あなたの営業力は、単なる売上ではなく<strong>エンジニアと企業、双方の成長を加速させる力</strong>になります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '仕事内容', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのセールスは、ITエンジニアと企業をつなぐ"橋渡し役" です。</p><h3>① 新規開拓・課題ヒアリング</h3><p>電話・メールを通じて企業へアプローチ。IT人材に関する課題を丁寧にヒアリングし、商談につなげます。</p><h3>② 人材提案・マッチング</h3><p>案件内容に応じて、自社エンジニアを提案。面談調整・条件整理・フォローまで一貫して対応します。</p><h3>③ 契約・稼働後フォロー</h3><p>契約締結後も、エンジニア・企業の双方を継続的にサポート。信頼関係を築き、追加提案や長期取引へつなげます。</p><p>◎「売って終わり」ではありません<br>◎ エンジニアのキャリアに責任を持つ営業です<br>◎ IT業界未経験から活躍しているメンバーも多数います</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/sales/work-1.png',
        alt: 'セールスの仕事'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'キャリアパス', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのセールスに、決まった型はありません。</p><h3>① プレイヤーとしての実績づくり</h3><p>企業開拓、人材提案、継続フォローを通じて、再現性のある営業力を磨きます。<br>※代表からロジカルな営業ノウハウを直接学べます。</p><h3>② マネジメント・戦略立案へ</h3><p>チームリーダー・マネージャーとして、メンバー育成、営業戦略の設計、組織づくりを担うポジションへ。</p><h3>③ 他職種・経営ポジションへの挑戦</h3><p>人事、広報、事業企画、新規事業など、職種を越えたキャリアも可能。代表直下で事業戦略や新規プロジェクトを推進するチャンスもあります。</p><p>◎ 営業から始まり、キャリアを広げていける<br>◎ ベンチャーならではの裁量とスピード感</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '必須スキル', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>社会人経験があり、ブランクがない方（業界不問）</li><li>IT × 人材ビジネスへの興味</li><li>目標達成に向けてやり抜く意欲</li><li>主体的に課題解決へ取り組める力</li><li>周囲を巻き込むコミットメント力</li><li>信頼関係を築けるコミュニケーション力</li><li>基本的なPC操作（Google Workspace）</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '歓迎スキル', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>SI／SES営業の経験</li><li>営業事務の経験</li><li>数字・データへのリテラシー</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '求める人物像', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>FLARETECHのミッション・バリューに共感できる方</li><li>誠実で素直な方</li><li>会社と共に成長したい方</li><li>好奇心を持って課題に挑戦できる方</li><li>自ら機会を創り出す意思を持っている方</li></ul><p>※FLARETECHのバリュー：「挑戦」「誠実」「共創」</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'quote',
      data: {
        text: '今の案件が"次の自分にどう効くか"を考えるようになりました。',
        author: '現場エンジニア',
        role: ''
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '社員の声', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>中釜 直樹｜営業部 部長</h3><p>「数字より先に信頼を見る。結果として、そのほうが条件も単価も後から必ず良くなります。」</p><h3>鈴木（寿）｜営業部 チームリーダー</h3><p>「案件を取る仕事ではなく、エンジニアが次に成長できる場所を"設計する"仕事だと思っています。」</p><h3>市倉 凌成｜営業部</h3><p>「断られた理由を次に使う。それをやり続けると、営業は感覚じゃなく再現性になります。」</p><h3>森戸 有輝｜営業部</h3><p>「エンジニアの将来を背負って提案している感覚があるから、いい加減なマッチはできません。」</p><h3>高木 界｜取締役</h3><p>「FLARETECHの営業は、売上を作る人ではなく"市場価値が上がる構造"を作る人です。」</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/jobs/sales/final-1.jpg',
        alt: '営業チームの声'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '最後に', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのセールスは、売る仕事ではありません。</p><p>人を見て、未来を考えて、構造をつくる仕事です。</p><p>この考え方に、少しでもしっくり来たなら。まずは話してみてください。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '社員インタビュー',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>営業として働く社員のインタビュー記事をご紹介します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'noteArticles',
      data: {
        articles: [
          {
            title: 'Marvelの営業部ってどんな感じ？？👀',
            link: 'https://note.com/flaretech_it/n/nd47e615f22c0',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/124631948/rectangle_large_type_2_16c107e3b31d5e650b3ff937b5eccd0c.png?width=1280'
          },
          {
            title: '元教師からIT業界へ。人と向き合う仕事への情熱を持ち続ける挑戦ストーリー',
            link: 'https://note.com/flaretech_it/n/n42df48dc86e3',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/132530340/rectangle_large_type_2_ca895d96d35bca75c1ec77e59aeaf307.png?width=1280'
          },
          {
            title: '航海士から未経験でIT企業の営業職へ～23歳若手社員の挑戦を追う～【営業インタビュー】',
            link: 'https://note.com/flaretech_it/n/ne3509e63ab03',
            imageUrl: 'https://assets.st-note.com/production/uploads/images/314836550/rectangle_large_type_2_d2887f2c3f86e14c189bccf81f000e37.png?width=800'
          }
        ]
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

// ============================================
// WORKSTYLE PAGES
// ============================================

export const workstyleSurveyPageData = {
  slug: '/workstyle/survey',
  title: '社員アンケート',
  titleEn: 'Employee Survey',
  subtitle: 'Survey',
  category: 'workstyle',
  site: 'career',
  publishedAt: '2025-01-26',
  showToc: true,
  tocLinks: [
    { title: '社員アンケート', path: '/workstyle/survey' },
    { title: 'Organization Design', path: '/workstyle/px' },
    { title: 'オンボーディング', path: '/workstyle/onboarding' },
    { title: '育成・学習機会', path: '/workstyle/development' },
    { title: '評価と報酬', path: '/workstyle/evaluation' },
    { title: 'キャリアパス', path: '/workstyle/career-path' },
    { title: '福利厚生', path: '/workstyle/benefits' }
  ],
  metadata: {
    updatedDate: '2026.02.04',
    readTime: '約5分'
  },
  hero: {
    type: 'simple',
    title: '社員アンケート',
    titleEn: 'Survey',
    description: 'このページでは、2025年に実施した全社アンケートの結果をもとに、FLARETECHで働くメンバーの声を、そのまま掲載しています。どんな想いで入社し、日々どんなことを感じながら働いているのか。良いところも、課題も含めて、数値とコメントの両方から、FLARETECHという組織の「今」と「これから」をご覧ください。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'quote',
      data: {
        text: '判断に迷ったとき、この3つに当てはめると答えが出ます。',
        author: '採用担当',
        role: ''
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/0017.jpg',
        alt: 'チームディスカッション - オープンな対話の様子',
        caption: '',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '1. FLARETECHへの応募理由',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '代表的なコメント',
        content: '<ul><li>「挑戦・誠実・共創という価値観に共感できた」</li><li>「エンジニアの市場価値を本気で高めようとしている会社だと感じた」</li><li>「SESだけど、キャリアや成長をちゃんと考えてくれると思った」</li><li>「働き方や人間関係が柔軟で、無理をしなくていいと感じた」</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '読み解き',
        content: '<p>応募理由として多く挙げられたのは、<strong>「会社の考え方・価値観への共感」と「成長できそうだという期待」</strong>でした。</p><p>特に目立ったのは、「SESという形態でありながら、人を使い捨てにしない姿勢が感じられた」という声です。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '2. 最終的な入社の決め手',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '代表的なコメント',
        content: '<ul><li>「選考や面談で、誠実に話を聞いてもらえた」</li><li>「現場のリアルな話を包み隠さず教えてくれた」</li><li>「条件だけでなく、将来のキャリアについて一緒に考えてくれた」</li><li>「"誰と働くか"がイメージできた」</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '読み解き',
        content: '<p>入社の決め手として最も多かったのは、<strong>人との接点で感じた信頼感</strong>でした。</p><p>条件や制度よりも、「この人たちとなら、ちゃんと向き合って働けそうか」という感覚が、意思決定を後押ししています。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '3. 入社後に感じたFLARETECHの良さ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '代表的なコメント',
        content: '<ul><li>「相談できる人がいる安心感がある」</li><li>「1on1やフォローがあり、放置されない」</li><li>「エンジニアを"人"として見てくれている」</li><li>「表彰や発信など、頑張りを見てもらえる文化がある」</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '読み解き',
        content: '<p>入社後に最も評価されているのは、<strong>人間関係の安心感と支援の手触り感</strong>です。</p><p>特に、「SESでは孤立しがちだと思っていたが、そうではなかった」という声が多く寄せられました。</p>'
      }
    },
    {
      type: 'quote',
      data: {
        text: '地味なことをちゃんとやってる人が、一番信頼されてます。',
        author: 'エンジニアリーダー',
        role: ''
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/0062.jpg',
        alt: '1on1ミーティング - 支援とフォローアップ',
        caption: '',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '4. 入社後に感じた課題',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '代表的なコメント',
        content: '<ul><li>「入社直後の情報が多く、整理が追いつかなかった」</li><li>「ナレッジや制度がどこにあるか分かりづらい」</li><li>「評価や成長の基準が、もう少し明確だと嬉しい」</li><li>「勉強会や取り組みに参加したくても、現場都合で難しいことがある」</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '読み解き',
        content: '<p>課題として多く挙げられたのは、<strong>オンボーディング・情報整理・評価の見えやすさ</strong>です。</p><p>組織が成長する中で、「仕組みのアップデートが追いついていない部分」が正直に指摘されています。</p><p>FLARETECHでは、これらの声をもとに制度・情報設計の改善を継続しています。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '5. 入社後に感じたギャップ',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '代表的なコメント',
        content: '<ul><li>「思っていたよりも、優しくて話しやすい人が多かった」</li><li>「チームや現場によって、温度感に差がある」</li><li>「変化のスピードが速く、自分の軸が必要だと感じた」</li><li>「想像以上に自由度が高かった」</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '読み解き',
        content: '<p>ギャップとしては、<strong>良い意味での"柔らかさ"</strong>と、現場・チームごとの違いが挙げられています。</p><p>画一的な組織ではない分、自分なりのスタンスを持つことが求められる環境とも言えます。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '6. FLARETECHはどんな「雰囲気・カルチャー」の会社？',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '代表的なコメント',
        content: '<ul><li>「オープンで、上下関係が強すぎない」</li><li>「個人を尊重しながら、チームで成果を出そうとしている」</li><li>「挑戦を止めない雰囲気がある」</li><li>「誠実さを大事にしている人が多い」</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '読み解き',
        content: '<p>多くの声に共通していたのは、<strong>挑戦・誠実・共創というバリューが、実際の行動や空気感に表れている</strong>という点です。</p><p>一方で、「だからこそ、自分もちゃんと向き合わないといけない」という緊張感も、同時に存在しています。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/0073.jpg',
        alt: 'オフィス環境 - オープンで協働的な雰囲気',
        caption: '',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'FLARETECHの今と、これから',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>アンケート結果から見えてきたのは、FLARETECHが「人に向き合う会社」であろうとしている、という事実です。</p><p>理念や方向性は、少しずつ共有されてきている。<br>人間関係やサポート体制への評価も、確かにある。<br>一方で、成長フェーズならではの未整備な部分も、まだ残っています。</p><p>私たちは、この「良いところ」と「足りないところ」の両方から目を逸らさず、より安心して挑戦できる組織へと進化していきます。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>アンケートに映っているのは、完成された理想の会社ではありません。</p><p>試行錯誤の途中で、うまくいっていることも、課題も、すべて抱えながら前に進んでいる組織の姿です。</p><p>それでもなお、この環境で挑戦したいと思えるか。<br>この人たちと、時間を重ねたいと思えるか。</p><p>このページが、あなた自身の判断の材料になっていれば幸いです。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'FLARETECHの中身をもう少し見てみる',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの価値観や事業内容について、さらに詳しく知りたい方は <a href="/about">Aboutページ</a> をご覧ください。</p>',
        alignment: 'center'
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const workstylePxPageData = {
  slug: '/workstyle/px',
  title: 'Organization Design',
  titleEn: 'People Experience',
  subtitle: 'FLARETECHの組織デザイン',
  category: 'workstyle',
  site: 'career',
  metaTitle: 'Organization Design | FLARETECH採用サイト',
  metaDescription: 'FLARETECHの組織デザイン、SES事業における「安心」「成長」「つながり」を運にしない仕組みをご紹介します。',
  metaKeywords: [],
  publishedAt: '2025-01-26',
  showToc: true,
  tocLinks: [
    { title: '社員アンケート', path: '/workstyle/survey' },
    { title: 'Organization Design', path: '/workstyle/px' },
    { title: 'オンボーディング', path: '/workstyle/onboarding' },
    { title: '育成・学習機会', path: '/workstyle/development' },
    { title: '評価と報酬', path: '/workstyle/evaluation' },
    { title: 'キャリアパス', path: '/workstyle/career-path' },
    { title: '福利厚生', path: '/workstyle/benefits' }
  ],
  metadata: {
    updatedDate: '2026.02.04',
    readTime: '約8分'
  },
  hero: {
    type: 'simple',
    title: 'Organization Design',
    titleEn: 'FLARETECHの組織デザイン',
    description: 'このページでは、FLARETECHが大切にしている「人の体験を、運用で守る」という考え方と、具体的な取り組みをご紹介します。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>制度の"豪華さ"を語りたいのではありません。<br>SESという事業の構造上、体験が現場に引っ張られやすいからこそ、<br>「安心」「成長」「つながり」を運にしないために、会社として何を設計しているのか。<br>その前提を共有するためのページです。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '私たちが「人」をどう捉えているか',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの中心にあるのは、いつも「人」です。ここでいう「人」とは、スキルだけではありません。</p><ul><li>どんな現場で、どんな経験を積めているか</li><li>今、何が不安で、何が前に進む力になるか</li><li>何があれば成長できるか／折れてしまうか</li></ul><p>SESでは、現場や環境が分散し、体験に差が出ます。だから私たちは、<strong>「良い現場に当たれば成長できる」</strong>を前提にしません。体験の差はゼロにできなくても、「最低ラインの安心」と「成長の導線」は会社側で揃える。それがFLARETECHの考え方です。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/px-people-philosophy.jpg',
        alt: 'チームメンバーが協力している様子',
        caption: '「人」を中心に据えた組織づくり'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '体験から制度を生み、声で改善する',
        alignment: 'left',
        background: 'white'
      }
    },

    {
      type: 'richText',
      data: {
        content: '<p>制度は、作って終わりでは意味がありません。使われて、声が出て、改善されて初めて機能します。</p><p>FLARETECHでは、次のループで運用を回します。</p> '
      }
    },
    {
      type: 'imageText',
      data: {
        layout: 'imageLeft',
        image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/homepage/icons/icon_pdca.png',
        imageAlt: '構造化イメージ',
        content: '<p><strong>声（実体験） → 構造化（原因） → 仕組み化（運用） → 検証（サーベイ/面談） → 改善</strong></p><p>例えば、「相談先が分からない」「成長実感が薄い」「評価の軸が見えない」といった声は、個人の努力不足ではなく <strong>構造の問題</strong>として扱います。だからこそ、キャリア面談・オンボーディング・評価の明文化・学習支援などを、"点"ではなく"線"でつなげて設計しています。</p>'
      },
    },
    {
      type: 'sectionTitle',
      data: {
        title: '体験設計を支える役割（エンジニアリーダー・採用・経営）',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>現場が分散するからこそ、会社側には「戻れる場所」が必要です。</p><ul><li><strong>エンジニアリーダー</strong>：現場と切り離した1on1で、個人の成長と選択肢を一緒に設計する</li><li><strong>教育推進</strong>：学びを個人任せにせず、資格取得や勉強会を会社の導線として用意する</li><li><strong>採用/人事</strong>：入社前後の不安を最小化し、立ち上がりを加速する</li><li><strong>経営</strong>：個人の成長と、会社の方向性がズレないように言語化する</li></ul><p>重要なのは、これが「管理」ではなく <strong>支援のインフラ</strong>であること。現場が違っても、会社の支援が切れない状態をつくります。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '代表的な取り組み',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '1) オンボーディング（不安→安心→自信）',
        content: '<p>入社初期に必要なのは、気合ではなく「迷わない導線」です。オンボーディングで重視しているのは次の3つです。</p><ul><li>立ち上がりを早くする（初期タスクと相談導線を明確にする）</li><li>カルチャーを体験する（言葉を"読ませる"のではなく"使える"状態にする）</li><li>仲間がいる安心感をつくる（孤独化させない接続を設計する）</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '2) サーベイ（良いところも、足りないところも可視化する）',
        content: '<p>「雰囲気が良い」は、測れないと再現できません。FLARETECHは、数値とコメントの両方で、現在地を見ます。改善は"気分"ではなく、"観測"から始めます。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '3) イベント（帰属を運ではなく設計で作る）',
        content: '<p>社員総会／表彰／交流会／サークル／ファミリーデー等。目的は一つです。現場が違っても「一人じゃない」と実感できる状態をつくること。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '4) 学習・成長支援（市場価値のための福利厚生）',
        content: '<p>書籍購入補助、資格支援、案件設計、面談。福利厚生を"娯楽"ではなく、市場価値の導線として設計します。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/px-initiatives.jpg',
        alt: 'FLARETECHの組織的な取り組みの様子',
        caption: '体験設計を支える様々な取り組み'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '多様な背景を前提にする働き方',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>人の体験は、人の数だけ違います。住む場所、家庭、体力、志向、過去の経験。違いを特別扱いせず、前提として受け止めるのがFLARETECHです。</p><p>大切なのは「自由」ではなく、成果が出る条件を揃えること。そのために、働き方の調整・相談導線・制度運用を整えています。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '最後に',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>私たちがつくりたいのは、「頑張れる人」だけが生き残る環境ではありません。</p><p><strong>迷わず前に進める人が増える環境です。</strong></p><p>それは、個人の根性ではなく、会社の運用で作れます。</p><p>この考え方が合うかどうか。まずは、すり合わせから始めてください。</p>'
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const workstyleOnboardingPageData = {
  slug: '/workstyle/onboarding',
  title: 'オンボーディング',
  titleEn: 'Onboarding',
  subtitle: 'Onboarding',
  category: 'workstyle',
  site: 'career',
  publishedAt: '2025-01-26',
  showToc: true,
  tocLinks: [
    { title: '社員アンケート', path: '/workstyle/survey' },
    { title: 'Organization Design', path: '/workstyle/px' },
    { title: 'オンボーディング', path: '/workstyle/onboarding' },
    { title: '育成・学習機会', path: '/workstyle/development' },
    { title: '評価と報酬', path: '/workstyle/evaluation' },
    { title: 'キャリアパス', path: '/workstyle/career-path' },
    { title: '福利厚生', path: '/workstyle/benefits' }
  ],
  metadata: {
    updatedDate: '2026.02.04',
    readTime: '約9分'
  },
  hero: {
    type: 'simple',
    title: 'オンボーディング',
    titleEn: 'Onboarding',
    description: 'FLARETECHのオンボーディングは、入社直後の"不安を減らすため"だけの仕組みではありません。早く仲間になじみ、早く成果を出し、長く成長できる状態に入ってもらうことを目的に、入社前から3ヶ月間を「オンボーディング期間」として設計しています。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'オンボーディングで大切にしていること',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>私たちがオンボーディングで特に重視しているのは、次の3つです。</p>'
      }
    },
    {
      type: 'attractionList',
      data: {
        items: [
          {
            number: '1',
            title: '立ち上がりを早くする',
            description: '「慣れるまで待つ」ではなく、最初の迷いを減らす。環境・ツール・ルール・相談先を先に整え、仕事に集中できる状態をつくります。'
          },
          {
            number: '2',
            title: '文化を"理解"ではなく"体験"する',
            description: '価値観は、読めば伝わるものではありません。日々の行動の中で体験し、言葉として使い、判断の基準として身につける。その入口をオンボーディングでつくります。'
          },
          {
            number: '3',
            title: '仲間がいる安心感をつくる',
            description: 'SES/リモート中心の働き方では、関係性が希薄になりやすい。だからこそ、最初に「相談できる人」と「つながれる場」を設計し、心理的安全性の土台をつくります。'
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/onboarding-team-welcome.jpg',
        alt: 'チームで新メンバーを歓迎する様子',
        caption: '新メンバーを温かく迎え入れる文化'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'オンボーディングの全体像（入社前〜3ヶ月）',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>オンボーディングは「研修を受けたら終わり」ではなく、ToDoをこなしながら、関係性と行動を揃えるプロセスです。</p><p>入社後3ヶ月の終わりに、オンボーディングのチェック項目を完了し、所定チャンネルへ報告して卒業します。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/onboarding-process.png',
        alt: 'オンボーディングプロセスの全体像',
        caption: '入社前から3ヶ月間のオンボーディングフロー'
      }
    },
    {
      type: 'timeline',
      data: {
        items: [
          {
            phase: '',
            title: '入社前',
            description: '不安を減らし、初日を迎える準備を整える<br><br>入社が決まったら、必要手続き・アカウント準備・初日の案内を順に進めます。「何を、いつまでに、どうやって」を可視化し、入社初日を"迷いなく迎えられる状態"にします。'
          },
          {
            phase: '',
            title: '入社初日',
            description: '環境を整え、最初の安心をつくる<br><br>入社初日は、仕事を急に始める日ではなく、安心して走り出すための初速を整える日です。<br><br>・アカウント／メール等の設定<br>・社内規定の確認（働き方・ルールの基本）<br>・ツール・システムの案内<br>・福利厚生・社内制度の説明<br>・ランチ／オリエンテーション（会社・文化の理解）<br><br>「入社直後に詰まりやすいポイント」を先に潰し、以降の学習と業務がスムーズになるよう設計しています。'
          },
          {
            phase: '',
            title: '入社1週間以内',
            description: 'まず"名前と顔"が一致する<br><br>最初の1週間は、スキルよりも先に関係性と基本行動を揃えます。<br><br>・Slackプロフィール設定<br>・自己紹介（社内プロフィール／社内発信）<br>・社内ルールや申請方法の理解<br>・"連絡が返ってくる状態"をつくる（相談経路の確保）<br><br>「困ったとき、誰に聞けばいいか分からない」を放置しないための期間です。'
          },
          {
            phase: '',
            title: '入社1ヶ月以内',
            description: '会社の"言語"をインストールする<br><br>このフェーズでは、日々の業務に必要な基礎に加えて、会社の思想・文化・情報源に触れ、判断基準を揃えていきます。<br><br>・オンライン交流会／ランチへの参加<br>・社内発信（記事・制度・活動）へのアクセス習慣<br>・1ヶ月後の目標設定と、進捗面談（1回目）<br><br>"知らないまま頑張る"をなくし、方向性のズレを早期に修正します。'
          },
          {
            phase: '',
            title: '入社2ヶ月以内',
            description: '小さな成果をつくり、伸び方を決める<br><br>ここからは「できること」を増やしながら、次の伸び方を具体化します。<br><br>・オンライン勉強会への参加<br>・2ヶ月目の目標達成<br>・進捗面談（2回目）<br><br>学習・実務・コミュニケーションのバランスを取り、再現性ある成長ルートに乗せます。'
          },
          {
            phase: '',
            title: '入社3ヶ月',
            description: 'オンボーディング卒業<br><br>3ヶ月は、関係性と基本行動が揃い、「自分の力で前に進める」状態に入る節目です。<br><br>・3ヶ月目の目標設定・振り返り<br>・チェックリスト完了報告<br>・次の成長計画へ接続（通常運用へ移行）'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'メンター制度：新メンバーに"相談の固定席"をつくる',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>オンボーディング期間中、新メンバーには先輩社員のメンターがつきます。</p><ul><li>月1回30分の進捗面談</li><li>担当は最大5名/人を目安（負荷を設計）</li><li>面談内容はレポート化し、必要に応じて関係者がフォロー</li></ul><p>メンター制度の狙いは、「教える」だけではありません。<br>入社者の不安を早期に回収し、詰まりを構造的に解消するための仕組みです。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/mentor-meeting.jpg',
        alt: 'メンターと新メンバーの1on1面談',
        caption: '月1回の進捗面談で成長をサポート'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'オンボーディングは"共につくるもの"',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>オンボーディングに完成形はありません。<br>実際に入社して初めて見える詰まりがあり、働き方や組織の変化で前提も変わります。</p><p>だからFLARETECHでは、オンボーディングを「与えるもの」ではなく、より良くする前提で運用し、改善していくものとして扱います。</p><p>あなたが入社後に感じた違和感や提案は、次に入ってくる誰かの安心と成長に、直接つながっていきます。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '入社初期に、うまく馴染む人の共通点',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>最後に、入社初期の立ち上がりが早い人に共通している行動を、あえて言語化します。</p><ul><li>分からないことを、抱え込まずに相談できる</li><li>ただし、丸投げせず「自分なりの仮説」を持って聞ける</li><li>交流の機会を、待たずに自分から取りに行く</li><li>会社の言語（価値観・行動原理）を、日常の判断に使っている</li></ul><p>オンボーディングは、環境側の設計で半分が決まります。<br>残り半分は、あなた自身が「どう関わるか」で決まります。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>オンボーディングは、会社が用意する。<br>立ち上がりは、あなたと一緒につくる。</strong></p><p>FLARETECHが大切にしているのは、入社後に「無理に頑張れる人」ではありません。</p><p>入社後に、迷わず前に進める人を、仕組みで増やすこと。</p><p>だからこそ、入社前の段階で、きちんとすり合わせたいと考えています。</p><p>まずは、あなたのこれまでと、これからを聞かせてください。<br>この環境が合うかどうかを、先に確認しましょう。</p>'
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const workstyleDevelopmentPageData = {
  slug: '/workstyle/development',
  title: '育成・学習機会',
  titleEn: 'Development',
  subtitle: 'Development',
  category: 'workstyle',
  site: 'career',
  publishedAt: '2025-01-26',
  showToc: true,
  tocLinks: [
    { title: '社員アンケート', path: '/workstyle/survey' },
    { title: 'Organization Design', path: '/workstyle/px' },
    { title: 'オンボーディング', path: '/workstyle/onboarding' },
    { title: '育成・学習機会', path: '/workstyle/development' },
    { title: '評価と報酬', path: '/workstyle/evaluation' },
    { title: 'キャリアパス', path: '/workstyle/career-path' },
    { title: '福利厚生', path: '/workstyle/benefits' }
  ],
  metadata: {
    updatedDate: '2025.01.26',
    readTime: '約5分'
  },
  hero: {
    type: 'simple',
    title: '育成・学習機会',
    titleEn: 'Development',
    description: 'FLARETECHの育成や学習支援に関する考え方と、具体的な制度をご紹介します。私たちは「人の成長こそが事業の源泉」という想いを大切にし、社員が自らキャリアをデザインできるように、多様な学びの機会を提供しています。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHの育成に対する考え方', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: 'テクノロジーという、人の行動や感情を扱うFLARETECHのビジネスにとって「人の成長」は欠かせません。代表自身が「人の成長を見届けたい」という強い想いを持ち続けており、これまでさまざまな育成や学習の仕組みを整えてきました。\n\nただし、会社が制度の利用を強制することはありません。成長とは会社が与えるものではなく、本人の意志によって掴み取るものだと考えているからです。会社としてはできる限りの成長支援は用意しますが、本人に強い意志がなければ、どんなに優れた仕組みを整えても、社員の本当の成長にはつながりません。\n\nだからこそFLARETECHは「自ら学び、成長しようとする姿勢」を持っていることを前提とした上で、社員たちが挑戦できる環境を整えようとしています。'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '支援制度', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        subtitle: '制度の全体像',
        content: 'FLARETECHでは、知識やスキルを学ぶ場と、それを実践する機会の両方を大切にしています。一人ひとりが自ら学び、挑戦する姿勢を持てるように、学びの段階に応じて4つの軸で制度を設計しています。'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '1. 知識インプット', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        subtitle: '本読み放題',
        content: '業務や自己成長に役立つ書籍を購入できる制度で、専門書や海外書籍など、個人では手に取りにくい本も対象です。過去に購入された書籍は会社で保管されており、誰でも自由に借りることができます。それぞれの関心や課題に合わせて学びを深められる環境をつくっています。'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '資格取得奨励金制度',
        content: '専門スキルの証明となる資格の取得を推奨し、受験費用を会社が負担しています。専門性の強化や新しい領域への挑戦を後押しし、メンバーが自らのキャリアを広げていけるよう支援しています。'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '社内ナレッジ',
        content: 'FLARETECHでは、社内の過去の知見にアクセスできる環境を整えています。社内ではConfluenceやNotionなどのツールを活用し、知見やノウハウを日々蓄積しています。プロジェクトの事例から実務に役立つフレームワークまで、知見を個人の中だけにとどめず組織全体で共有する文化が根づいています。'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '社内勉強会',
        content: '部門主導の社内勉強会も盛んに行われています。定期開催されるものとしては、エンジニアが中心となる技術共有会や、デザイナーによる作品発表会などが代表的です。また、新技術や特定テーマを扱う勉強会も不定期で開催されており、社員同士が実務に直結する知見を気軽に共有できる場になっています。'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '2. 実践的な学び', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        subtitle: 'ジョブチャレンジ（社内公募）',
        content: '社内で挑戦したい案件やポジションに応募できる制度です。異動の対象はその時点で募集している職種やプロジェクトに限られますが、「新しい領域に挑戦したい」「別のチームで経験を積みたい」といった意思を伝えることでキャリアの可能性を広げられます。なお、異動の可否は事業状況や本人の貢献度などを踏まえて総合的に判断されます。'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '3. 事業・経営へのチャレンジ', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        subtitle: '新規事業はいつでも・誰でも提案OK',
        content: 'FLARETECHでは、代表にいつでも新規事業の提案や相談ができます。会社として公式な支援制度はありませんが、これは代表の「事業を生み出す人間は、制度の有無に関わらず動く」という起業家としての経験によるものです。経営陣による壁打ちの上で、見込みがあると判断されれば、主に予算策定のタイミングで投資の計画を行います。'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: 'パートナー企業への出向',
        content: 'パートナー企業に出向し、異なる環境で経験を積むことができる制度です。成果や適性が認められた社員を抜擢し、新しいフィールドでの挑戦を後押ししています。'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '4. ミドルマネジメント支援', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: 'ミドルマネジメント層に向けても、組織の中でリーダーシップを発揮できるような支援を用意しています。\n\n正式なマネージャー登用前には、半年〜1年の「ポテンシャルマネージャー期間」を設けています。この期間中に、主体性やセルフマネジメント力を磨く研修と、業務や人材マネジメントの基礎を体系的に学ぶ社内独自の研修を実施。各回の冒頭で現場課題を共有し、生々しい悩みや視点を議論し合うことで、少人数でインタラクティブに学びを深めていきます。こうした学びと実践を通じて、マネジメントの土台を固め、正式登用を目指します。'
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const workstyleEvaluationPageData = {
  slug: '/workstyle/evaluation',
  title: '評価と報酬',
  titleEn: 'Evaluation',
  subtitle: 'Evaluation',
  category: 'workstyle',
  site: 'career',
  publishedAt: '2025-01-26',
  showToc: true,
  tocLinks: [
    { title: '社員アンケート', path: '/workstyle/survey' },
    { title: 'Organization Design', path: '/workstyle/px' },
    { title: 'オンボーディング', path: '/workstyle/onboarding' },
    { title: '育成・学習機会', path: '/workstyle/development' },
    { title: '評価と報酬', path: '/workstyle/evaluation' },
    { title: 'キャリアパス', path: '/workstyle/career-path' },
    { title: '福利厚生', path: '/workstyle/benefits' }
  ],
  metadata: {
    updatedDate: '2026.02.03',
    readTime: '約6分'
  },
  hero: {
    type: 'simple',
    title: '評価と報酬',
    titleEn: 'Evaluation',
    description: 'このページでは、FLARETECHの評価・昇給・等級制度の考え方をお伝えします。私たちが重視しているのは、「評価で人を管理すること」ではありません。評価を、成長と市場価値を高めるための"設計"として機能させることです。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHの評価思想', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        subtitle: '評価は「現在地」を測り、「次の一歩」を決めるためにある',
        content: '<p>FLARETECHでは、評価を次のように定義しています。</p><p><strong>評価とは、これまでの積み重ねを振り返り、次にどこへ進むかを一緒に決めるための対話である。</strong></p><p>そのため、</p><ul><li>一時的な成果だけ</li><li>声の大きさや印象</li><li>年功や社歴</li></ul><p>では評価しません。</p><p>行動・スキル・顧客貢献という再現性のある要素を軸に、「成長しているか」「市場価値が高まっているか」を見ていきます。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/evaluation-philosophy.png',
        alt: 'FLARETECHの評価思想を表す図',
        caption: '評価は対話であり、成長のための設計'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'なぜ「評価」と「昇給」を分けているのか', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、<strong>評価（成長の確認）</strong>と<strong>昇給（役割・責任の反映）</strong>を分離しています。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '理由①：成長は、すぐに単価や給与に変わらない',
        content: '<p>スキルの習得や行動の変化が、顧客評価や単価向上に反映されるまでには時間差があります。</p><p><strong>評価＝成長の確認／昇給＝市場価値への反映</strong></p><p>と分けることで、成長に集中できる環境をつくっています。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '理由②：納得感のある対話を成立させるため',
        content: '<p>評価と昇給を一体にすると、</p><ul><li>評価が良い＝必ず昇給するはず</li><li>昇給がない＝否定された</li></ul><p>という誤解が生まれがちです。</p><p>分けることで、<strong>評価は成長の話、昇給は役割の話</strong>として整理できます。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '評価の3つの観点', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、以下の3軸で総合評価を行います。</p>'
      }
    },
    {
      type: 'attractionList',
      data: {
        items: [
          {
            number: '1',
            title: '行動（スタンス・姿勢）',
            description: '主体的に学び、挑戦しているか / 課題に対して向き合う姿勢があるか / チームや周囲に良い影響を与えているか'
          },
          {
            number: '2',
            title: 'スキル（技術・再現性）',
            description: '技術・設計・実装のレベル / 品質・安定性・説明力 / 他案件・他環境でも通用する力か'
          },
          {
            number: '3',
            title: '顧客貢献（価値提供）',
            description: '顧客の課題解決に寄与しているか / 信頼を積み重ねられているか / プロジェクトの価値を高めているか'
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/evaluation-criteria.png',
        alt: '評価の3つの観点を示す図',
        caption: '行動・スキル・顧客貢献の3軸で総合評価'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>技術だけでも、姿勢だけでも足りない。3つが揃って、初めて評価対象になります。</strong></p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '評価は「事実ベース」で行う', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの評価は、主観ではなく<strong>事実（ファクト）</strong>を重視します。</p><p><strong>評価に使われる例</strong></p><ul><li>実際に任された役割・責任</li><li>顧客やチームからの具体的なフィードバック</li><li>実装・設計・改善のアウトプット</li><li>指導・レビュー・ナレッジ共有の実績</li></ul><p>評価は「裁定」ではなく、次の成長を設計するための材料です。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '昇給について', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        subtitle: '昇給とは',
        content: '<p>等級内でのステップアップ（横の成長）／実施頻度：半年〜1年に1回（5月／11月）</p><p><strong>原則：年1回は昇給</strong>／特に経験の浅いフェーズでは、市場価値の伸びが大きいため、昇給幅も大きくなりやすい傾向があります。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: '昇給が見送られるケース',
        content: '<ul><li>勤怠・勤務態度に継続的な課題がある</li><li>待機期間が長期化している</li><li>スキルと希望条件に大きな乖離がある</li><li>プロジェクトでの改善が見られない</li></ul><p>※いずれも <strong>事前の対話とフィードバックが前提</strong>です。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '昇格（ランクアップ）について', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        subtitle: '昇格とは',
        content: '<p>等級テーブルの段を上がること（縦の成長）／役割・責任・期待値が大きく変わる</p><p><strong>判断基準</strong></p><ul><li>IT業務経験と技術領域</li><li>実務での再現性</li><li>会社・顧客への貢献度</li><li>等級に見合う行動と視座</li></ul><p>一部ランクでは、技能確認（試験）を行う場合がありますが、多くは日常の実績ベースで判断されます。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '等級制度（E〜Sランク）', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、以下の等級を設けています。</p><ul><li><strong>E</strong>：学習・微経験</li><li><strong>D</strong>：IT経験あり</li><li><strong>C</strong>：PG / SE</li><li><strong>B</strong>：ハイクラスSE / PL</li><li><strong>A</strong>：PM / コンサル</li><li><strong>S</strong>：スーパースター（技術×事業）</li></ul><p>それぞれに、</p><ul><li>必要スキル</li><li>求められる役割</li><li>市場価値の目安</li></ul><p>を明確に定義しています。</p><p>FLARETECHでは<strong>給与 ≒ エンジニアとしての市場価値</strong>と考えています。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/rank-system.png',
        alt: 'E〜Sランクの等級制度を示す図',
        caption: '等級制度（E〜Sランク）と市場価値の対応'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '評価を支えるコミュニケーション', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>評価は、半年に一度だけ行われるものではありません。</p><ul><li>定期面談（半期）</li><li>不定期の1on1</li><li>チャット・オンラインでの相談</li></ul><p>を通じて、評価は日常の延長線上で積み上がっていきます。</p><p>評価面談は、「初めて聞く場」ではなく、<strong>これまでの対話を整理する場</strong>です。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/evaluation-communication.jpg',
        alt: '評価面談やコミュニケーションの様子',
        caption: '日常の対話が評価につながる'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHが目指す評価文化', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHが増やしたいのは、入社後に「頑張れる人」ではなく、<strong>入社後に「迷わず成長できる人」</strong>です。</p><p>評価制度は、そのためのインフラです。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '最後に', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの評価と報酬は、完成された制度ではありません。</p><p>この仕組みをどう使い、どう対話し、どう成長につなげるか。</p><p>それを一緒につくっていける人と、私たちは働きたいと考えています。</p>'
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const workstyleCareerPathPageData = {
  slug: '/workstyle/career-path',
  title: 'キャリアパス',
  titleEn: 'Career Path',
  subtitle: 'Career Path',
  category: 'workstyle',
  site: 'career',
  publishedAt: '2025-01-26',
  showToc: true,
  tocLinks: [
    { title: '社員アンケート', path: '/workstyle/survey' },
    { title: 'Organization Design', path: '/workstyle/px' },
    { title: 'オンボーディング', path: '/workstyle/onboarding' },
    { title: '育成・学習機会', path: '/workstyle/development' },
    { title: '評価と報酬', path: '/workstyle/evaluation' },
    { title: 'キャリアパス', path: '/workstyle/career-path' },
    { title: '福利厚生', path: '/workstyle/benefits' }
  ],
  metadata: {
    updatedDate: '2026.02.03',
    readTime: '約7分'
  },
  hero: {
    type: 'simple',
    title: 'キャリアパス',
    titleEn: 'Career Path',
    description: 'FLARETECHでは、「キャリアは会社に与えられるものではなく、自分で設計するもの」という考え方を大切にしています。一方で、設計したキャリアを"実行可能なもの"にするための環境・選択肢・対話の仕組みを、会社として用意しています。このページでは、FLARETECHにおけるキャリアパスの考え方と、どのような成長ルートが描けるのかを具体的に紹介します。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: { title: 'キャリアパスの考え方', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのキャリア設計の前提は、明確です。</p><ul><li>キャリアの主役は、常に本人</li><li>会社は「機会」と「構造」を用意する</li><li>成果と姿勢が、次の選択肢をひらく</li></ul><p>私たちは、「年次」や「在籍年数」で役割を固定しません。</p><p>目の前の仕事に向き合い、成果と信頼を積み重ねた人には、次の挑戦肢が自然と開いていく設計です。</p><p>だからこそ、<strong>まずは 今いるポジションで成果を出すこと</strong> が、すべてのキャリアの起点になります。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/career-thinking.jpg',
        alt: 'キャリアについて考える様子',
        caption: 'キャリアの主役は本人、会社は機会と構造を提供'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '越境で広がるキャリアパス', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのキャリアパスは、職種・領域を越えることを前提に設計されています。</p><p>人の強みや志向は、時間とともに変化します。「一度決めた職種に縛られ続ける」必要はありません。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: 'キャリアの基本軸',
        content: '<p>FLARETECHには、大きく分けて2つの方向性があります。</p><p><strong>1. マネジメント</strong></p><p>チームや事業を率い、成果を最大化する役割</p><p>プロジェクトマネージャー／事業責任者／組織リーダー など</p><p><strong>2. スペシャリスト</strong></p><p>特定領域の専門性を磨き、価値を出し続ける役割</p><p>テックリード／リードエンジニア／専門特化人材 など</p><p>どちらか一方に固定されることはありません。タイミングや志向に応じて、行き来することも可能です。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/career-crossover.jpg',
        alt: '職種や領域を越えて成長する様子',
        caption: '職種・領域を越えるキャリア設計'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '職種別キャリアパス例', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        subtitle: 'エンジニア',
        content: '<p>FLARETECHのエンジニアは、<strong>「市場価値を高め続けること」</strong>を前提に育成されます。</p><ul><li>Web／クラウド／モダン技術への転向・強化</li><li>クライアントワークでの多様な案件経験</li><li>Techリード・PL・PMへのステップアップ</li><li>自社事業エンジニアとしての裁量ある開発</li></ul><p>技術を磨き続ける道も、事業やチームを牽引する道も、実力に応じて選択可能です。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: 'プロダクトマネージャー（PdM）',
        content: '<p>PdMは、プロダクトと事業の両立点を担うポジションです。</p><ul><li>クライアントワークでのPdMリード</li><li>複数プロジェクトの統括・意思決定</li><li>自社事業PdMとして事業責任に近い立場へ</li></ul><p>「作る」から「決める」へ。視座を上げていくキャリアが描けます。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        subtitle: 'セールス',
        content: '<p>FLARETECHのセールスは、売るだけで終わらないキャリアを描けます。</p><ul><li>インサイド／フィールド／アカウントセールス</li><li>事業開発・アライアンス領域への越境</li><li>マーケティング・PdM・事業責任者への転向</li></ul><p>クライアント課題を起点に、事業づくりまで関われるのが特徴です。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'キャリア面談・対話の場', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>制度だけでなく、キャリアを言語化するための対話を重視しています。</p><ul><li>定期面談（半期・通期）</li><li>不定期のキャリア相談</li><li>経営層への希望共有</li></ul><p>「考えているだけ」で終わらせず、現実的な次の一手に落とすための場です。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/career-interview.jpg',
        alt: 'キャリア面談の様子',
        caption: 'キャリアを言語化し、次の一手を決める対話'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'クライアントワークならではのキャリアメリット', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの成長環境の特徴は、ここにあります。</p>'
      }
    },
    {
      type: 'attractionList',
      data: {
        items: [
          {
            number: '1',
            title: '短期間で多くの案件を経験できる',
            description: '事業会社のように同じプロダクトに長く関わるのと異なり、短期間でさまざまな業界や課題に触れることができます。短いサイクルで実践経験を積み重ねられるため、成長スピードが早くなります。'
          },
          {
            number: '2',
            title: '特定領域を繰り返し深掘りできる',
            description: '多様な案件に携わる一方で、同じ業界や領域を何度も担当することで、業界構造やユーザー理解が深まっていきます。その結果、「この分野ならこの人に」と特定領域に強みを持てるようになります。'
          },
          {
            number: '3',
            title: '社内に専門家が多く、学び合える',
            description: '社内には、特定領域に強みを持つメンバーが数多く在籍しています。Slack上での相談やナレッジ共有会など、知見を共有する文化が根づいているため、悩みを抱え込まずに相談できる環境があります。'
          },
          {
            number: '4',
            title: '制約下で意思決定力が鍛えられる',
            description: 'クライアントワークでは、限られた期間やリソースの中で成果を出すことが求められます。何を優先し、どうクオリティを担保するかを判断する力が磨かれ、仕事の精度やスピード、プロとしての視座を高めることができます。'
          }
        ]
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>結果として、「どこでも通用する人材」ではなく、<strong>「市場で評価される人材」</strong>へと成長できます。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'キャリアパスにおける制約', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>正直にお伝えします。</p><p>FLARETECHは、希望すればすべてが即叶う会社ではありません。</p><ul><li>クライアント契約による制約</li><li>案件・タイミングの都合</li><li>組織バランスの調整</li></ul><p>ただし、制約がある中でも <strong>対話と調整を続ける</strong> ことを約束します。</p><p>キャリアは一度で決めるものではありません。時間をかけて、現実的に前に進むものだと考えています。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '最後に', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHが提供したいのは、「正解のキャリアパス」ではありません。</p><p>自分で考え、自分で選び、自分で積み上げていく。</p><p>そのための <strong>余白と構造</strong> です。</p><p>この環境で、あなたはどんなキャリアを描きたいですか。</p>'
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const workstyleBenefitsPageData = {
  slug: '/workstyle/benefits',
  title: '福利厚生',
  titleEn: 'Benefits',
  subtitle: 'Benefits',
  category: 'workstyle',
  site: 'career',
  publishedAt: '2025-01-26',
  showToc: true,
  tocLinks: [
    { title: '社員アンケート', path: '/workstyle/survey' },
    { title: 'Organization Design', path: '/workstyle/px' },
    { title: 'オンボーディング', path: '/workstyle/onboarding' },
    { title: '育成・学習機会', path: '/workstyle/development' },
    { title: '評価と報酬', path: '/workstyle/evaluation' },
    { title: 'キャリアパス', path: '/workstyle/career-path' },
    { title: '福利厚生', path: '/workstyle/benefits' }
  ],
  metadata: {
    updatedDate: '2026.02.03',
    readTime: '約10分'
  },
  hero: {
    type: 'simple',
    title: '福利厚生',
    titleEn: 'Benefits',
    description: 'このページでは、FLARETECHの福利厚生・働く環境の考え方を紹介します。私たちが整えたいのは、制度の"豪華さ"ではありません。安心して働けること（生活が崩れない）、成長できること（市場価値が上がる）、孤独にならないこと（相談できる）。現場が多様なSESだからこそ、この3つは「運」ではなく、会社の設計で守ります。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/benefits-1.jpg',
        alt: 'FLARETECHの働く環境'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '一人ひとりが、自分に合った働き方を選べる', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHには、さまざまな働き方のメンバーがいます。常駐／リモート／ハイブリッド。生活事情も、キャリアの志向も違う。</p><p>だから私たちは、「働き方を統一する」のではなく、<strong>成果が出る条件を揃える</strong>方を選びます。制度は、そのための"道具"です。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/systems/2502050617.jpg',
        alt: '多様な働き方を実現するメンバーたち',
        caption: '一人ひとりに合った働き方を選べる環境'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHの働き方のリアル', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>「SESって、結局きついのでは？」そう思われるのも自然です。現場によって体験が変わるからです。</p><p>私たちが大切にしている前提は、次の3つです。</p>'
      }
    },
    {
      type: 'attractionList',
      data: {
        items: [
          {
            number: '1',
            title: '現場が違っても、会社の支援は切らさない',
            description: '困ったときに"戻れる場所"を用意する（面談・相談導線・学習支援）'
          },
          {
            number: '2',
            title: '成長の偶然を減らし、設計で増やす',
            description: '案件選び／育成導線を意図的に設計'
          },
          {
            number: '3',
            title: '福利厚生は、この前提を日常で成立させるための仕組み',
            description: '制度があるだけでなく、運用され、使われ、改善される'
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/values/values-1.jpg',
        alt: 'FLARETECHの働き方の様子',
        caption: '現場が違っても、会社の支援は切らさない'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '働き方・休暇', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>休暇制度</h3><p>安心して休み、また戻ってこられる状態をつくるための制度です。</p><ul><li><strong>対象：</strong>全社員</li><li><strong>申請：</strong>社内規程に準拠</li><li><strong>注意：</strong>有給休暇は法定。特別休暇（慶弔・感染症等）の運用は社内規程に準拠します</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>勤務時間・出社頻度</h3><p>勤務時間・出社頻度は案件条件により異なります。ただし私たちは、「案件都合で生活が壊れる」状態を良しとしません。</p><p>稼働や働き方に違和感が出たら、エンジニアリーダー／営業／上長が早期に入ります。</p><ul><li><strong>対象：</strong>全社員</li><li><strong>注意：</strong>働き方は案件ごとに異なります</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>子育て・介護との両立</h3><p>ライフイベントは、仕事の"邪魔"ではなく人生の一部です。両立のために必要なのは、気合ではなく設計です。</p><ul><li><strong>対象：</strong>全社員</li><li><strong>注意：</strong>育児・介護に関する制度は、法定制度および社内規程に準拠します</li></ul>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/benefits-family.png',
        alt: '仕事と家族・育児を両立する様子',
        caption: 'ライフイベントと仕事を両立できる環境'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>引越費用補助</h3><p>指定条件を満たす方に向けた、引越費用の補助制度です。生活の立ち上げで無理をしてほしくない。環境の変化を理由に、挑戦を諦めてほしくない。そのための制度です。</p><ul><li><strong>対象：</strong>社内規程に準拠</li><li><strong>申請：</strong>申請前に必ず条件をご確認ください</li><li><strong>注意：</strong>家賃補助ではありません</li></ul>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '成長・キャリア支援（FLARETECH福利厚生の中核）', alignment: 'left' }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/career-1.jpg',
        alt: '成長とキャリア支援の環境'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>福利厚生の中心は、娯楽ではなく<strong>市場価値</strong>です。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/benefits-growth.jpg',
        alt: '成長とキャリア開発をサポートする環境',
        caption: '福利厚生の中心は市場価値の向上'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>書籍購入補助</h3><p>ITエンジニアのスキルアップにつながる書籍購入を支援します。知識が充実することで、仕事の質が上がり、個人と会社の成長に直結します。</p><ul><li><strong>対象：</strong>ITエンジニアのスキルアップにつながる書籍</li><li><strong>申請：</strong>書籍購入補助申請フォーム（社内フォーム）</li><li><strong>注意：</strong>申請前に制度概要を必ず確認してください</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>資格取得支援</h3><p>推奨資格の合格者を対象に、受験費用を会社が負担します。「資格取得」がゴールではなく、実務で再現性のある力に変えることを目的にしています。</p><ul><li><strong>対象：</strong>社内の推奨資格一覧に準拠</li><li><strong>申請：</strong>合格後、合格証明書＋領収書を添付して精算申請</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>エンジニアリーダーによる1on1</h3><p>現場と切り離した場で、エンジニアリーダーと「次に何を伸ばすか」を一緒に決めます。「何をやるか」だけでなく、次の成長につながる経験を設計する場です。</p><ul><li><strong>対象：</strong>全エンジニア</li><li><strong>頻度：</strong>定期（半期・通期などのタイミング）／不定期（状況に応じて随時）</li></ul>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'ベネフィットステーション', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>育児・介護・学び・スポーツ・健康・レジャー・グルメ・ショッピングなど、生活の選択肢を広げる福利厚生サービスです。</p><ul><li><strong>対象：</strong>希望者にアカウントを付与します</li><li><strong>申請：</strong>入社後、社内ポータルからご案内します</li></ul>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'コミュニケーション（孤独にしない仕組み）', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>SESは、現場が違うほど横のつながりが薄くなります。だから会社が、意図的に"つながる機会"を設計します。</p><ul><li>オンライン／オフライン交流会</li><li>サークル活動（任意参加）</li><li>オンボーディング施策（入社初期の接続支援）</li></ul>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/px-initiatives.jpg',
        alt: 'チームメンバーが交流している様子',
        caption: '孤独にしない、つながりを設計する'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'すべての社員に公平な制度を', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>現場が違う。住む場所が違う。経験値も違う。それでも、FLARETECHが揃えるべきものは揃えます。</p><ul><li>相談の導線（困ったらどこに行けばいいか）</li><li>成長の機会（学習・案件設計・フィードバック）</li><li>つながり（孤独化させない）</li></ul><p>福利厚生とは、会社の思想を"運用"に落とすことです。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '社員の声', alignment: 'left' }
    },
    {
      type: 'quote',
      data: {
        text: '資格は"武器"。案件設計や提案の幅が明らかに変わります。',
        author: '中釜 直樹',
        role: '営業部'
      }
    },
    {
      type: 'quote',
      data: {
        text: '福利厚生は甘やかしではなく、挑戦を続けるためのインフラだと思っています。',
        author: '高木 界',
        role: '取締役'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/closing-1.jpg',
        alt: 'FLARETECHで働く仲間たち'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>最後に<br><br>福利厚生は、制度があるだけでは成立しません。運用され、使われ、改善されて初めて"安心"になります。</p><p>この環境が合うかどうかは、あなたの状況と私たちの設計が噛み合うかで決まります。</p><p>だからまずは、すり合わせから始めてください。</p>'
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const workstyleSystemsPageData = {
  slug: '/workstyle/systems',
  title: '制度関連',
  titleEn: 'Growth Support & Systems',
  subtitle: 'テクノロジーと対話で、個人の成長と組織の生産性を最大化する',
  category: 'workstyle',
  site: 'career',
  publishedAt: '2026-03-02',
  showToc: true,
  tocLinks: [
    { title: '社員アンケート', path: '/workstyle/survey' },
    { title: 'Organization Design', path: '/workstyle/px' },
    { title: 'オンボーディング', path: '/workstyle/onboarding' },
    { title: '育成・学習機会', path: '/workstyle/development' },
    { title: '評価と報酬', path: '/workstyle/evaluation' },
    { title: 'キャリアパス', path: '/workstyle/career-path' },
    { title: '福利厚生', path: '/workstyle/benefits' },
    { title: '制度関連', path: '/workstyle/systems' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、新入社員がスムーズに環境に馴染み、早期に活躍できるよう、データと対話を融合させた独自のサポート体制を整えています。このページでは、入社から日々の業務に至るまでの具体的なサイクルをご紹介します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-1',
        title: '1. オンボーディング「FLARETECHコンプリートへの道」',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、入社から3ヶ月間を「オンボーディング期間」と位置づけ、文化や働き方に馴染むためのプログラムを提供しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>3ヶ月間の成長ロードマップ</h3><p>「FLARETECHコンプリートへの道」と題したプログラムでは、Slackの設定からリモートランチ会の開催、オンライン勉強会への参加まで、フェーズに合わせたチェックリストを消化していきます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'phaseTimeline',
      data: {
        phases: [
          {
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/systems/2502050617.jpg',
            phase: '入社当日〜1週間以内',
            title: '基盤構築',
            description: 'まずは環境を整え、社内の仲間に顔と名前を覚えてもらうことからスタートします。',
            points: [
              {
                label: 'ツールのセットアップ：',
                text: 'PC設定、Slack、ourly（社内報）など'
              },
              {
                label: 'ルールの理解：',
                text: '社内規定、経費や有休・代休の申請方法、福利厚生について正しく理解する。'
              },
              {
                label: '目標設定：',
                text: '今後3ヶ月間の月ごとの目標を策定する。'
              }
            ]
          },
          {
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/systems/0089.jpg',
            phase: '入社1ヶ月以内',
            title: '社内交流の開始',
            description: '環境に慣れ始めると同時に、自ら進んでコミュニケーションの輪を広げます。',
            points: [
              {
                label: '目標達成と振り返り：',
                text: '初月の目標を達成し、メンターとの進捗面談を実施してフィードバックを受ける。'
              }
            ]
          },
          {
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/systems/0062.jpg',
            phase: '入社2ヶ月以内',
            title: '知識の習得と実践',
            description: '業務や社内活動への関わりを深め、技術や文化への理解を一段階引き上げます。',
            points: [
              {
                label: '勉強会への参加：',
                text: '社内勉強会に参加し、最新トレンドや知見を吸収する。'
              },
              {
                label: '継続的な成果：',
                text: '2ヶ月目の目標を達成し、引き続きメンターとの面談でネクストアクションを明確にする。'
              }
            ]
          },
          {
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/systems/0064.jpg',
            phase: '入社3ヶ月以内',
            title: 'FLARETECHコンプリート',
            description: '3ヶ月間の集大成として、全社的なイベントへの参加やアウトプットを行います。',
            points: [
              {
                label: '全社イベントへの参加：',
                text: '社員総会やオンライン交流会に参加し、組織の一体感を体感する。'
              },
              {
                label: '情報発信：',
                text: '自らourlyで記事を投稿し、社内へ向けてアウトプットを行う。'
              },
              {
                label: '最終面談と卒業：',
                text: '3ヶ月目の目標を達成し、最終の進捗面談を終えることでオンボーディングを「卒業」します。'
              }
            ]
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-2',
        title: '2. メンター制度：入社3ヶ月間の集中サポート',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>新入社員が抱く「誰に相談すればいいかわからない」という不安を解消し、安心してキャリアをスタートできるよう、FLARETECHでは先輩社員がメンターとして伴走する体制を整えています。</p><p>このメンター制度では、入社月から3ヶ月後までの計4回、月に一度（1回30分）の定期的な面談を実施します。面談の場では、設定した目標の進捗確認やチェックリストの実践状況の共有、次の一歩となるネクストアクションの決定、さらには日々の困りごとの相談など、メンティー（新入社員）が主体となって対話を進めていくのが特徴です。</p><p>この取り組みの目的は、単なる業務の進捗確認にとどまりません。会社の文化をいち早く理解し、先輩や同期とのつながりを深めることで、何でも気兼ねなく相談できる環境を自ら作り上げていくことを大切にしています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/systems/2502050457.jpg',
        alt: 'メンター制度',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-3',
        title: '3. 目標管理（MBO）：バリューとキャリアの紐付け',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの目標管理は、会社のビジョン・バリューと個人のキャリア目標をすり合わせるプロセスです。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>バリューに則った目標設定</h3><p>オンボーディング期間中の目標は、当社の3つのバリューに基づいて設定します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'valueImages',
      data: {
        values: [
          {
            title: '挑戦',
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/1.png',
            alt: '挑戦'
          },
          {
            title: '誠実',
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/2.png',
            alt: '誠実'
          },
          {
            title: '共創',
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/3.png',
            alt: '共創'
          }
        ]
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>挑戦：</strong> 「資格を取る」「自ら手を挙げてタスクを巻き取る」など。</p><p><strong>誠実：</strong> 「ミスはすぐに報告する」「タスクを責任持ってやり切る」など。</p><p><strong>共創：</strong> 「社内記事を投稿する」「サークルに入る」「Slackで発信する」など。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>運用サイクル</h3><p>期初に上長と方向性を合意し、隔月の1on1で進捗を確認、四半期ごとに振り返りを行うことで、状況に応じた目標の微修正や継続的なフィードバックを可能にしています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-4',
        title: '4. 1on1：AI支援システムによる継続的な対話',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHでは、オンボーディング期間が終了した後も、定期的な1on1を実施しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/workstyle/systems/1on1.png',
        alt: '1on1 AI支援システム',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};


// ============================================
// NEW GRADUATE PAGES
// ============================================

export const newGraduateAboutSummaryPageData = {
  slug: '/newgraduate/about/summary',
  title: 'FLARETECHを知るための、最短ルート。',
  titleEn: 'Recruit Overview',
  subtitle: '',
  category: 'about',
  site: 'newgraduate',
  publishedAt: '2026-03-02',
  showToc: true,
  tocLinks: [
    { title: '新卒向け：すぐ分かるFLARETECH', path: '/newgraduate/about/summary' },
    { title: '1ページで考えるFLARETECH', path: '/about/summary' },
    { title: 'FLARETECHの事業', path: '/about/business' },
    { title: '入社前に知ってほしい前提', path: '/about/prerequisites' },
    { title: 'AIと働くFLARETECHの現在地', path: '/about/ai' },
    { title: 'FLARETECHの物語', path: '/about/story' },
    { title: '私たちの進む方向', path: '/about/future' },
    { title: '企業の体験価値シフトの歴史', path: '/about/experience-shift' },
    { title: 'FLARETECHの経営陣', path: '/about/leadership' },
    { title: 'FLARETECHをもっと知るリンク集', path: '/about/more' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'richText',
      data: {
        content: '<p>数あるIT企業の中からFLARETECHを見つけていただき、ありがとうございます。 私たちは2025年11月、創業5周年を機に「Marvel株式会社」から「FLARETECH株式会社」へと社名を変更しました。ITの総合商社を目指す「第2創業期」へと突入した私たちの現在地と、新卒の皆さんが手に入れられる「最高の1歩目」を凝縮してお伝えします。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-1',
        title: '1. FLARETECHを定義する4つのキーワード',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>誰もが輝く社会を創る（Mission）</h3><p>ITの技術力を武器に、クライアントの課題解決はもちろん、働く社員一人ひとりが「自分が主役だ」と実感できる環境を追求しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>数字が証明する「期待」と「信頼」</h3><p>中途採用では<strong>月間400名超</strong>の応募があり、多くの経験者が私たちのビジョンに共感しています。<br>また、累計取引社数は<strong>500社を突破</strong>。豊富なプロジェクト背景が、あなたの成長を支える確かな基盤となります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>伴走型キャリア支援「L&D室」</h3><p>「アサインして終わり」にはしません。人材開発＆育成の専門部署「L&D室」を設置。市場価値を最大化するためのロードマップを共に描きます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>ITの総合商社への進化</h3><p>SES・受託開発をベースに、AWS、DX、そして自社プロダクトへ。一つの領域に固執せず、ITの力で全方位の課題解決ができる集団を目指しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/Recruit-Overview_取引実績.png',
        alt: '取引実績',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-2',
        title: '2. 私たちが大切にする3つのValue',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>ミッションを実現するために、私たちが日々の意思決定で指針としている価値観です。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>01. 挑戦（Challenge）</h3><p>AWSやDXといった新領域、自社プロダクト開発など、常に新しい挑戦をし続けます。失敗を恐れず自ら手を挙げる人を、私たちは全力で応援します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>02. 誠実（Integrity）</h3><p>顧客にも、仲間にも、常にオープンで誠実であること。L&D室の面談や月1回の1on1など、嘘のない対話が揺るぎない信頼基盤を作ります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>03. 共創（Co-Creation）</h3><p>エンジニア、営業、バックオフィス。立場の垣根を越え、個々の「輝き」を掛け合わせることで、一人では到達できない大きなインパクトを社会に提供します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-3',
        title: '3. FLARETECHで働く5つの魅力',
        alignment: 'left'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/0002.jpg',
        alt: 'FLARETECH社員',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>1. 可能性を広げる「営業力」</h3><p>誠実な対応で築いた顧客との信頼関係が強みです。大手企業との取引も拡大しており、豊富な案件の中からあなたのキャリアに最適な「挑戦の場」を開拓します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>2. 孤立させない「L&D室」のサポート</h3><p>現場アサイン後も専任部署が並走。定期面談を通じてスキルの現在地を確認し、理想のキャリアパスを現実のものにします。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>3. リーダー陣との「月1回の1on1」</h3><p>技術的な悩みやキャリアの不安を解消する場を提供しています。経験豊富な先輩が、あなたの良き理解者として並走します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>4. 互いを高め合う「学び」のコミュニティ</h3><p>月1回の社内勉強会やSlackでの活発な情報交換。部署や拠点を越えて教え合う、技術好きにはたまらない文化が根付いています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>5. リアルを大切にする「カルチャー」</h3><p>3ヶ月に一度のオフライン交流会など、社員同士の繋がりを深める機会が豊富。互いを認め合う仲間がいるから、もっと先へ進めます。（オンラインでの交流機会も豊富にあります。）</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-4',
        title: '4. 外部機関からも評価される、確かな実績',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>ベンチャーとしての勢いだけでなく、外部機関や社会からも確かな評価をいただいています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'recognitionList',
      data: {
        items: [
          {
            title: '「働きがいのある会社（GPTW）」認定',
            description: '2025年10月、Great Place To Work® Institute Japan（以下、GPTW Japan）が実施する調査において、「働きがいのある会社」に認定されました。',
            link: 'https://prtimes.jp/main/html/rd/p/000000015.000150941.html',
            linkText: 'プレスリリース'
          },
          {
            title: '水戸ホーリーホック「シルバーパートナー」契約締結',
            description: '2025年9月より、Jリーグ「水戸ホーリーホック」のシルバーパートナーとなりました。スポーツ支援を通じて、地域社会の活性化にもコミットしています。',
            link: 'https://prtimes.jp/main/html/rd/p/000000014.000150941.html',
            linkText: 'プレスリリース'
          },
          {
            title: '2026年度「ベストベンチャー100」選出',
            description: '「ベストベンチャー100」は、イシン(株)が提供する法人向け有料会員制サービスです。エントリーした企業の中から、厳正な審査のもと選出したベンチャー企業100社が「ベストベンチャー100」として紹介されます。',
            link: 'https://best100.v-tsushin.jp/',
            linkText: 'ベストベンチャー100'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-5',
        title: '5. 数字で見る、FLARETECH',
        alignment: 'left'
      }
    },
    {
      type: 'statsGrid',
      data: {
        columns: 2,
        items: [
          {
            label: '中途採用応募数',
            prefix: '月間',
            number: '400',
            unit: '名超',
            description: '',
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/Recruit-Overview_中途採用応募数.png'
          },
          {
            label: '26卒応募総数 (※累計)',
            values: [
              { label: 'エンジニア', number: '525', unit: '名' },
              { label: '営業', number: '537', unit: '名' }
            ],
            notice: '27卒応募総数 (※累計)',
            values2: [
              { label: 'エンジニア', number: '875', unit: '名' },
              { label: '営業', number: '963', unit: '名' }
            ],
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/Recruit-Overview_新卒応募数.png'
          },
          {
            label: '累計取引社数',
            number: '500',
            unit: '社以上',
            description: '',
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/Recruit-Overview_累計取引社数.png'
          },
          {
            label: '社員数 (※2026年2月時点)',
            number: '211',
            unit: '名',
            description: '',
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/Recruit-Overview_社員数.png'
          },
          {
            label: 'エンジニア比率 (※2026年2月時点)',
            values: [
              { label: 'エンジニア', number: '91.5', unit: '％' },
              { label: '管理部', number: '8.5', unit: '％' }
            ],
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/Recruit-Overview_エンジニア比率.png'
          },
          {
            label: 'リモートワーク率 (※2026年2月時点)',
            valueLabel: 'フルリモート・週3以上リモート',
            number: '66.4',
            unit: '％',
            description: '',
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/Recruit-Overview_リモートワーク率.png'
          },
          {
            label: '技術領域割合 (※2026年2月時点)',
            values: [
              { label: '開発', number: '64.4', unit: '％' },
              { label: 'インフラ', number: '8.3', unit: '％' },
              { label: 'DX', number: '26.7', unit: '％' }
            ],
            description: '',
            image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/Recruit-Overview_技術領域割合.png'
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/about/Recruit-Overview_技術領域割合.png',
        alt: '技術領域割合',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-6',
        title: '6. よくある質問に答えます',
        alignment: 'left'
      }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: 'FLARETECHはどんな会社ですか？',
            answer: '<p>2020年創業のITテクノロジー企業です。SES・受託開発を基盤とし、AWS・DX領域、自社プロダクト開発へと事業領域を拡大中。「ITの総合商社」を目指し、多様なクライアントの課題解決を支援しています。</p>'
          },
          {
            question: '新卒採用の特徴は何ですか？',
            answer: '<p>「L&D室」による伴走型サポートが最大の特徴です。配属後も専任部署が定期面談を実施し、スキル形成とキャリアパスを一緒に設計。孤立させない体制を整えています。</p>'
          },
          {
            question: '研修制度はありますか？',
            answer: '<p>入社後の研修プログラムに加え、配属後も月1回の社内勉強会、書籍購入補助、資格取得支援など、継続的な学習環境を提供しています。</p>'
          },
          {
            question: 'リモートワークは可能ですか？',
            answer: '<p>2026年2月時点で66.4％の社員がフルリモートまたは週3以上リモートで働いています。新卒の方も経験とスキルに応じて柔軟な働き方が可能です。</p>'
          },
          {
            question: 'キャリアパスはどうなっていますか？',
            answer: '<p>スペシャリスト、PM、上流設計など複数の方向性があります。月1回の1on1を通じて、あなたの希望と適性に合わせたキャリア形成を支援します。</p>'
          }
        ]
      }
    }
  ],
  status: 'published'
};

export const newGraduateProcessHiringPageData = {
  slug: '/newgraduate/process/hiring-process',
  title: '相互の価値観をすり合わせ、共に「輝く」ためのステップ',
  titleEn: 'Hiring Process',
  subtitle: '',
  category: 'process',
  site: 'newgraduate',
  publishedAt: '2026-03-03',
  showToc: true,
  tocLinks: [
    { title: '採用基準と選考プロセス', path: '/newgraduate/process/hiring-process' },
    { title: '選考プロセス', path: '/process/process' },
    { title: 'リエントリー制度', path: '/process/reentry' },
    { title: 'アルムナイ採用', path: '/process/alumni' },
    { title: 'よくある質問', path: '/process/faq' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  contentBlocks: [
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの新卒採用に興味をお持ちいただき、ありがとうございます。<br>私たちの選考は、単に合否を決める場ではなく、FLARETECHのビジョンやValueと、皆様の「これから成し遂げたいこと」をフラットにすり合わせる場だと考えています。<br>この記事では、エンジニア職および営業職の選考プロセスと、それぞれのステップで大切にしているポイントをご紹介します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-1',
        title: '1. 選考の全体像とスピード感',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>誰もが輝く社会を創る（Mission）</h3><p>FLARETECHの選考は、平均して<strong>約2週間</strong>というスピード感で進みます。皆様の貴重な時間を大切にしながら、スムーズな意思決定ができるようサポートいたします。</p><ul style="list-style: disc; padding-left: 24px; margin: 16px 0;"><li><strong>結果通知：</strong> 各ステップ通過のご連絡は、<strong>3日以内</strong>を目安に差し上げます。</li><li><strong>提出書類：</strong> 全職種共通で「履歴書」をご提出いただきます。</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-2',
        title: '2. エンジニア職の選考プロセス',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>ITやものづくりへの興味関心や、チームで働くための価値観を重視します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/process/選考プロセス_エンジニア.png',
        alt: 'エンジニア職の選考プロセス',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>STEP 01：会社説明会（オンライン）</h3><p>まずはFLARETECHの事業内容や文化、目指すビジョンについて人事担当よりお話しします（約30分）。<br>※説明会後の結果通知はありません。次のステップを希望される方は適性検査へ進みます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>STEP 02：適性検査</h3><p>エンジニア職としての適性を診断するための知能検査を受検いただきます。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>STEP 03：一次面接（対面 or オンライン）</h3><p>人事が担当し、主に価値観・志向性を中心にFLARETECHのカルチャーとの相性を確認します。</p><ul style="list-style: disc; padding-left: 24px; margin: 16px 0;"><li><strong>主な内容：</strong> 学生時代の取り組み、チーム活動での役割、困難を乗り越えた経験など。</li><li><strong>形態：</strong> 一都三県以外の方はオンラインも可能ですが、対面を推奨しています。</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>STEP 04：適性検査</h3><p>一次面接通過後、より深く志向性を理解するための検査を実施します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>STEP 05：最終面接（対面）</h3><p>代表の河畑と直接対話いただく場です。</p><ul style="list-style: disc; padding-left: 24px; margin: 16px 0;"><li><strong>主な内容：</strong> 幼少期から現在に至るまでのエピソードを通じた価値観の形成、意思決定の背景、将来のビジョンについて。</li><li><strong>形態：</strong> 本社（東京・西麻布）にて実施。</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/process/0045.jpg',
        alt: '最終面接',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-3',
        title: '3. 営業職の選考プロセス',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>対人スキルや、物事を前向きに捉えてやり抜く姿勢を重視しています。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/process/選考プロセス_営業.png',
        alt: '営業職の選考プロセス',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>STEP 01：会社説明会（オンライン）</h3><p>人事担当より、FLARETECHの成長の核である営業本部の役割やキャリアパスをお伝えします。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>STEP 02：一次面接（対面 or オンライン）</h3><p>人事が担当し、主に価値観・志向性を中心にFLARETECHのカルチャーとの相性を確認します。</p><ul style="list-style: disc; padding-left: 24px; margin: 16px 0;"><li><strong>主な内容：</strong> 学生時代の取り組み、なぜ営業に挑戦したいのか、目標達成のために努力した経験など。</li><li><strong>形態：</strong> 一都三県以外の方はオンラインも可能ですが、対面を推奨しています。</li></ul>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>STEP 03：適性検査</h3><p>思考の特性や適性を確認するための検査を実施します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<h3>STEP 04：最終面接（対面）</h3><p>代表の河畑とフラットに語り合う場です。代表自身の営業経験に基づいた、熱量の高い時間となります。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/process/2502050529.jpg',
        alt: '最終面接',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-4',
        title: '4. FLARETECHが「対面」と「対話」を重視する理由',
        alignment: 'left'
      }
    },
    {
      type: 'splitContent',
      data: {
        image: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/process/20250522-hiring-process.jpg',
        imageAlt: '対面での対話',
        imagePosition: 'left',
        imageWidth: '33%',
        content: '<p>私たちの最終面接は、原則として対面で実施しています。それは、画面越しでは伝わりきらない「熱量」や、お互いの細かなニュアンスを大切にしたいと考えているからです。</p><p>代表の河畑との面接も、一方的な選考ではありません。</p><p>「誰もが輝く社会を創る」というミッションに対し、皆様のキャリアがどう重なり合うのか。幼少期からの経験を振り返りながら、あなた自身の「軸」を一緒に見つけていくような時間を目指しています。</p><p>また、内製システムを活用した1on1やMBOといった「入社後の伴走」も、この選考時の対話から始まります。 あなたの個性をデータとしてではなく、一人の人間としての「体験」として大切にしたい。それがFLARETECHの採用のあり方です。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: `<p style="text-align: center; margin-top: 60px; font-size: 1.125rem;"><strong>次のステップ：<a href="${ALL_JOBS_URL}" target="_blank" rel="noopener noreferrer" style="color: var(--color-brand-red);">エントリーはこちら<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: -0.125em; margin-left: 4px;"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></a></strong></p>`,
        alignment: 'center',
        maxWidth: 'medium'
      }
    }
  ],
  status: 'published'
};

export const newGraduateInternshipPageData = {
  slug: '/newgraduate/internship',
  title: '代表直伝の営業マインドと、社会人基礎力を手に入れる',
  titleEn: 'Internship',
  subtitle: '',
  category: 'internship',
  site: 'newgraduate',
  publishedAt: '2026-03-03',
  showToc: true,
  tocLinks: [
    { title: 'インターンシップ', path: '/newgraduate/internship' }
  ],
  hero: { type: 'simple', backgroundImage: '', buttons: [] },
  description: 'FLARETECHは今、設立5期目でメンバー数約200名、売上前年対比300%という爆発的な成長フェーズにあります。この勢いをさらに加速させる推進役として、営業本部の長期インターンシップ生を募集します。',
  ogImage: '/og-images/newgraduate-internship.png',
  contentBlocks: [
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHは今、設立5期目でメンバー数約200名、売上前年対比300%という爆発的な成長フェーズにあります。この勢いをさらに加速させる推進役として、営業本部の長期インターンシップ生を募集します。</p>',
        alignment: 'left',
        maxWidth: 'medium'
      }
    },
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-1',
        title: '1. 募集の背景：営業を「一生物のスキル」に',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>弊社の代表・河畑は、自身のファーストキャリアが営業職であったことから、営業職の指導と育成に並々ならぬ情熱を注いでいます。<br />「学生のうちに、どこへ行っても通用する社会人基礎力と営業スキルを身につけてほしい」 そんな想いから、今回のインターン募集が決定しました。平均年齢30歳という、若くて活気ある営業社員たちと共に、和気あいあいとした環境でプロの仕事を学んでみませんか？</p>',
        alignment: 'left',
        maxWidth: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-2',
        title: '2. 配属部署とミッション',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>配属先は、FLARETECHの心臓部である<strong>「営業本部」</strong>です。 「誰もが輝く社会を創る」というミッションを掲げ、クライアント企業の課題を解決するためのプロジェクトを獲得し、最適なエンジニアをマッチングさせることが私たちの役割です。</p>',
        alignment: 'left',
        maxWidth: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-3',
        title: '3. 業務内容：基礎から実践まで',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>未経験からスタートした中途社員も多数活躍している環境です。ステップを追って、着実にスキルを磨いていただけます。</p>',
        alignment: 'left',
        maxWidth: 'large'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>・社会人基礎業務</strong><br />電話対応、データ整理、資料作成など、ビジネスパーソンとしての土台となる業務からスタートします 。</p><p><strong>・リレーション構築と案件獲得</strong><br />新規・既存顧客へニーズをヒアリングし、プロジェクトの内容や必要なエンジニアのスキル・人数を把握します。</p><p><strong>・エンジニアとのマッチング</strong><br />要件にぴったりのエンジニアを選定。紹介から契約手続きまで、一連の流れを担当します。</p><p><strong>・プロジェクトのフォローアップ</strong><br />円滑にプロジェクトが進むよう、参画中のエンジニアや顧客企業とコミュニケーションを取り、サポートを行います。</p>',
        alignment: 'left',
        maxWidth: 'large'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/internship/2502050504.jpg',
        alt: 'オフィスの様子',
        alignment: 'center',
        width: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-4',
        title: '4. 募集要項・条件',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p style="font-weight: 700; margin-bottom: 8px;">対象・スキル</p><ul><li>対象： 28卒・29卒（大学生）・文理不問</li><li>必要スキル： パソコンの基本操作ができる方</li></ul>',
        alignment: 'left',
        maxWidth: 'large'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p style="font-weight: 700; margin-bottom: 8px;">勤務条件</p><ul><li>期間： 3ヶ月〜</li><li>シフト： 週3日〜（原則週16時間以上）</li><li>勤務時間： 9:30〜18:30の間 ※テスト期間などのシフト調整は柔軟に相談に応じます。</li><li>勤務地：FLARETECH 本社オフィス</li><li>交通費支給あり（2万円/月 まで）</li></ul>',
        alignment: 'left',
        maxWidth: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-5',
        title: '5. 求める人物像',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>スキル以上に、FLARETECHのバリューに共感し、誠実に物事に取り組める方を重視しています。</p><ul><li>真面目に、そして前向きに物事を考えられる方</li><li>チームワークを大切にし、人の役に立ちたいという想いがある方</li><li>相手の立場に立って考え、挨拶や身だしなみ、マナーを大事にできる方</li></ul>',
        alignment: 'left',
        maxWidth: 'large'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        id: 'section-6',
        title: '6. インターン生へのインタビュー',
        alignment: 'left'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p style="text-align: center;"><a href="https://note.com/flaretech_it/n/n4085d1e0c201?sub_rt=share_sb" target="_blank" rel="noopener noreferrer"><img src="https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/newgraduate/internship/INTERN INTERVIEW.png" alt="インターン生インタビュー" style="max-width: 600px; width: 100%; height: auto; border-radius: 8px;" /></a></p><p style="text-align: center; font-size: 0.875rem; color: #666; margin-top: 16px;"><a href="https://note.com/flaretech_it/n/n4085d1e0c201?sub_rt=share_sb" target="_blank" rel="noopener noreferrer" style="color: var(--color-brand-red);">IT人材営業を目指す学生必見！──内定者インターン体験記<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: -0.125em; margin-left: 4px;"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></a></p>',
        alignment: 'center',
        maxWidth: 'medium'
      }
    },
    {
      type: 'richText',
      data: {
        content: `<p style="text-align: center; margin-top: 60px; font-size: 1.125rem;"><strong>次のステップ：<a href="${SALES_INTERNSHIP_URL}" target="_blank" rel="noopener noreferrer" style="color: var(--color-brand-red);">エントリーはこちら<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block; vertical-align: -0.125em; margin-left: 4px;"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></a></strong></p>`,
        alignment: 'center',
        maxWidth: 'medium'
      }
    }
  ],
  status: 'published'
};


// ============================================
// PROCESS PAGES
// ============================================

export const processProcessPageData = {
  slug: '/process/process',
  title: '選考プロセス',
  titleEn: 'Recruitment Process',
  subtitle: 'Selection Process',
  category: 'process',
  site: 'career',
  publishedAt: '2025-01-26',
  showToc: true,
  tocLinks: [
    { title: '選考プロセス', path: '/process/process' },
    { title: 'リエントリー制度', path: '/process/reentry' },
    { title: 'アルムナイ採用', path: '/process/alumni' },
    { title: 'よくある質問', path: '/process/faq' }
  ],
  metadata: {
    updatedDate: '2025.01.26',
    readTime: '約3分'
  },
  hero: {
    type: 'simple',
    title: '選考プロセス',
    titleEn: 'Selection Process',
    description: 'このページでは、FLARETECHのキャリア採用における採用基準と選考プロセスをご紹介します。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>私たちが大切にしているのは、スキルの有無だけでなく、価値観を共有し、これからのキャリアを一緒に設計できるかという視点です。</p><p>選考は「見極めの場」ではなく、お互いが判断するためのプロセスだと考えています。</p><h3>選考全体の流れについて</h3><p>選考は、下記のステップで進みます。<br>※職種・候補者の状況により、順番や回数が変更される場合があります。</p><p><strong>全体の選考期間：平均1週間〜1ヶ月程度</strong><br>（中途エンジニア／ビジネス職はスピード選考の場合あり）</p><p>また、選考中の不安や認識ズレを減らすため、<br>各プロセスの間で15〜30分程度の人事面談を挟むことがあります。<br>（実施する場合は事前にご案内します）</p>'
      }
    },
    {
      type: 'timeline',
      data: {
        items: [
          {
            phase: 'STEP 1',
            title: 'カジュアル面談'
          },
          {
            phase: 'STEP 2',
            title: '書類選考'
          },
          {
            phase: 'STEP 3',
            title: '一次面接'
          },
          {
            phase: 'STEP 4',
            title: '二次面接'
          },
          {
            phase: 'STEP 5',
            title: '最終面接'
          },
          {
            phase: 'STEP 6',
            title: 'オファー面談'
          }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/0029.jpg',
        alt: 'FLARETECHのオフィス環境',
        caption: '全社員が成長できる環境を目指しています'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '1. カジュアル面談',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>目的</strong>：相互理解を深めるための、選考前の対話の場です。</p><p>FLARETECHの事業・組織・働き方についてお話ししながら、「今の状況」「次に何を伸ばしたいか」「どんな環境を求めているか」をすり合わせます。</p><p>応募意思は不要です。気になることは、遠慮なく聞いてください。</p>'
      }
    },
    {
      type: 'table',
      data: {
        rows: [
          { cells: ['項目', '内容'] },
          { cells: ['所要時間', '30分〜1時間'] },
          { cells: ['担当者', '人事／配属予定部署のメンバー'] },
          { cells: ['形式', 'オンライン'] },
          { cells: ['結果連絡', '当日〜3営業日以内'] },
          { cells: ['備考', '応募前提ではありません'] }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '2. 書類選考',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>履歴書・職務経歴書・ポートフォリオ等をもとに、これまでの経験だけでなく「どう考え、どう向き合ってきたか」を拝見します。</p><p>成果物の完成度だけでなく、背景・意図・試行錯誤のプロセスを重視しています。</p><p><strong>提出物</strong>：履歴書／職務経歴書、ポートフォリオ（職種による）</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>※エンジニア：GitHub／サービスURL等（開発内容が分かるもの）</p><p><strong>結果連絡：3営業日以内</strong></p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '3. 一次面接',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>目的</strong>：業務適性・スキル・仕事への向き合い方を確認します。</p><p>これまでの経験を中心に、</p><ul><li>どんな役割を担ってきたか</li><li>どんな壁にどう向き合ってきたか</li><li>何が得意で、何を伸ばしたいか</li></ul><p>を伺います。</p>'
      }
    },
    {
      type: 'table',
      data: {
        rows: [
          { cells: ['項目', '内容'] },
          { cells: ['所要時間', '45分〜1時間'] },
          { cells: ['担当者', '配属予定部署のマネージャー／リーダー'] },
          { cells: ['形式', 'オンライン'] },
          { cells: ['結果連絡', '3営業日以内'] }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '4. 二次面接',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>目的</strong>：価値観・志向性・カルチャーフィットの確認。</p><p>スキル以上に、</p><ul><li>意思決定の軸</li><li>キャリア観</li><li>仕事における大切にしていること</li></ul><p>について深掘りします。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHのバリューや行動原理と、どこが重なり、どこが違うのかを率直に話す場です。</p>'
      }
    },
    {
      type: 'table',
      data: {
        rows: [
          { cells: ['項目', '内容'] },
          { cells: ['所要時間', '1時間'] },
          { cells: ['担当者', '部門責任者'] },
          { cells: ['形式', 'オンライン／対面（ご希望による）'] },
          { cells: ['結果連絡', '3営業日以内'] },
          { cells: ['備考', 'オフィス見学も可能です'] }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '5. 最終面接',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>目的</strong>：FLARETECHの代表と直接対話し、「これからを一緒にやれるか」を確認する場です。</p><p>一方的な評価ではなく、これまでの原体験・意思決定・価値観を軸に、お互いの考えをフラットに共有します。</p><p>「この人に、どんな機会を提供できるか」という視点で話をしています。</p>'
      }
    },
    {
      type: 'table',
      data: {
        rows: [
          { cells: ['項目', '内容'] },
          { cells: ['所要時間', '1時間'] },
          { cells: ['担当者', '代表取締役'] },
          { cells: ['形式', 'オンライン／対面（ご希望による）'] },
          { cells: ['結果連絡', '3営業日以内'] },
          { cells: ['備考', 'これまでの人生・仕事観を中心に対話します'] }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '6. オファー面談',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>内定通知とあわせて、選考を通じて感じた強み・入社後に期待している役割・待遇・条件面をすり合わせます。</p><p>不安や疑問は、この場で全て解消してください。</p>'
      }
    },
    {
      type: 'table',
      data: {
        rows: [
          { cells: ['項目', '内容'] },
          { cells: ['所要時間', '30分〜1時間'] },
          { cells: ['担当者', '人事／代表'] },
          { cells: ['形式', 'オンライン／対面'] },
          { cells: ['備考', '入社後のキャリアパスについても詳しくご説明します'] }
        ]
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/culture/values/values-3.jpg',
        alt: 'FLARETECHの価値観と文化',
        caption: '価値観を共有し、一緒に成長できる人を探しています'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: 'FLARETECHの選考が大切にしていること',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>選考を「試験」にしない</li><li>認識ズレを早く解消する</li><li>条件よりも、成長の設計を重視する</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>だからこそ、スピード感と対話量を大切にしています。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: {
        title: '最後に',
        alignment: 'left',
        background: 'white'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>このプロセスを通じて判断してほしいのは、「受かるかどうか」ではなく、</p><p><strong>この環境で、前に進めそうか、この人たちと、時間を重ねたいか</strong></p><p>です。</p><p>まずは、カジュアルに話しませんか。応募ではありません。働き方・成長・制度の前提を、先にすり合わせるための面談です。</p>'
      }
    },

    {
      type: 'cta',
      data: {
        // カジュアル面談は内部フォーム、募集要項はHERP（2026年7月にHRMOSへ移行予定）
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const processReentryPageData = {
  slug: '/process/reentry',
  title: 'リエントリー制度',
  subtitle: 'Re-entry Program',
  category: 'process',
  site: 'career',
  publishedAt: '2026-02-02',
  showToc: true,
  tocLinks: [
    { title: '選考プロセス', path: '/process/process' },
    { title: 'リエントリー制度', path: '/process/reentry' },
    { title: 'アルムナイ採用', path: '/process/alumni' },
    { title: 'よくある質問', path: '/process/faq' }
  ],
  metadata: {
    updatedDate: '2026.02.02',
    readTime: '約3〜4分'
  },
  hero: {
    type: 'simple',
    title: 'リエントリー制度',
    titleEn: 'Re-entry Program',
    description: 'このページでは、FLARETECHのリエントリー制度（再挑戦制度）についてご紹介します。過去に選考を受けた方が、成長や環境の変化を経て、もう一度FLARETECHに挑戦できる仕組みです。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>私たちは、「一度の選考結果で、人の可能性を決めきらない」という採用姿勢を大切にしています。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '「もう一度挑戦したい」という想いに応えるために', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>採用の場では、スキル・経験・タイミングが募集条件と合わず、やむを得ずお見送りとなることがあります。</p><p>しかしその中には、</p><ul><li>人柄や価値観がFLARETECHと合っている</li><li>学習意欲や伸びしろを強く感じる</li><li>「今は合わないが、時間が経てばぜひ一緒に働きたい」</li></ul><p>そう感じる方が、少なからず存在します。</p><p>FLARETECHのリエントリー制度は、そうした方が成長を経て、再び挑戦できるための仕組みです。</p><p>一度の選考結果で関係を終わらせるのではなく、再挑戦の機会をつくる。それが、FLARETECHの考える誠実な採用です。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '対象となる方', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>以下の条件を満たす方が対象となります。</p><p><strong>過去にFLARETECHのキャリア採用（エンジニア／ビジネス職）において一次面接以上を受けたことがある方</strong></p><p><strong>FLARETECHで最後に受けた面接から 6カ月以上 が経過している方</strong></p><p>※過去に内定を辞退された方（他社優先・タイミング・家庭事情などの理由）も対象です。※「当時は合わなかったが、今なら再挑戦したい」という応募も歓迎します。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '選考フロー', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>リエントリー制度では、<br>書類選考をスキップし、選考を再開します。</p><p><strong>基本フロー</strong></p><ol><li>リエントリー応募</li><li>一次面接</li><li>最終面接</li><li>オファー面談</li></ol><p>※職種や候補者の状況により、面接回数や順番が変わる場合があります<br>※リエントリー制度での再挑戦は <strong>原則1年に1回まで</strong> となります</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '応募方法', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>以下の専用フォームより、希望職種を選択してご応募ください。</p><ul><li>エンジニア職｜リエントリー応募フォーム</li><li>ビジネス職｜リエントリー応募フォーム</li></ul><p><small>※通常の応募フォームとは異なりますのでご注意ください</small></p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '選考で見るポイント', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>基本的な観点は通常の選考と同じですが、リエントリーでは特に <strong>「前回からの変化・成長」</strong> を重視します。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'よくあるお見送り理由', alignment: 'left' }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: 'スキルや経験が募集要件に届いていなかった',
            answer: '基礎的なスキルや実務経験が不足していた\n\n募集ポジションに必要な技術スタックの経験がなかった'
          },
          {
            question: '受け身で、成長イメージが描きづらかった',
            answer: '指示待ちの姿勢が強く、主体性が感じられなかった\n\nキャリアビジョンや目標が曖昧だった'
          },
          {
            question: '業務の一部工程しか経験していなかった',
            answer: '特定の工程のみの経験で、全体像が見えていなかった\n\n企画から実装まで一貫した経験が少なかった'
          },
          {
            question: '成果や改善を言語化できなかった',
            answer: '「何をやったか」は説明できても、「なぜ・どう」が弱かった\n\n定量的な成果や改善結果を示せなかった'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '再挑戦で評価されるポイント', alignment: 'left' }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: '学習・実務を通じた明確なスキルアップが見える',
            answer: '実務や自己学習を通じて、必要なスキルを習得している\n\n具体的なプロジェクト経験を積み、成果を示せるようになった'
          },
          {
            question: '自ら学び、挑戦した具体的な行動を語れる',
            answer: '自ら課題を見つけて行動した経験を語れる\n\n「なぜそれをやったのか」を明確に説明できる'
          },
          {
            question: '一連のプロセス理解が深まり、役割が広がっている',
            answer: '上流から下流まで、一連のプロセスを経験している\n\n異なる役割・立場での経験を積んでいる'
          },
          {
            question: '振り返りを通じて、再現性として説明できる',
            answer: '具体的な数値や事例をもとに、成果を説明できる\n\n「課題→施策→結果」を論理的に語れる'
          }
        ]
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p>私たちが見ているのは、<strong>「完璧になったか」ではなく、「どう変わったか」</strong> です。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHがリエントリーで大切にしていること', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<ul><li>成長は直線ではない</li><li>タイミングが合わないこともある</li><li>人は、環境と意思で変われる</li></ul><p>FLARETECHは、挑戦を続ける人を歓迎する会社でありたいと考えています。</p><p>もし今、「もう一度、話してみたい」「今なら、あの時より前に進める」そう感じているなら、ぜひリエントリーしてください。</p><p><strong>もう一度、FLARETECHに挑戦してみませんか。</strong></p><p>過去の結果ではなく、これからの可能性を見せてください。</p>'
      }
    },

    {
      type: 'cta',
      data: {
        // カジュアル面談は内部フォーム、募集要項はHERP（2026年7月にHRMOSへ移行予定）
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const processAlumniPageData = {
  slug: '/process/alumni',
  title: 'アルムナイ採用',
  titleEn: 'Alumni Hiring',
  subtitle: 'Alumni Hiring',
  category: 'process',
  site: 'career',
  publishedAt: '2026-02-02',
  showToc: true,
  tocLinks: [
    { title: '選考プロセス', path: '/process/process' },
    { title: 'リエントリー制度', path: '/process/reentry' },
    { title: 'アルムナイ採用', path: '/process/alumni' },
    { title: 'よくある質問', path: '/process/faq' }
  ],
  metadata: {
    updatedDate: '2026.02.02',
    readTime: '約5〜7分'
  },
  hero: {
    type: 'simple',
    title: 'アルムナイ採用',
    titleEn: 'Alumni Hiring',
    description: 'このページでは、FLARETECHのアルムナイ採用（卒業生採用）についてご紹介します。アルムナイ採用とは、過去にFLARETECHで働いたことのある方を対象にした再採用の仕組みです。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'richText',
      data: {
        content: '<p>私たちは、退職を「関係の終了」だとは考えていません。<br>成長の過程で一度離れ、外の世界を経験した上で、<br>もう一度FLARETECHで挑戦したいと思える関係性を保つことを、大切にしています。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'FLARETECHがアルムナイ採用を行う理由', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>FLARETECHの根底にある思想は、<br><strong>「エンジニアの市場価値を高めること」</strong>です。</p><p>その価値は、社内に留まり続けることでしか証明できないものではありません。<br>むしろ――</p><ul><li>一度外に出て</li><li>別の環境で挑戦し</li><li>それでも「もう一度戻りたい」と思える</li></ul><p>この循環こそが、<br><strong>FLARETECHの育成と文化が"本物だった証拠"</strong>だと考えています。</p><p>私たちは、<br>「ずっと居続けてもらう会社」ではなく、<br><strong>「戻る選択肢として、信頼され続ける会社」</strong>でありたい。</p><p>アルムナイ採用は、そのための制度です。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '卒業生アンケートから見えた、FLARETECHの"残る価値"', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>実際にFLARETECHを離れた卒業生へのアンケートから、<br>共通して挙がった声があります。</p>'
      }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: 'FLARETECHで働いて「良かった」と感じた点は？',
            answer: '「裁量のある環境で、実務を通じて確実にスキルが伸びた」── 作業者ではなく、"考えるエンジニア"として扱われていた。\n\n「未経験や不安がある状態でも、キャリア形成を本気で支えてくれた」── 技術だけでなく、"どう成長するか"を一緒に考える文化があった。\n\n「現場が変わっても、キャリアの相談先が会社にあった」── 一人で抱え込まずに済む仕組みがあった。\n\n「成長が、報酬や次の案件にきちんと反映されていた」── 努力が無駄にならない感覚があった。\n\n「学習や挑戦が"個人任せ"ではなかった」── 置いていかれない安心感があった。'
          },
          {
            question: '離れた後に、あらためて気づいた価値は？',
            answer: '「外に出て初めて、FLARETECHは"育成に時間をかける会社"だったと分かった」\n\n「成長が"自己責任"ではなく、"設計"されていた」\n\n「相談できる会社は、実はとても少ない」\n\nこれらは、在籍中には当たり前だった価値が、外に出て初めて可視化されたものです。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '対象となる方', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>以下を満たす方が、アルムナイ採用の対象です。</p><p><strong>過去にFLARETECHで<br>正社員・契約社員・SESエンジニアとして在籍した経験がある方</strong></p><p>※ 在籍期間の長短は問いません<br>※ 再入社後の職種・ポジションは、現在のスキル・経験・志向に応じて相談の上で決定します<br>※ 一度退職した理由（転職・家庭事情・タイミング等）は問いません。</p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '応募方法・選考フロー', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>アルムナイ採用では、<br>退職からの期間・現在のキャリア状況に応じて、柔軟に選考を行います。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>① 社員経由でのご相談</strong></p><ul><li>在籍中の社員や元上司を通じてご連絡いただいた場合、<br>まずはカジュアルな面談を実施</li><li>状況によっては、そのまま選考・再採用につながるケースもあります</li></ul>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>② フォームからのエントリー</strong></p><ul><li>専用フォームよりエントリー</li><li>人事が経歴・希望を確認後、最適な選考フローをご案内します</li><li>状況によっては、通常のキャリア採用と同様の選考を行う場合があります</li></ul><p><a href="mailto:r.contact@flaretech.tech?subject=%E3%80%90%E3%82%A2%E3%83%AB%E3%83%A0%E3%83%8A%E3%82%A4%E6%8E%A1%E7%94%A8%E3%80%91%E5%B0%82%E7%94%A8%E3%82%A8%E3%83%B3%E3%83%88%E3%83%AA%E3%83%BC%E3%83%95%E3%82%A9%E3%83%BC%E3%83%A0&body=%E3%81%8A%E5%90%8D%E5%89%8D%3A%20%0A%E3%83%95%E3%83%AA%E3%82%AC%E3%83%8A%3A%20%0A%0A%E3%83%A1%E3%83%BC%E3%83%AB%E3%82%A2%E3%83%89%E3%83%AC%E3%82%B9%3A%20%0A%E9%9B%BB%E8%A9%B1%E7%95%AA%E5%8F%B7%3A%20%0A%0A%E5%9C%A8%E7%B1%8D%E6%9C%9F%E9%96%93%3A%20%0A%E9%80%80%E8%81%B7%E6%99%82%E3%81%AE%E6%89%80%E5%B1%9E%E9%83%A8%E7%BD%B2%3A%20%0A%0A%E5%86%8D%E5%85%A5%E7%A4%BE%E5%B8%8C%E6%9C%9B%E7%90%86%E7%94%B1%3A%0A">アルムナイ採用 専用エントリーフォーム</a></p>'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '応募する前に考えてほしいこと', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>アルムナイ採用は、単なる「復帰」や「出戻り」ではありません。</p><p>私たちは、<br><strong>次のキャリアをより良くするための"再選択"</strong>として捉えています。</p><p>そのため、面談では以下のような点を大切にしています。</p>'
      }
    },
    {
      type: 'richText',
      data: {
        content: '<p><strong>よくお聞きする質問例</strong></p><ul><li>なぜ、もう一度FLARETECHに戻りたいと思ったのか</li><li>退職後、どんな経験を積み、どんな視点が身についたか</li><li>今後、どんなキャリアを描いているか</li><li>FLARETECHで、そのキャリアをどう実現したいか</li><li>外で得た経験を、チームや事業にどう還元したいか</li></ul><p>「戻りたい理由」よりも、<br><strong>「今、どんな人になっているか」</strong>を重視します。</p>'
      }
    },
    {
      type: 'image',
      data: {
        url: 'https://storage.googleapis.com/recruit-flaretech.firebasestorage.app/cms/process/alumni-1.png',
        alt: 'アルムナイ採用のイメージ'
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'もう一度、話しませんか', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>離れたからこそ見えたこと。<br>成長したからこそ、もう一度挑戦したい気持ち。</p><p>それがあるなら、まずは選考ではなく、対話から始めましょう。</p><p>FLARETECHは、一度きりで終わらないキャリアを、前提にする会社です。</p><p>また一緒に働く未来があるかどうか。<br>その答えを、急がなくて大丈夫です。</p>'
      }
    },

    {
      type: 'cta',
      data: {
        // カジュアル面談は内部フォーム、募集要項はHERP（2026年7月にHRMOSへ移行予定）
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

export const processFaqPageData = {
  slug: '/process/faq',
  title: 'よくある質問',
  titleEn: 'FAQ',
  subtitle: 'Q&A',
  category: 'process',
  site: 'career',
  publishedAt: '2025-01-26',
  showToc: true,
  tocLinks: [
    { title: '選考プロセス', path: '/process/process' },
    { title: 'リエントリー制度', path: '/process/reentry' },
    { title: 'アルムナイ採用', path: '/process/alumni' },
    { title: 'よくある質問', path: '/process/faq' }
  ],
  metadata: {
    updatedDate: '2026.02.02',
    readTime: '約7分'
  },
  hero: {
    type: 'simple',
    title: 'よくある質問',
    titleEn: 'Q&A',
    description: 'このページでは、FLARETECHでの働き方、プロジェクト体制、事業戦略、評価・キャリアパス、採用プロセスなど、応募前によくいただく質問をまとめています。「どんな環境で、どのように働く会社なのか」を具体的にイメージするための判断材料としてご活用ください。'
  },
  contentBlocks: [
    {
      type: 'tableOfContentsSection',
      data: {}
    },
    {
      type: 'sectionTitle',
      data: { title: '働く環境・働き方', alignment: 'left' }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: 'Q. 働き方や、出社の頻度について教えてください。',
            answer: 'FLARETECHでは、案件・チーム・フェーズに応じた柔軟な働き方を採用しています。<br>SES事業が中心のため、<br>・フルリモート<br>・ハイブリッド<br>・クライアント先常駐<br>など、働き方は案件ごとに異なります。<br>一律に「全員リモート」「全員出社」とはしていません。<br>大切にしているのは、成果と成長につながる働き方を選ぶことです。'
          },
          {
            question: 'Q. 残業時間や、繁忙期の働き方について教えてください。',
            answer: '残業時間は月20〜30時間程度に収まるよう、営業とエンジニアリーダーが案件状況を継続的に確認しています。<br>SESという特性上、繁忙期や一時的に負荷が上がる場面はありますが、<br>・長時間労働が常態化しない<br>・無理が続く場合は調整・配置転換を行う<br>といった方針で運用しています。'
          },
          {
            question: 'Q. リモートワーク時のコミュニケーションツールは何を使っていますか？',
            answer: '主に以下を使用しています。<br>・テキスト：Slack<br>・オンラインMTG：Google Meet / Zoom<br>・情報共有：Notion<br>リモート特有の課題（雑談不足・質問しづらさ）は認識しており、1on1・定例MTG・オンボーディング施策で補完しています。'
          },
          {
            question: 'Q. 副業は可能ですか？',
            answer: '申請・許可制で可能です。<br>以下に該当する場合は原則不可としています。<br>・競合企業での業務<br>・本業に支障が出る稼働<br>・取引中クライアントとの直接取引<br>副業も「挑戦の一部」と捉えつつ、本業との健全なバランスを重視しています。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: { title: 'プロジェクト体制', alignment: 'left' }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: 'Q. 一人あたりの案件担当数はどれくらいですか？',
            answer: '基本は1人1案件が中心です。<br>ただし、フェーズや役割によっては複数案件を兼務する場合もあります。<br>キャリアや志向に応じて、<br>・腰を据えて深く関わる案件<br>・経験の幅を広げる案件<br>を調整しています。'
          },
          {
            question: 'Q. クライアントとのプロジェクト期間はどれくらいですか？',
            answer: '案件により異なりますが、中長期（半年〜1年以上）になるケースも多いです。<br>短期で切り替わる案件よりも、信頼関係を前提とした継続支援を重視しています。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '事業戦略', alignment: 'left' }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: 'Q. FLARETECHは今後どんな方向を目指していますか？',
            answer: 'FLARETECHは現在、<br>・SES事業の高度化<br>・AI・生成AI領域への本格参入<br>・エンジニアの市場価値を高める事業展開<br>を軸に、「人の価値を最大化する会社」を目指しています。<br>短期的な売上よりも、中長期で「選ばれ続ける人材・組織」をつくることを重視しています。<br><a href="/about/future">私たちの歩む方向ページ</a>もご参照ください。'
          },
          {
            question: 'Q. AI・生成AIへの取り組みについて教えてください。',
            answer: '社内ではすでに、<br>・業務効率化<br>・開発補助<br>・ナレッジ活用<br>などでAI活用を進めています。<br>今後は、AIを前提としたエンジニア育成・案件設計を強化していく方針です。<br><a href="/about/ai">AIと働くFRARETECHの現在地ページ</a>もご参照ください。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '評価・キャリアパス', alignment: 'left' }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: 'Q. 評価制度はどのようになっていますか？',
            answer: '等級制度をベースに、役割・スキル・成果・行動を総合的に評価しています。<br>SES特有の「単価だけ評価」にならないよう、日常の行動や成長プロセスも重視しています。<br><a href="/workstyle/evaluation">評価と報酬ページ</a>もご参照ください。'
          },
          {
            question: 'Q. キャリアパスは決まっていますか？',
            answer: '決まった一本道はありません。<br>・技術を深める<br>・リーダー・マネジメントに進む<br>・AI・新規事業に挑戦する<br>など、本人の志向と組織の方向性をすり合わせながら設計します。<br>定期的な1on1・キャリア面談を通じて、一緒に考えるスタイルです。<br><a href="/workstyle/career-path">キャリアパスページ</a>もご参照ください。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '採用・選考フロー', alignment: 'left' }
    },
    {
      type: 'accordion',
      data: {
        items: [
          {
            question: 'Q. 選考フローについて教えてください。',
            answer: '基本的な流れは以下です。<br>1. 書類選考<br>2. 一次面接（現場・マネージャー）<br>3. 二次面接（役員または代表）<br>ポジションにより回数が変わる場合があります。<br>選考は一方的な見極めではなく、相互理解の場として設計しています。<br><a href="/process/process">選考フローページ</a>もご参照ください。'
          },
          {
            question: 'Q. カジュアル面談は応募になりますか？',
            answer: 'いいえ、応募ではありません。<br>まずは、「この環境が合いそうか」をすり合わせる場です。<br>無理な選考誘導は行いません。'
          }
        ]
      }
    },
    {
      type: 'sectionTitle',
      data: { title: '最後に', alignment: 'left' }
    },
    {
      type: 'richText',
      data: {
        content: '<p>ここに書いてあるのは、FLARETECHの理想像ではなく、現在地です。</p><p>完璧な会社ではありません。だからこそ、改善し続けています。</p><p>それでもなお、この環境で挑戦したいか。この人たちと働きたいか。</p><p>その判断材料として、このQ&Aが役立てば幸いです。</p>'
      }
    },
    {
      type: 'cta',
      data: {
        buttons: ['casual', 'jobs']
      }
    },
    {
      type: 'hiringBanner',
      data: HIRING_BANNER_DATA
    }
  ],
  status: 'published'
};

// Helper function to seed individual page
export const seedPage = async (pageData, overwrite = false) => {
  try {
    console.log(`Creating page: ${pageData.title} with slug: ${pageData.slug}`);
    const id = await createPage(pageData, overwrite);
    const action = overwrite ? '上書きしました' : '作成しました';
    console.log(`✅ ${action} page: ${pageData.title} (ID: ${id})`);
    return { success: true, id, message: `${pageData.title}を${action} (ID: ${id})` };
  } catch (error) {
    console.error(`❌ Failed to create page: ${pageData.title}`, error);
    console.error('Error details:', error.message, error.stack);

    // Check if it's a duplicate error
    if (error.message.includes('すでに存在します')) {
      return {
        success: false,
        error: error.message,
        isDuplicate: true,
        slug: pageData.slug,
        title: pageData.title
      };
    }

    return { success: false, error: error.message || 'ページの作成に失敗しました' };
  }
};
